/**
 * ATC 코드 → 분류명 조회 헬퍼 팩토리.
 * code 앞 len글자를 대문자화해 table에서 조회, 없으면 "". 5개 단계 헬퍼 공통.
 */
function makeAtcNameLookup(table: Record<string, string>, len: number) {
  return (code?: string | null): string =>
    code ? table[code.slice(0, len).toUpperCase()] || "" : "";
}

/**
 * ATC 코드 첫 글자(해부학적 대분류, 14개) → 분류명 매핑.
 * 처방 스냅샷의 atcCode로 ATC순 정렬 시 대분류 그룹 헤더에 사용.
 */
export const ATC_MAIN_CATEGORIES: Record<string, string> = {
  A: "소화관 및 대사",
  B: "혈액 및 조혈기관",
  C: "심혈관계",
  D: "피부",
  G: "비뇨생식기계 및 성호르몬",
  H: "전신호르몬제(성호르몬 제외)",
  J: "전신 항감염제",
  L: "항종양제 및 면역조절제",
  M: "근골격계",
  N: "신경계",
  P: "구충·살충·기피제",
  R: "호흡기계",
  S: "감각기관",
  V: "기타",
};

/** atcCode 첫 글자(1단계) → 대분류명 */
export const atcMainName = makeAtcNameLookup(ATC_MAIN_CATEGORIES, 1);

/**
 * ATC 3단계(4글자, 약리·치료 소분류) → 한글 분류명 매핑.
 * 예: "C09A" → "ACE 억제제(단일)", "A10B" → "경구 혈당강하제".
 *
 * 출처: docs/researches/원문/WHO_ATC_index_2026_full.csv 의 레벨3 271개 전수.
 * 영문 표준명을 기준으로 번역했으며, 식약처/심평원 공식 한글 표기와 미세하게
 * 다를 수 있어 약사 검수 권장. CSV 개정 시 이 테이블만 갱신하면 된다.
 * 영문명이 동일한 S01A/S02A/S03A 등은 영역(안과/이과/안이과)으로 구분 표기.
 */
export const ATC_LEVEL3_CATEGORIES: Record<string, string> = {
  // A — 소화관 및 대사
  A01A: "구강 치료제",
  A02A: "제산제",
  A02B: "소화성궤양·위식도역류질환 치료제",
  A02X: "기타 위산 관련 질환 치료제",
  A03A: "기능성 위장관 장애 치료제",
  A03B: "벨라돈나 및 유도체(단일)",
  A03C: "진경제·정신억제제 복합",
  A03D: "진경제·진통제 복합",
  A03E: "진경·항콜린제 기타 복합",
  A03F: "위장관 운동 촉진제",
  A04A: "진토제",
  A05A: "담즙 치료제",
  A05B: "간 치료제·지방친화제",
  A05C: "담즙·지방친화제 복합",
  A06A: "변비 치료제",
  A07A: "장내 항감염제",
  A07B: "장 흡착제",
  A07C: "전해질·탄수화물 보급제",
  A07D: "지사제(장운동 억제)",
  A07E: "장 항염증제",
  A07F: "정장생균제(유산균)",
  A07X: "기타 지사제",
  A08A: "비만 치료제(다이어트식품 제외)",
  A09A: "소화제(효소 포함)",
  A10A: "인슐린 및 유사체",
  A10B: "경구 혈당강하제",
  A10X: "기타 당뇨병 치료제",
  A11A: "종합비타민(복합)",
  A11B: "종합비타민(단일)",
  A11C: "비타민 A 및 D",
  A11D: "비타민 B1",
  A11E: "비타민 B 복합",
  A11G: "비타민 C(아스코르브산)",
  A11H: "기타 단일 비타민제",
  A11J: "기타 비타민 복합제",
  A12A: "칼슘제",
  A12B: "칼륨제",
  A12C: "기타 무기질 보충제",
  A13A: "강장제",
  A14A: "단백동화 스테로이드",
  A14B: "기타 단백동화제",
  A16A: "기타 소화관·대사용제",
  // B — 혈액 및 조혈기관
  B01A: "항혈전제",
  B02A: "항섬유소용해제",
  B02B: "비타민 K 및 지혈제",
  B03A: "철분제",
  B03B: "비타민 B12 및 엽산",
  B03X: "기타 빈혈 치료제",
  B05A: "혈액 및 관련 제제",
  B05B: "정맥주사액",
  B05C: "세척용액",
  B05D: "복막투석액",
  B05X: "정맥주사액 첨가제",
  B05Z: "혈액투석·혈액여과액",
  B06A: "기타 혈액학적 제제",
  // C — 심혈관계
  C01A: "강심배당체",
  C01B: "항부정맥제(I·III군)",
  C01C: "강심제(배당체 제외)",
  C01D: "심장질환용 혈관확장제",
  C01E: "기타 심장용제",
  C02A: "중추성 항아드레날린제",
  C02B: "신경절차단 항아드레날린제",
  C02C: "말초성 항아드레날린제",
  C02D: "세동맥 평활근 작용제",
  C02K: "기타 항고혈압제",
  C02L: "항고혈압제·이뇨제 복합",
  C02N: "항고혈압제 복합(C02군)",
  C03A: "티아지드계 이뇨제",
  C03B: "티아지드 외 이뇨제",
  C03C: "루프 이뇨제",
  C03D: "알도스테론 길항·칼륨보존 이뇨제",
  C03E: "이뇨제·칼륨보존제 복합",
  C03X: "기타 이뇨제",
  C04A: "말초 혈관확장제",
  C05A: "치질·항문열창 국소 치료제",
  C05B: "정맥류 치료제",
  C05C: "모세혈관 안정제",
  C05X: "기타 혈관보호제",
  C07A: "베타차단제",
  C07B: "베타차단제·티아지드 복합",
  C07C: "베타차단제·기타이뇨제 복합",
  C07D: "베타차단제·티아지드·기타이뇨제 복합",
  C07E: "베타차단제·혈관확장제 복합",
  C07F: "베타차단제 기타 복합",
  C08C: "칼슘채널차단제(혈관 선택성)",
  C08D: "칼슘채널차단제(심장 선택성)",
  C08E: "비선택적 칼슘채널차단제",
  C08G: "칼슘채널차단제·이뇨제 복합",
  C09A: "ACE 억제제(단일)",
  C09B: "ACE 억제제 복합",
  C09C: "안지오텐신 II 수용체 차단제(ARB)",
  C09D: "ARB 복합",
  C09X: "기타 레닌-안지오텐신계 작용제",
  C10A: "지질저하제(단일)",
  C10B: "지질저하제 복합",
  // D — 피부
  D01A: "국소 항진균제",
  D01B: "전신 항진균제",
  D02A: "피부연화·보호제",
  D02B: "자외선 차단제",
  D03A: "상처치유 촉진제",
  D03B: "효소제(상처)",
  D04A: "지양제(항히스타민·마취제 포함)",
  D05A: "국소 건선 치료제",
  D05B: "전신 건선 치료제",
  D06A: "국소 항생제",
  D06B: "국소 화학요법제",
  D06C: "국소 항생·화학요법 복합",
  D07A: "국소 코르티코스테로이드(단일)",
  D07B: "코르티코스테로이드·소독제 복합",
  D07C: "코르티코스테로이드·항생제 복합",
  D07X: "코르티코스테로이드 기타 복합",
  D08A: "소독·살균제",
  D09A: "약용 드레싱",
  D10A: "국소 여드름 치료제",
  D10B: "전신 여드름 치료제",
  D11A: "기타 피부과용제",
  // G — 비뇨생식기계 및 성호르몬
  G01A: "부인과 항감염·소독제(스테로이드 복합 제외)",
  G01B: "부인과 항감염·소독제·스테로이드 복합",
  G02A: "자궁수축제",
  G02B: "국소 피임제",
  G02C: "기타 부인과용제",
  G03A: "전신 호르몬 피임제",
  G03B: "안드로겐(남성호르몬)",
  G03C: "에스트로겐",
  G03D: "프로게스토겐",
  G03E: "안드로겐·여성호르몬 복합",
  G03F: "프로게스토겐·에스트로겐 복합",
  G03G: "고나도트로핀·배란유도제",
  G03H: "항안드로겐제",
  G03X: "기타 성호르몬·생식계 조절제",
  G04B: "비뇨기계용제",
  G04C: "전립선비대증 치료제",
  // H — 전신호르몬제
  H01A: "뇌하수체전엽호르몬 및 유사체",
  H01B: "뇌하수체후엽호르몬",
  H01C: "시상하부호르몬",
  H02A: "전신 코르티코스테로이드(단일)",
  H02B: "전신 코르티코스테로이드 복합",
  H02C: "부신기능 억제제",
  H03A: "갑상선호르몬제",
  H03B: "항갑상선제",
  H03C: "요오드 요법제",
  H04A: "글리코겐 분해 호르몬(글루카곤)",
  H05A: "부갑상선호르몬 및 유사체",
  H05B: "항부갑상선제",
  // J — 전신 항감염제
  J01A: "테트라사이클린계",
  J01B: "암페니콜계(클로람페니콜)",
  J01C: "페니실린계",
  J01D: "기타 베타락탐계(세팔로스포린 등)",
  J01E: "설폰아미드·트리메토프림",
  J01F: "마크로라이드·린코사마이드계",
  J01G: "아미노글리코사이드계",
  J01M: "퀴놀론계",
  J01R: "항생제 복합",
  J01X: "기타 항생제",
  J02A: "전신 항진균제",
  J04A: "항결핵제",
  J04B: "나병 치료제",
  J05A: "항바이러스제",
  J06A: "면역혈청",
  J06B: "면역글로불린",
  J07A: "세균 백신",
  J07B: "바이러스 백신",
  J07C: "세균·바이러스 혼합 백신",
  J07X: "기타 백신",
  // L — 항종양제 및 면역조절제
  L01A: "알킬화제",
  L01B: "대사길항제",
  L01C: "식물알칼로이드 및 천연물",
  L01D: "세포독성 항생물질",
  L01E: "단백질 인산화효소 억제제",
  L01F: "단클론항체·항체약물접합체",
  L01X: "기타 항종양제",
  L02A: "호르몬 및 관련 제제(항종양)",
  L02B: "호르몬 길항제(항종양)",
  L03A: "면역증강제",
  L04A: "면역억제제",
  // M — 근골격계
  M01A: "비스테로이드 항염증제(NSAIDs)",
  M01B: "항염·항류마티스 복합제",
  M01C: "특이적 항류마티스제(DMARDs)",
  M02A: "국소 관절·근육통 치료제",
  M03A: "말초성 근이완제",
  M03B: "중추성 근이완제",
  M03C: "직접작용 근이완제",
  M04A: "통풍 치료제",
  M05B: "골다공증 치료제(골구조·무기질화)",
  M09A: "기타 근골격계 질환 치료제",
  // N — 신경계
  N01A: "전신마취제",
  N01B: "국소마취제",
  N02A: "아편유사제(마약성 진통제)",
  N02B: "해열·진통제",
  N02C: "편두통 치료제",
  N03A: "항전간제(항경련제)",
  N04A: "항콜린성 파킨슨치료제",
  N04B: "도파민성 파킨슨치료제",
  N04C: "기타 파킨슨병 치료제",
  N05A: "항정신병약",
  N05B: "항불안제",
  N05C: "수면진정제",
  N06A: "항우울제",
  N06B: "정신자극제(ADHD·인지개선제)",
  N06C: "정신억제·흥분제 복합",
  N06D: "치매 치료제",
  N07A: "부교감신경흥분제",
  N07B: "중독 치료제",
  N07C: "어지럼(전정기관) 치료제",
  N07X: "기타 신경계용제",
  // P — 구충·살충·기피제
  P01A: "아메바·원충 치료제",
  P01B: "항말라리아제",
  P01C: "리슈만편모충·트리파노소마 치료제",
  P02B: "흡충 구제제",
  P02C: "선충 구제제",
  P02D: "조충 구제제",
  P03A: "외부기생충 구제제(옴 포함)",
  P03B: "살충·기피제",
  // R — 호흡기계
  R01A: "국소 비충혈제거제 및 비강용제",
  R01B: "전신 비충혈제거제",
  R02A: "인후 치료제",
  R03A: "흡입용 기관지확장제(베타작용제)",
  R03B: "기타 흡입 기도폐쇄질환 치료제(흡입 스테로이드 등)",
  R03C: "전신 기관지확장제",
  R03D: "기타 전신 기도폐쇄질환 치료제(류코트리엔 등)",
  R05C: "거담제",
  R05D: "진해제(기침억제)",
  R05F: "진해거담 복합제",
  R05X: "기타 감기약",
  R06A: "전신 항히스타민제",
  R07A: "기타 호흡기계용제",
  // S — 감각기관
  S01A: "안과용 항감염제",
  S01B: "안과용 항염증제",
  S01C: "안과용 항염·항감염 복합",
  S01E: "녹내장 치료제·축동제",
  S01F: "산동·조절마비제",
  S01G: "안과용 충혈제거·항알레르기제",
  S01H: "안과용 국소마취제",
  S01J: "안과용 진단제",
  S01K: "안과 수술보조제",
  S01L: "안혈관질환 치료제",
  S01X: "기타 안과용제",
  S02A: "이과용 항감염제",
  S02B: "이과용 코르티코스테로이드",
  S02C: "이과용 스테로이드·항감염 복합",
  S02D: "기타 이과용제",
  S03A: "안이과용 항감염제",
  S03B: "안이과용 코르티코스테로이드",
  S03C: "안이과용 스테로이드·항감염 복합",
  S03D: "기타 안이과용제",
  // V — 기타
  V01A: "알레르겐",
  V03A: "기타 모든 치료용제",
  V04B: "소변검사용제",
  V04C: "기타 진단용제",
  V06A: "비만치료용 식이제제",
  V06B: "단백질 보충제",
  V06C: "영유아용 조제식",
  V06D: "기타 영양보급제",
  V07A: "기타 비치료용 제품",
  V08A: "X선 조영제(요오드)",
  V08B: "X선 조영제(비요오드)",
  V08C: "MRI 조영제",
  V08D: "초음파 조영제",
  V09A: "진단용 방사성의약품(중추신경계)",
  V09B: "진단용 방사성의약품(골격계)",
  V09C: "진단용 방사성의약품(신장계)",
  V09D: "진단용 방사성의약품(간·세망내피계)",
  V09E: "진단용 방사성의약품(호흡기계)",
  V09F: "진단용 방사성의약품(갑상선)",
  V09G: "진단용 방사성의약품(심혈관계)",
  V09H: "진단용 방사성의약품(염증·감염)",
  V09I: "진단용 방사성의약품(종양)",
  V09X: "기타 진단용 방사성의약품",
  V10A: "치료용 방사성의약품(항염증)",
  V10B: "치료용 방사성의약품(골전이 통증완화)",
  V10X: "기타 치료용 방사성의약품",
};

/**
 * atcCode 앞 4글자 → 3단계 분류명. 매핑 없으면 빈 문자열.
 * (호출부에서 빈 값이면 atcMainName으로 폴백)
 */
export const atcSubName = makeAtcNameLookup(ATC_LEVEL3_CATEGORIES, 4);

/**
 * ATC 4단계(5글자, 화학적 소분류) → 한글 분류명 매핑.
 * 예: "C09AA" → "ACE 억제제(단일제)", "A10BH" → "디펩티딜펩티다제-4(DPP-4) 억제제".
 *
 * 출처: docs/researches/원문/WHO_ATC_index_2026_full.csv 의 레벨4 921개 전수.
 * 상위 3단계(ATC_LEVEL3_CATEGORIES) 용어와 일관되게 영문명을 번역했으며,
 * 식약처/심평원 공식 한글 표기와 미세하게 다를 수 있어 약사 검수 권장.
 * CSV 개정 시 이 테이블만 갱신하면 된다.
 */
export const ATC_LEVEL4_CATEGORIES: Record<string, string> = {
  // A — 소화관 및 대사
  A01AA: "충치 예방제",
  A01AB: "국소 구강용 항감염제 및 소독제",
  A01AC: "국소 구강용 코르티코스테로이드",
  A01AD: "기타 국소 구강용 치료제",
  A02AA: "마그네슘 화합물",
  A02AB: "알루미늄 화합물",
  A02AC: "칼슘 화합물",
  A02AD: "알루미늄·칼슘·마그네슘 화합물 복합제",
  A02AF: "제산제·소화관내가스제거제 복합제",
  A02AG: "제산제·진경제 복합제",
  A02AH: "제산제·탄산수소나트륨 복합제",
  A02AX: "기타 제산제 복합제",
  A02BB: "프로스타글란딘류",
  A02BC: "프로톤펌프억제제",
  A02BD: "헬리코박터 파일로리 제균 복합제",
  A02BX: "기타 소화성궤양·위식도역류질환 치료제",
  A03AA: "합성 항콜린제(3급 아미노기 에스터)",
  A03AB: "합성 항콜린제(4급 암모늄 화합물)",
  A03AC: "합성 진경제(3급 아민 아미드)",
  A03AD: "파파베린 및 유도체",
  A03AE: "세로토닌 수용체 길항제",
  A03AX: "기타 기능성 위장관 장애 치료제",
  A03BA: "벨라돈나 알칼로이드(3급 아민)",
  A03BB: "반합성 벨라돈나 알칼로이드(4급 암모늄 화합물)",
  A03CA: "합성 항콜린제·정신억제제 복합제",
  A03CB: "벨라돈나 및 유도체·정신억제제 복합제",
  A03CC: "기타 진경제·정신억제제 복합제",
  A03DA: "합성 항콜린제·진통제 복합제",
  A03DB: "벨라돈나 및 유도체·진통제 복합제",
  A03DC: "기타 진경제·진통제 복합제",
  A03EA: "진경제·정신억제제·진통제 복합제",
  A03ED: "진경제·기타 약물 복합제",
  A03FA: "위장관 운동 촉진제",
  A04AA: "세로토닌(5HT3) 길항제",
  A04AD: "기타 진토제",
  A05AA: "담즙산 및 유도체",
  A05AB: "담도 치료제",
  A05AX: "기타 담즙 치료제",
  A05BA: "간 치료제",
  A06AA: "연화성 완하제",
  A06AB: "자극성 완하제",
  A06AC: "팽창성 완하제",
  A06AD: "삼투성 완하제",
  A06AG: "관장제",
  A06AH: "말초성 오피오이드 수용체 길항제",
  A06AX: "기타 변비 치료제",
  A07AA: "항생제",
  A07AB: "설폰아마이드계",
  A07AC: "이미다졸 유도체",
  A07AX: "기타 장내 항감염제",
  A07BA: "활성탄 제제",
  A07BB: "비스무트 제제",
  A07BC: "기타 장 흡착제",
  A07CA: "경구 수분·전해질 보충제(ORS)",
  A07DA: "장운동 억제성 지사제",
  A07EA: "국소작용 코르티코스테로이드",
  A07EB: "항알레르기제(코르티코스테로이드 제외)",
  A07EC: "아미노살리실산 및 유사 약물",
  A07FA: "지사성 미생물 제제(정장생균제)",
  A07XA: "기타 지사제",
  A08AA: "중추작용 비만 치료제",
  A08AB: "말초작용 비만 치료제",
  A08AX: "기타 비만 치료제",
  A09AA: "효소 제제",
  A09AB: "산 제제",
  A09AC: "효소·산 복합 제제",
  A10AB: "속효성 주사용 인슐린 및 유사체",
  A10AC: "중간형 주사용 인슐린 및 유사체",
  A10AD: "중간형 또는 지속형·속효성 혼합 주사용 인슐린 및 유사체",
  A10AE: "지속형 주사용 인슐린 및 유사체",
  A10AF: "흡입용 인슐린 및 유사체",
  A10BA: "비구아나이드계",
  A10BB: "설포닐우레아계",
  A10BC: "설폰아마이드계(헤테로고리)",
  A10BD: "경구 혈당강하제 복합제",
  A10BF: "알파글루코시다제 억제제",
  A10BG: "티아졸리딘디온계",
  A10BH: "디펩티딜펩티다제-4(DPP-4) 억제제",
  A10BJ: "글루카곤유사펩타이드-1(GLP-1) 유사체",
  A10BK: "나트륨-포도당 공동수송체-2(SGLT2) 억제제",
  A10BX: "기타 경구 혈당강하제(인슐린 제외)",
  A10XA: "알도스환원효소 억제제",
  A10XX: "기타 당뇨병 치료제",
  A11AA: "종합비타민·무기질 복합제",
  A11AB: "기타 종합비타민 복합제",
  A11BA: "종합비타민(단일제)",
  A11CA: "비타민 A(단일제)",
  A11CB: "비타민 A·D 복합제",
  A11CC: "비타민 D 및 유사체",
  A11DA: "비타민 B1(단일제)",
  A11DB: "비타민 B1·B6 및/또는 B12 복합제",
  A11EA: "비타민 B 복합제(단일)",
  A11EB: "비타민 B 복합제·비타민 C 복합",
  A11EC: "비타민 B 복합제·무기질 복합",
  A11ED: "비타민 B 복합제·단백동화 스테로이드 복합",
  A11EX: "기타 비타민 B 복합제 복합",
  A11GA: "아스코르브산(비타민 C)(단일제)",
  A11GB: "아스코르브산(비타민 C) 복합제",
  A11HA: "기타 단일 비타민제",
  A11JA: "비타민 복합제",
  A11JB: "비타민·무기질 복합제",
  A11JC: "기타 비타민 복합제",
  A12AA: "칼슘",
  A12AX: "칼슘·비타민 D 및/또는 기타 약물 복합제",
  A12BA: "칼륨",
  A12CA: "나트륨",
  A12CB: "아연",
  A12CC: "마그네슘",
  A12CD: "불소",
  A12CE: "셀레늄",
  A12CX: "기타 무기질 제제",
  A14AA: "안드로스탄 유도체",
  A14AB: "에스트렌 유도체",
  A16AA: "아미노산 및 유도체",
  A16AB: "효소",
  A16AX: "기타 소화관·대사용제",
  // B — 혈액 및 조혈기관
  B01AA: "비타민 K 길항제",
  B01AB: "헤파린계",
  B01AC: "혈소판 응집 억제제(헤파린 제외)",
  B01AD: "효소제",
  B01AE: "직접 트롬빈 억제제",
  B01AF: "직접 Xa 인자 억제제",
  B01AX: "기타 항혈전제",
  B02AA: "아미노산",
  B02AB: "단백분해효소 억제제",
  B02BA: "비타민 K",
  B02BB: "피브리노겐",
  B02BC: "국소 지혈제",
  B02BD: "혈액응고인자",
  B02BX: "기타 전신 지혈제",
  B03AA: "철분(2가) 경구제",
  B03AB: "철분(3가) 경구제",
  B03AC: "철분 주사제",
  B03AD: "철분·엽산 복합제",
  B03AE: "철분 기타 복합제",
  B03BA: "비타민 B12(시아노코발라민 및 유사체)",
  B03BB: "엽산 및 유도체",
  B03XA: "기타 빈혈 치료제",
  B05AA: "혈액대용제 및 혈장단백분획",
  B05AX: "기타 혈액제제",
  B05BA: "정맥영양 수액",
  B05BB: "전해질 균형 조절 수액",
  B05BC: "삼투성 이뇨 수액",
  B05CA: "항감염제",
  B05CB: "염류 용액",
  B05CX: "기타 세척용액",
  B05DA: "등장성 복막투석액",
  B05DB: "고장성 복막투석액",
  B05XA: "전해질 용액",
  B05XB: "아미노산",
  B05XC: "비타민",
  B05XX: "기타 정맥주사액 첨가제",
  B05ZA: "혈액투석액 농축액",
  B05ZB: "혈액여과액",
  B06AA: "효소제",
  B06AB: "헴 제제",
  B06AC: "유전성 혈관부종 치료제",
  B06AX: "기타 혈액학적 제제",
  // C — 심혈관계
  C01AA: "디기탈리스 배당체",
  C01AB: "스킬라 배당체",
  C01AC: "스트로판투스 배당체",
  C01AX: "기타 강심배당체",
  C01BA: "항부정맥제, Ia군",
  C01BB: "항부정맥제, Ib군",
  C01BC: "항부정맥제, Ic군",
  C01BD: "항부정맥제, III군",
  C01BG: "기타 항부정맥제, I군 및 III군",
  C01CA: "아드레날린성·도파민성 제제",
  C01CE: "포스포디에스테라제 억제제",
  C01CX: "기타 강심제",
  C01DA: "유기 질산염",
  C01DB: "퀴놀론계 혈관확장제",
  C01DX: "기타 심장질환용 혈관확장제",
  C01EA: "프로스타글란딘",
  C01EB: "기타 심장용제",
  C01EX: "기타 심장용 복합제",
  C02AA: "라우월피아 알칼로이드",
  C02AB: "메틸도파",
  C02AC: "이미다졸린 수용체 작용제",
  C02BA: "설포늄 유도체",
  C02BB: "이차·삼차 아민",
  C02BC: "비스4급 암모늄 화합물",
  C02CA: "알파 아드레날린 수용체 차단제",
  C02CC: "구아니딘 유도체",
  C02DA: "티아지드 유도체",
  C02DB: "하이드라지노프탈라진 유도체",
  C02DC: "피리미딘 유도체",
  C02DD: "니트로페리시안화물 유도체",
  C02DG: "구아니딘 유도체",
  C02KA: "알칼로이드(라우월피아 제외)",
  C02KB: "티로신 수산화효소 억제제",
  C02KC: "MAO 억제제",
  C02KD: "세로토닌 길항제",
  C02KN: "기타 항고혈압제",
  C02KX: "폐동맥고혈압 치료용 항고혈압제",
  C02LA: "라우월피아 알칼로이드와 이뇨제 복합제",
  C02LB: "메틸도파와 이뇨제 복합제",
  C02LC: "이미다졸린 수용체 작용제와 이뇨제 복합제",
  C02LE: "알파 아드레날린 수용체 차단제와 이뇨제 복합제",
  C02LF: "구아니딘 유도체와 이뇨제 복합제",
  C02LG: "하이드라지노프탈라진 유도체와 이뇨제 복합제",
  C02LK: "알칼로이드(라우월피아 제외)와 이뇨제 복합제",
  C02LL: "MAO 억제제와 이뇨제 복합제",
  C02LN: "세로토닌 길항제와 이뇨제 복합제",
  C02LX: "기타 항고혈압제와 이뇨제 복합제",
  C03AA: "티아지드계 단일제",
  C03AB: "티아지드와 칼륨 복합제",
  C03AH: "티아지드와 정신억제제 및/또는 진통제 복합제",
  C03AX: "티아지드와 기타 약물 복합제",
  C03BA: "설폰아미드계 단일제",
  C03BB: "설폰아미드와 칼륨 복합제",
  C03BC: "수은계 이뇨제",
  C03BD: "잔틴 유도체",
  C03BK: "설폰아미드와 기타 약물 복합제",
  C03BX: "기타 저효능 이뇨제",
  C03CA: "설폰아미드계 단일제",
  C03CB: "설폰아미드와 칼륨 복합제",
  C03CC: "아릴옥시아세트산 유도체",
  C03CD: "피라졸론 유도체",
  C03CX: "기타 고효능 이뇨제",
  C03DA: "알도스테론 길항제",
  C03DB: "기타 칼륨보존 이뇨제",
  C03EA: "저효능 이뇨제와 칼륨보존 이뇨제 복합제",
  C03EB: "고효능 이뇨제와 칼륨보존 이뇨제 복합제",
  C03XA: "바소프레신 길항제",
  C04AA: "2-아미노-1-페닐에탄올 유도체",
  C04AB: "이미다졸린 유도체",
  C04AC: "니코틴산 및 유도체",
  C04AD: "퓨린 유도체",
  C04AE: "맥각 알칼로이드",
  C04AF: "효소제",
  C04AX: "기타 말초 혈관확장제",
  C05AA: "코르티코스테로이드",
  C05AB: "항생제",
  C05AD: "국소마취제",
  C05AE: "근이완제",
  C05AX: "기타 치질·항문열창 국소 치료제",
  C05BA: "국소용 헤파린 또는 헤파리노이드",
  C05BB: "국소주사용 경화제",
  C05BX: "기타 경화제",
  C05CA: "바이오플라보노이드",
  C05CX: "기타 모세혈관 안정제",
  C05XX: "기타 혈관보호제",
  C07AA: "베타차단제, 비선택성",
  C07AB: "베타차단제, 선택성",
  C07AG: "알파·베타차단제",
  C07BA: "비선택성 베타차단제와 티아지드 복합제",
  C07BB: "선택성 베타차단제와 티아지드 복합제",
  C07BG: "알파·베타차단제와 티아지드 복합제",
  C07CA: "비선택성 베타차단제와 기타 이뇨제 복합제",
  C07CB: "선택성 베타차단제와 기타 이뇨제 복합제",
  C07CG: "알파·베타차단제와 기타 이뇨제 복합제",
  C07DA: "비선택성 베타차단제와 티아지드 및 기타 이뇨제 복합제",
  C07DB: "선택성 베타차단제와 티아지드 및 기타 이뇨제 복합제",
  C07EA: "비선택성 베타차단제와 혈관확장제 복합제",
  C07EB: "선택성 베타차단제와 혈관확장제 복합제",
  C07FB: "베타차단제와 칼슘채널차단제 복합제",
  C07FX: "베타차단제, 기타 복합제",
  C08CA: "디하이드로피리딘 유도체",
  C08CX: "기타 혈관 선택성 칼슘채널차단제",
  C08DA: "페닐알킬아민 유도체",
  C08DB: "벤조티아제핀 유도체",
  C08EA: "페닐알킬아민 유도체",
  C08EX: "기타 비선택적 칼슘채널차단제",
  C08GA: "칼슘채널차단제와 이뇨제 복합제",
  C09AA: "ACE 억제제(단일제)",
  C09BA: "ACE 억제제와 이뇨제 복합제",
  C09BB: "ACE 억제제와 칼슘채널차단제 복합제",
  C09BX: "ACE 억제제, 기타 복합제",
  C09CA: "안지오텐신 II 수용체 차단제(ARB) 단일제",
  C09DA: "안지오텐신 II 수용체 차단제(ARB)와 이뇨제 복합제",
  C09DB: "안지오텐신 II 수용체 차단제(ARB)와 칼슘채널차단제 복합제",
  C09DX: "안지오텐신 II 수용체 차단제(ARB), 기타 복합제",
  C09XA: "레닌 억제제",
  C09XX: "기타 레닌-안지오텐신계 작용제",
  C10AA: "HMG-CoA 환원효소 억제제(스타틴)",
  C10AB: "피브레이트",
  C10AC: "담즙산 결합제",
  C10AD: "니코틴산 및 유도체",
  C10AX: "기타 지질저하제",
  C10BA: "여러 지질저하제의 복합제",
  C10BX: "지질저하제와 기타 약물 복합제",
  // D — 피부
  D01AA: "항생제(국소 항진균)",
  D01AC: "이미다졸 및 트리아졸 유도체",
  D01AE: "기타 국소 항진균제",
  D01BA: "전신 항진균제",
  D02AA: "실리콘 제제",
  D02AB: "아연 제제",
  D02AC: "연질 파라핀 및 유지 제제",
  D02AD: "액상 반창고",
  D02AE: "요소(카르바미드) 제제",
  D02AF: "살리실산 제제",
  D02AX: "기타 피부연화·보호제",
  D02BA: "국소 자외선 차단제",
  D02BB: "전신 자외선 차단제",
  D03AA: "간유 연고",
  D03AX: "기타 상처치유 촉진제",
  D03BA: "단백분해효소제",
  D04AA: "국소 항히스타민제",
  D04AB: "국소 마취제",
  D04AX: "기타 지양제",
  D05AA: "타르 제제",
  D05AC: "안트라센 유도체",
  D05AD: "국소 소랄렌제",
  D05AX: "기타 국소 건선 치료제",
  D05BA: "전신 소랄렌제",
  D05BB: "건선 치료용 레티노이드",
  D05BX: "기타 전신 건선 치료제",
  D06AA: "테트라사이클린 및 유도체",
  D06AX: "기타 국소 항생제",
  D06BA: "설폰아마이드계",
  D06BB: "항바이러스제",
  D06BX: "기타 화학요법제",
  D07AA: "코르티코스테로이드, 약효 약함(I군)",
  D07AB: "코르티코스테로이드, 중등도(II군)",
  D07AC: "코르티코스테로이드, 강함(III군)",
  D07AD: "코르티코스테로이드, 매우 강함(IV군)",
  D07BA: "코르티코스테로이드(약함)·소독제 복합제",
  D07BB: "코르티코스테로이드(중등도)·소독제 복합제",
  D07BC: "코르티코스테로이드(강함)·소독제 복합제",
  D07BD: "코르티코스테로이드(매우 강함)·소독제 복합제",
  D07CA: "코르티코스테로이드(약함)·항생제 복합제",
  D07CB: "코르티코스테로이드(중등도)·항생제 복합제",
  D07CC: "코르티코스테로이드(강함)·항생제 복합제",
  D07CD: "코르티코스테로이드(매우 강함)·항생제 복합제",
  D07XA: "코르티코스테로이드(약함) 기타 복합제",
  D07XB: "코르티코스테로이드(중등도) 기타 복합제",
  D07XC: "코르티코스테로이드(강함) 기타 복합제",
  D07XD: "코르티코스테로이드(매우 강함) 기타 복합제",
  D08AA: "아크리딘 유도체",
  D08AB: "알루미늄 제제",
  D08AC: "비구아나이드 및 아미딘",
  D08AD: "붕산 제제",
  D08AE: "페놀 및 유도체",
  D08AF: "니트로푸란 유도체",
  D08AG: "요오드 제제",
  D08AH: "퀴놀린 유도체",
  D08AJ: "4급 암모늄 화합물",
  D08AK: "수은 제제",
  D08AL: "은 화합물",
  D08AX: "기타 소독·살균제",
  D09AA: "항감염제 함유 약용 드레싱",
  D09AB: "아연 붕대",
  D09AX: "연질 파라핀 드레싱",
  D10AA: "여드름 치료용 코르티코스테로이드 복합제",
  D10AB: "황 함유 제제",
  D10AD: "국소 여드름 치료용 레티노이드",
  D10AE: "과산화물 제제",
  D10AF: "여드름 치료용 항감염제",
  D10AX: "기타 국소 여드름 치료제",
  D10BA: "여드름 치료용 레티노이드",
  D10BX: "기타 전신 여드름 치료제",
  D11AA: "발한억제제",
  D11AC: "약용 샴푸",
  D11AE: "국소 안드로겐제",
  D11AF: "사마귀·티눈 치료제",
  D11AH: "피부염 치료제(코르티코스테로이드 제외)",
  D11AX: "기타 피부과용제",
  // G — 비뇨생식기계 및 성호르몬
  G01AA: "항생제",
  G01AB: "비소 화합물",
  G01AC: "퀴놀린 유도체",
  G01AD: "유기산",
  G01AE: "설폰아미드계",
  G01AF: "이미다졸 유도체",
  G01AG: "트리아졸 유도체",
  G01AX: "기타 항감염제 및 소독제",
  G01BA: "항생제·코르티코스테로이드 복합",
  G01BC: "퀴놀린 유도체·코르티코스테로이드 복합",
  G01BD: "소독제·코르티코스테로이드 복합",
  G01BE: "설폰아미드계·코르티코스테로이드 복합",
  G01BF: "이미다졸 유도체·코르티코스테로이드 복합",
  G02AB: "맥각 알칼로이드",
  G02AC: "맥각 알칼로이드·옥시토신(유사체 포함) 복합",
  G02AD: "프로스타글란딘",
  G02AX: "기타 자궁수축제",
  G02BA: "자궁내 피임제",
  G02BB: "질내 피임제",
  G02CA: "교감신경흥분제(자궁수축억제제)",
  G02CB: "프로락틴 억제제",
  G02CC: "질 투여용 항염증제",
  G02CX: "기타 부인과용제",
  G03AA: "프로게스토겐·에스트로겐 고정 복합제",
  G03AB: "프로게스토겐·에스트로겐 순차 투여제",
  G03AC: "프로게스토겐",
  G03AD: "응급피임제",
  G03BA: "3-옥소안드로스텐(4) 유도체",
  G03BB: "5-안드로스타논(3) 유도체",
  G03CA: "천연 및 반합성 에스트로겐(단일제)",
  G03CB: "합성 에스트로겐(단일제)",
  G03CC: "에스트로겐 복합제",
  G03CX: "기타 에스트로겐",
  G03DA: "프레그넨(4) 유도체",
  G03DB: "프레그나디엔 유도체",
  G03DC: "에스트렌 유도체",
  G03EA: "안드로겐·에스트로겐 복합",
  G03EB: "안드로겐·프로게스토겐·에스트로겐 복합",
  G03EK: "안드로겐·여성호르몬 기타 복합제",
  G03FA: "프로게스토겐·에스트로겐 고정 복합제",
  G03FB: "프로게스토겐·에스트로겐 순차 투여제",
  G03GA: "고나도트로핀",
  G03GB: "합성 배란유도제",
  G03HA: "항안드로겐제(단일제)",
  G03HB: "항안드로겐제·에스트로겐 복합",
  G03XA: "항고나도트로핀 및 유사 약물",
  G03XB: "프로게스테론 수용체 조절제",
  G03XC: "선택적 에스트로겐 수용체 조절제(SERM)",
  G03XX: "기타 성호르몬 및 생식계 조절제",
  G04BA: "산성화제",
  G04BC: "요로결석 용해제",
  G04BD: "빈뇨·요실금 치료제",
  G04BE: "발기부전 치료제",
  G04BX: "기타 비뇨기계용제",
  G04CA: "알파 아드레날린 수용체 차단제",
  G04CB: "테스토스테론 5알파 환원효소 억제제",
  G04CX: "기타 전립선비대증 치료제",
  // H — 전신호르몬제(성호르몬 제외)
  H01AA: "부신피질자극호르몬(ACTH)",
  H01AB: "갑상선자극호르몬(TSH)",
  H01AC: "소마트로핀 및 소마트로핀 작용제",
  H01AX: "기타 뇌하수체전엽호르몬 및 유사체",
  H01BA: "바소프레신 및 유사체",
  H01BB: "옥시토신 및 유사체",
  H01CA: "성선자극호르몬방출호르몬(GnRH)",
  H01CB: "소마토스타틴 및 유사체",
  H01CC: "항성선자극호르몬방출호르몬(GnRH 길항제)",
  H02AA: "광물코르티코이드",
  H02AB: "당질코르티코이드",
  H02BX: "전신 코르티코스테로이드 복합제",
  H02CA: "항코르티코스테로이드",
  H03AA: "갑상선호르몬",
  H03BA: "티오우라실계",
  H03BB: "황 함유 이미다졸 유도체",
  H03BC: "과염소산염",
  H03BX: "기타 항갑상선제",
  H03CA: "요오드 요법",
  H04AA: "글리코겐 분해 호르몬",
  H05AA: "부갑상선호르몬 및 유사체",
  H05BA: "칼시토닌 제제",
  H05BX: "기타 항부갑상선제",
  // J — 전신 항감염제
  J01AA: "테트라사이클린계",
  J01BA: "암페니콜계",
  J01CA: "광범위 페니실린",
  J01CE: "베타락타마제 감수성 페니실린",
  J01CF: "베타락타마제 내성 페니실린",
  J01CG: "베타락타마제 억제제",
  J01CR: "페니실린 복합제(베타락타마제 억제제 포함)",
  J01DB: "1세대 세팔로스포린",
  J01DC: "2세대 세팔로스포린",
  J01DD: "3세대 세팔로스포린",
  J01DE: "4세대 세팔로스포린",
  J01DF: "모노박탐계",
  J01DH: "카바페넴계",
  J01DI: "기타 세팔로스포린 및 페넴계",
  J01EA: "트리메토프림 및 유도체",
  J01EB: "속효성 설폰아미드",
  J01EC: "중간형 설폰아미드",
  J01ED: "지속성 설폰아미드",
  J01EE: "설폰아미드·트리메토프림 복합제(유도체 포함)",
  J01FA: "마크로라이드계",
  J01FF: "린코사마이드계",
  J01FG: "스트렙토그라민계",
  J01GA: "스트렙토마이신계",
  J01GB: "기타 아미노글리코사이드계",
  J01MA: "플루오로퀴놀론계",
  J01MB: "기타 퀴놀론계",
  J01RA: "항생제 복합제",
  J01XA: "글리코펩타이드계 항생제",
  J01XB: "폴리믹신계",
  J01XC: "스테로이드계 항생제",
  J01XD: "이미다졸 유도체",
  J01XE: "니트로푸란 유도체",
  J01XX: "기타 항생제",
  J02AA: "항생제(폴리엔계)",
  J02AB: "이미다졸 유도체",
  J02AC: "트리아졸 및 테트라졸 유도체",
  J02AX: "기타 전신 항진균제",
  J04AA: "파라아미노살리실산 및 유도체",
  J04AB: "항생제",
  J04AC: "하이드라자이드계",
  J04AD: "티오카바마이드 유도체",
  J04AK: "기타 결핵 치료제",
  J04AM: "결핵 치료제 복합제",
  J04BA: "나병 치료제",
  J05AA: "티오세미카바존계",
  J05AB: "뉴클레오사이드 및 뉴클레오타이드(역전사효소 억제제 제외)",
  J05AC: "고리형 아민계",
  J05AD: "포스폰산 유도체",
  J05AE: "단백분해효소 억제제(프로테아제 억제제)",
  J05AF: "뉴클레오사이드·뉴클레오타이드 역전사효소 억제제",
  J05AG: "비뉴클레오사이드 역전사효소 억제제",
  J05AH: "뉴라미니다제 억제제",
  J05AJ: "인테그라제 억제제",
  J05AP: "C형 간염 치료 항바이러스제",
  J05AR: "HIV 감염 치료용 항바이러스제 복합제",
  J05AX: "기타 항바이러스제",
  J06AA: "면역혈청",
  J06BA: "사람 정상 면역글로불린",
  J06BB: "특이 면역글로불린",
  J06BC: "항세균 단클론항체",
  J06BD: "항바이러스 단클론항체",
  J07AC: "탄저 백신",
  J07AD: "브루셀라증 백신",
  J07AE: "콜레라 백신",
  J07AF: "디프테리아 백신",
  J07AG: "b형 헤모필루스 인플루엔자 백신",
  J07AH: "수막구균 백신",
  J07AJ: "백일해 백신",
  J07AK: "페스트 백신",
  J07AL: "폐렴구균 백신",
  J07AM: "파상풍 백신",
  J07AN: "결핵 백신",
  J07AP: "장티푸스 백신",
  J07AR: "발진티푸스 백신",
  J07AX: "기타 세균 백신",
  J07BA: "뇌염 백신",
  J07BB: "인플루엔자 백신",
  J07BC: "간염 백신",
  J07BD: "홍역 백신",
  J07BE: "볼거리 백신",
  J07BF: "폴리오(소아마비) 백신",
  J07BG: "광견병 백신",
  J07BH: "로타바이러스 설사 백신",
  J07BJ: "풍진 백신",
  J07BK: "수두대상포진 백신",
  J07BL: "황열 백신",
  J07BM: "인유두종바이러스 백신",
  J07BN: "코로나19 백신",
  J07BP: "치쿤구니아 백신",
  J07BX: "기타 바이러스 백신",
  J07CA: "세균·바이러스 혼합 백신",
  J07XA: "기생충 백신",
  // L — 항종양제 및 면역조절제
  L01AA: "질소 머스타드 유사체",
  L01AB: "알킬설포네이트",
  L01AC: "에틸렌이민",
  L01AD: "니트로소우레아",
  L01AG: "에폭사이드",
  L01AX: "기타 알킬화제",
  L01BA: "엽산 유사체",
  L01BB: "퓨린 유사체",
  L01BC: "피리미딘 유사체",
  L01CA: "빈카 알칼로이드 및 유사체",
  L01CB: "포도필로톡신 유도체",
  L01CC: "콜히친 유도체",
  L01CD: "탁산계",
  L01CE: "토포아이소머라제 1(TOP1) 억제제",
  L01CX: "기타 식물알칼로이드 및 천연물",
  L01DA: "악티노마이신",
  L01DB: "안트라사이클린 및 관련 물질",
  L01DC: "기타 세포독성 항생물질",
  L01EA: "BCR-ABL 티로신키나제 억제제",
  L01EB: "상피세포성장인자수용체(EGFR) 티로신키나제 억제제",
  L01EC: "B-Raf 세린-트레오닌키나제(BRAF) 억제제",
  L01ED: "역형성림프종키나제(ALK) 억제제",
  L01EE: "분열촉진제활성화단백질키나제(MEK) 억제제",
  L01EF: "사이클린의존성키나제(CDK) 억제제",
  L01EG: "라파마이신표적단백질(mTOR) 키나제 억제제",
  L01EH: "사람상피세포성장인자수용체2(HER2) 티로신키나제 억제제",
  L01EJ: "야누스연관키나제(JAK) 억제제",
  L01EK: "혈관내피세포성장인자수용체(VEGFR) 티로신키나제 억제제",
  L01EL: "브루톤티로신키나제(BTK) 억제제",
  L01EM: "포스파티딜이노시톨-3-키나제(PI3K) 억제제",
  L01EN: "섬유아세포성장인자수용체(FGFR) 티로신키나제 억제제",
  L01EP: "간세포성장인자수용체(c-MET) 키나제 억제제",
  L01EX: "기타 단백질 인산화효소 억제제",
  L01FA: "CD20 억제제",
  L01FB: "CD22 억제제",
  L01FC: "CD38 억제제",
  L01FD: "HER2 억제제",
  L01FE: "EGFR 억제제",
  L01FF: "PD-1/PD-L1 억제제",
  L01FG: "VEGF/VEGFR 억제제",
  L01FX: "기타 단클론항체 및 항체약물접합체",
  L01FY: "단클론항체 및 항체약물접합체 복합제",
  L01XA: "백금 화합물",
  L01XB: "메틸하이드라진",
  L01XD: "광역학·방사선요법용 감작제",
  L01XF: "항암용 레티노이드",
  L01XG: "프로테아솜 억제제",
  L01XH: "히스톤탈아세틸화효소(HDAC) 억제제",
  L01XJ: "헤지호그 경로 억제제",
  L01XK: "폴리(ADP-리보스)중합효소(PARP) 억제제",
  L01XL: "항종양 세포·유전자 치료제",
  L01XM: "이소시트르산탈수소효소(IDH) 억제제",
  L01XU: "기타 항종양제(추가)",
  L01XX: "기타 항종양제",
  L01XY: "항종양제 복합제",
  L02AA: "에스트로겐",
  L02AB: "프로게스토겐",
  L02AE: "성선자극호르몬방출호르몬 유사체",
  L02AX: "기타 호르몬",
  L02BA: "항에스트로겐제",
  L02BB: "항안드로겐제",
  L02BG: "아로마타제 억제제",
  L02BX: "기타 호르몬 길항제 및 관련 제제",
  L03AA: "집락자극인자",
  L03AB: "인터페론",
  L03AC: "인터루킨",
  L03AX: "기타 면역증강제",
  L04AA: "선택적 면역억제제",
  L04AB: "종양괴사인자알파(TNF-alpha) 억제제",
  L04AC: "인터루킨 억제제",
  L04AD: "칼시뉴린 억제제",
  L04AE: "스핑고신-1-인산(S1P) 수용체 조절제",
  L04AF: "야누스연관키나제(JAK) 억제제",
  L04AG: "단클론항체",
  L04AH: "라파마이신표적단백질(mTOR) 키나제 억제제",
  L04AJ: "보체 억제제",
  L04AK: "디하이드로오로테이트탈수소효소(DHODH) 억제제",
  L04AL: "신생아 Fc 수용체(FcRn) 억제제",
  L04AX: "기타 면역억제제",
  // M — 근골격계
  M01AA: "부틸피라졸리딘계",
  M01AB: "아세트산 유도체 및 관련 물질",
  M01AC: "옥시캄계",
  M01AE: "프로피온산 유도체",
  M01AG: "페나메이트계",
  M01AH: "콕시브계(COX-2 억제제)",
  M01AX: "기타 비스테로이드 항염·항류마티스제",
  M01BA: "항염·항류마티스제·코르티코스테로이드 복합제",
  M01BX: "기타 항염·항류마티스제 복합제",
  M01CA: "퀴놀린계",
  M01CB: "금 제제",
  M01CC: "페니실라민 및 유사 약물",
  M01CX: "기타 특이적 항류마티스제",
  M02AA: "국소용 비스테로이드 항염제",
  M02AB: "캡사이신 및 유사 약물",
  M02AC: "살리실산 유도체 함유 제제",
  M02AX: "기타 국소 관절·근육통 치료제",
  M03AA: "쿠라레 알칼로이드",
  M03AB: "콜린 유도체",
  M03AC: "기타 4급 암모늄 화합물",
  M03AX: "기타 말초성 근이완제",
  M03BA: "카르밤산 에스테르",
  M03BB: "옥사졸·티아진·트리아진 유도체",
  M03BC: "항히스타민제 유사 에테르계",
  M03BX: "기타 중추작용 근이완제",
  M03CA: "단트롤렌 및 유도체",
  M04AA: "요산 생성 억제제",
  M04AB: "요산 배설 촉진제",
  M04AC: "요산 대사에 영향이 없는 제제",
  M04AX: "기타 통풍 치료제",
  M05BA: "비스포스포네이트",
  M05BB: "비스포스포네이트 복합제",
  M05BC: "골형성 단백질",
  M05BX: "기타 골구조·무기질화 작용 약물",
  M09AA: "퀴닌 및 유도체",
  M09AB: "효소제",
  M09AX: "기타 근골격계 질환 치료제",
  // N — 신경계
  N01AA: "에테르계 전신마취제",
  N01AB: "할로겐화 탄화수소계 흡입마취제",
  N01AF: "바르비투르산계 전신마취제(단일제)",
  N01AG: "바르비투르산계 전신마취제 복합제",
  N01AH: "오피오이드계 전신마취제",
  N01AX: "기타 전신마취제",
  N01BA: "아미노벤조산 에스테르계 국소마취제",
  N01BB: "아미드계 국소마취제",
  N01BC: "벤조산 에스테르계 국소마취제",
  N01BX: "기타 국소마취제",
  N02AA: "천연 아편 알칼로이드",
  N02AB: "페닐피페리딘 유도체",
  N02AC: "디페닐프로필아민 유도체",
  N02AD: "벤조모르판 유도체",
  N02AE: "오리파빈 유도체",
  N02AF: "모르피난 유도체",
  N02AG: "오피오이드·진경제 복합제",
  N02AJ: "오피오이드·비마약성 진통제 복합제",
  N02AX: "기타 오피오이드",
  N02BA: "살리실산 및 유도체",
  N02BB: "피라졸론계",
  N02BE: "아닐리드계(아세트아미노펜 등)",
  N02BF: "가바펜티노이드계",
  N02BG: "기타 해열·진통제",
  N02CA: "맥각 알칼로이드",
  N02CB: "코르티코스테로이드 유도체",
  N02CC: "선택적 세로토닌(5HT1) 효능제(트립탄)",
  N02CD: "칼시토닌 유전자 관련 펩타이드(CGRP) 길항제",
  N02CX: "기타 편두통 치료제",
  N03AA: "바르비투르산계 및 유도체",
  N03AB: "히단토인 유도체",
  N03AC: "옥사졸리딘 유도체",
  N03AD: "숙신이미드 유도체",
  N03AE: "벤조디아제핀 유도체",
  N03AF: "카르복사미드 유도체",
  N03AG: "지방산 유도체",
  N03AX: "기타 항전간제",
  N04AA: "3차 아민계",
  N04AB: "항히스타민제 유사 에테르계",
  N04AC: "트로핀 또는 트로핀 유도체 에테르계",
  N04BA: "도파 및 도파 유도체",
  N04BB: "아다만탄 유도체",
  N04BC: "도파민 효능제",
  N04BD: "모노아민 산화효소 B(MAO-B) 억제제",
  N04BX: "기타 도파민성 약물",
  N04CX: "기타 파킨슨병 치료제",
  N05AA: "지방족 측쇄 페노티아진계",
  N05AB: "피페라진 구조 페노티아진계",
  N05AC: "피페리딘 구조 페노티아진계",
  N05AD: "부티로페논 유도체",
  N05AE: "인돌 유도체",
  N05AF: "티오크산텐 유도체",
  N05AG: "디페닐부틸피페리딘 유도체",
  N05AH: "디아제핀·옥사제핀·티아제핀·옥세핀계",
  N05AL: "벤즈아미드계",
  N05AN: "리튬",
  N05AX: "기타 항정신병약",
  N05BA: "벤조디아제핀 유도체",
  N05BB: "디페닐메탄 유도체",
  N05BC: "카르바메이트계",
  N05BD: "디벤조-바이사이클로-옥타디엔 유도체",
  N05BE: "아자스피로데칸디온 유도체",
  N05BX: "기타 항불안제",
  N05CA: "바르비투르산계(단일제)",
  N05CB: "바르비투르산계 복합제",
  N05CC: "알데히드계 및 유도체",
  N05CD: "벤조디아제핀 유도체",
  N05CE: "피페리딘디온 유도체",
  N05CF: "벤조디아제핀 유사 약물",
  N05CH: "멜라토닌 수용체 효능제",
  N05CJ: "오렉신 수용체 길항제",
  N05CM: "기타 수면진정제",
  N05CX: "수면진정제 복합제(바르비투르산계 제외)",
  N06AA: "비선택적 모노아민 재흡수 억제제(삼환계 항우울제)",
  N06AB: "선택적 세로토닌 재흡수 억제제(SSRI)",
  N06AF: "비선택적 모노아민 산화효소 억제제(MAOI)",
  N06AG: "모노아민 산화효소 A(MAO-A) 억제제",
  N06AX: "기타 항우울제",
  N06BA: "중추작용 교감신경흥분제",
  N06BC: "잔틴 유도체",
  N06BX: "기타 정신자극제 및 인지개선제",
  N06CA: "항우울제·정신억제제 복합제",
  N06CB: "정신자극제·정신억제제 복합제",
  N06DA: "콜린에스터분해효소 억제제",
  N06DX: "기타 치매 치료제",
  N07AA: "콜린에스터분해효소 억제제",
  N07AB: "콜린 에스테르계",
  N07AX: "기타 부교감신경흥분제",
  N07BA: "니코틴 의존 치료제",
  N07BB: "알코올 의존 치료제",
  N07BC: "오피오이드 의존 치료제",
  N07CA: "항어지럼제",
  N07XA: "강글리오사이드 및 유도체",
  N07XX: "기타 신경계용제",
  // P — 구충·살충·기피제
  P01AA: "하이드록시퀴놀린 유도체",
  P01AB: "니트로이미다졸 유도체",
  P01AC: "디클로로아세트아미드 유도체",
  P01AR: "비소 화합물",
  P01AX: "기타 아메바·원충 질환 치료제",
  P01BA: "아미노퀴놀린계",
  P01BB: "비구아니드계",
  P01BC: "메탄올퀴놀린계",
  P01BD: "디아미노피리미딘계",
  P01BE: "아르테미시닌 및 유도체(단일제)",
  P01BF: "아르테미시닌 및 유도체(복합제)",
  P01BX: "기타 항말라리아제",
  P01CA: "니트로이미다졸 유도체",
  P01CB: "안티몬 화합물",
  P01CC: "니트로푸란 유도체",
  P01CD: "비소 화합물",
  P01CX: "기타 리슈만편모충·트리파노소마 치료제",
  P02BA: "퀴놀린 유도체 및 관련 물질",
  P02BB: "유기인 화합물",
  P02BX: "기타 흡충 구제제",
  P02CA: "벤즈이미다졸 유도체",
  P02CB: "피페라진 및 유도체",
  P02CC: "테트라하이드로피리미딘 유도체",
  P02CE: "이미다조티아졸 유도체",
  P02CF: "아버멕틴계",
  P02CX: "기타 선충 구제제",
  P02DA: "살리실산 유도체",
  P02DX: "기타 조충 구제제",
  P03AA: "황 함유 제제",
  P03AB: "염소 함유 제제",
  P03AC: "피레트린(합성 화합물 포함)",
  P03AX: "기타 외부기생충 구제제(옴 치료제 포함)",
  P03BA: "피레트린",
  P03BX: "기타 살충·기피제",
  // R — 호흡기계
  R01AA: "교감신경흥분제(단일)",
  R01AB: "교감신경흥분제 복합(코르티코스테로이드 제외)",
  R01AC: "항알레르기제(코르티코스테로이드 제외)",
  R01AD: "코르티코스테로이드",
  R01AX: "기타 비강용제",
  R01BA: "교감신경흥분제",
  R02AA: "소독제",
  R02AB: "항생제",
  R02AD: "국소마취제",
  R02AX: "기타 인후 치료제",
  R03AA: "알파·베타 아드레날린수용체 작용제",
  R03AB: "비선택적 베타 아드레날린수용체 작용제",
  R03AC: "선택적 베타2 아드레날린수용체 작용제",
  R03AH: "아드레날린성 약물 복합제",
  R03AK: "아드레날린성 약물과 코르티코스테로이드 또는 기타 약물 복합제(항콜린제 제외)",
  R03AL: "아드레날린성 약물과 항콜린제 복합제(코르티코스테로이드 포함 3제 복합 포함)",
  R03BA: "글루코코르티코이드",
  R03BB: "항콜린제",
  R03BC: "항알레르기제(코르티코스테로이드 제외)",
  R03BX: "기타 기도폐쇄질환 치료제(흡입제)",
  R03CA: "알파·베타 아드레날린수용체 작용제",
  R03CB: "비선택적 베타 아드레날린수용체 작용제",
  R03CC: "선택적 베타2 아드레날린수용체 작용제",
  R03CK: "아드레날린성 약물과 기타 기도폐쇄질환 치료제 복합제",
  R03DA: "잔틴계",
  R03DB: "잔틴과 아드레날린성 약물 복합제",
  R03DC: "류코트리엔 수용체 길항제",
  R03DX: "기타 전신 기도폐쇄질환 치료제",
  R05CA: "거담제",
  R05CB: "점액용해제",
  R05DA: "아편 알칼로이드 및 유도체",
  R05DB: "기타 진해제",
  R05FA: "아편 유도체·거담제 복합제",
  R05FB: "기타 진해제·거담제 복합제",
  R06AA: "아미노알킬 에테르계",
  R06AB: "치환 알킬아민계",
  R06AC: "치환 에틸렌디아민계",
  R06AD: "페노티아진 유도체",
  R06AE: "피페라진 유도체",
  R06AK: "항히스타민제 복합제",
  R06AX: "기타 전신 항히스타민제",
  R07AA: "폐 표면활성제",
  R07AB: "호흡 흥분제",
  R07AX: "기타 호흡기계용제",
  // S — 감각기관
  S01AA: "항생제",
  S01AB: "설폰아미드",
  S01AD: "항바이러스제",
  S01AE: "플루오로퀴놀론",
  S01AX: "기타 항감염제",
  S01BA: "코르티코스테로이드(단일제)",
  S01BB: "코르티코스테로이드·산동제 복합제",
  S01BC: "비스테로이드성 항염증제",
  S01CA: "코르티코스테로이드·항감염제 복합제",
  S01CB: "코르티코스테로이드·항감염제·산동제 복합제",
  S01CC: "비스테로이드성 항염증제·항감염제 복합제",
  S01EA: "녹내장 치료용 교감신경흥분제",
  S01EB: "부교감신경흥분제",
  S01EC: "탄산탈수효소 억제제",
  S01ED: "베타차단제",
  S01EE: "프로스타글란딘 유사체",
  S01EX: "기타 녹내장 치료제",
  S01FA: "항콜린제",
  S01FB: "교감신경흥분제(녹내장 치료제 제외)",
  S01GA: "충혈제거용 교감신경흥분제",
  S01GX: "기타 항알레르기제",
  S01HA: "국소마취제",
  S01JA: "착색제",
  S01JX: "기타 안과용 진단제",
  S01KA: "점탄물질",
  S01KX: "기타 수술보조제",
  S01LA: "신생혈관억제제",
  S01XA: "기타 안과용제",
  S02AA: "항감염제",
  S02BA: "코르티코스테로이드",
  S02CA: "코르티코스테로이드·항감염제 복합제",
  S02DA: "진통제·마취제",
  S02DC: "비활성 기제제",
  S03AA: "항감염제",
  S03BA: "코르티코스테로이드",
  S03CA: "코르티코스테로이드·항감염제 복합제",
  // V — 기타
  V01AA: "알레르겐 추출물",
  V03AB: "해독제",
  V03AC: "철 킬레이트제",
  V03AE: "고칼륨혈증·고인산혈증 치료제",
  V03AF: "항암치료 해독·보조제",
  V03AG: "고칼슘혈증 치료제",
  V03AH: "저혈당 치료제",
  V03AK: "조직 접착제",
  V03AM: "색전술용 제제",
  V03AN: "의료용 가스",
  V03AX: "기타 치료용 제제",
  V03AZ: "신경 억제제",
  V04CA: "당뇨병 진단용 검사제",
  V04CB: "지방 흡수 검사제",
  V04CC: "담관 개통성 검사제",
  V04CD: "뇌하수체 기능 검사제",
  V04CE: "간 기능 검사제",
  V04CF: "결핵 진단제",
  V04CG: "위산 분비 검사제",
  V04CH: "신기능·요관 손상 검사제",
  V04CJ: "갑상선 기능 검사제",
  V04CK: "췌장 기능 검사제",
  V04CL: "알레르기 질환 검사제",
  V04CM: "불임 검사제",
  V04CX: "기타 진단용제",
  V06AA: "저열량 식이제제",
  V06CA: "페닐알라닌 무함유 영양제",
  V06DA: "탄수화물·단백질·무기질·비타민 복합제",
  V06DB: "지방·탄수화물·단백질·무기질·비타민 복합제",
  V06DC: "탄수화물 제제",
  V06DD: "아미노산제(폴리펩티드 복합 포함)",
  V06DE: "아미노산·탄수화물·무기질·비타민 복합제",
  V06DF: "조제분유(우유 대체식)",
  V06DX: "기타 영양소 복합제",
  V07AA: "반창고·첩부재",
  V07AB: "용제 및 희석제(세척액 포함)",
  V07AC: "수혈 보조제",
  V07AD: "혈액검사 보조제",
  V07AN: "요실금 용품",
  V07AR: "항생제 감수성 검사용 디스크·정제",
  V07AS: "장루 용품",
  V07AT: "화장품",
  V07AV: "공업용 소독제",
  V07AX: "세정제 등",
  V07AY: "기타 비치료용 보조제",
  V07AZ: "분석용 화학물질·시약",
  V08AA: "수용성·신장친화성 고삼투압 X선 조영제",
  V08AB: "수용성·신장친화성 저삼투압 X선 조영제",
  V08AC: "수용성·간친화성 X선 조영제",
  V08AD: "비수용성 X선 조영제",
  V08BA: "황산바륨 함유 X선 조영제",
  V08CA: "상자성 조영제",
  V08CB: "초상자성 조영제",
  V08CX: "기타 자기공명영상(MRI) 조영제",
  V08DA: "초음파 조영제",
  V09AX: "기타 중추신경계 진단용 방사성의약품",
  V09CX: "기타 신장계 진단용 방사성의약품",
  V09DX: "기타 간·세망내피계 진단용 방사성의약품",
  V09EX: "기타 호흡기계 진단용 방사성의약품",
  V09FX: "갑상선 진단용 방사성의약품",
  V09GX: "기타 심혈관계 진단용 방사성의약품",
  V09HX: "기타 염증·감염 진단용 방사성의약품",
  V09IX: "기타 종양 진단용 방사성의약품",
  V09XX: "기타 진단용 방사성의약품",
  V10AX: "기타 항염증 치료용 방사성의약품",
  V10BX: "골전이 통증완화용 방사성의약품",
  V10XX: "기타 치료용 방사성의약품",
};

/**
 * ATC 4단계(5글자, 화학적 소분류) → 한글 분류명.
 * 예: "C09AA02" → "ACE 억제제(단일제)". 매칭 없으면 빈 문자열.
 */
export const atcLevel4Name = makeAtcNameLookup(ATC_LEVEL4_CATEGORIES, 5);

/**
 * ATC 2단계(3글자, 치료 소분류) → 한글 분류명 매핑.
 * 예: "C09" → "레닌-안지오텐신계 작용제", "J01" → "전신용 항생제".
 *
 * 출처: docs/researches/원문/WHO_ATC_index_2026_full.csv 의 레벨2 94개 전수.
 * 1단계/3단계 한글 용어와 일관되게 번역. 식약처/심평원 표기와 미세 차이 가능 → 약사 검수 권장.
 */
export const ATC_LEVEL2_CATEGORIES: Record<string, string> = {
  // A — 소화관 및 대사
  A01: "구강 치료제",
  A02: "위산 관련 질환 치료제",
  A03: "기능성 위장관 장애 치료제",
  A04: "진토제",
  A05: "담즙 및 간 치료제",
  A06: "변비 치료제",
  A07: "지사제 및 장 항염·항감염제",
  A08: "비만 치료제(다이어트식품 제외)",
  A09: "소화제(효소 포함)",
  A10: "당뇨병 치료제",
  A11: "비타민제",
  A12: "무기질 보충제",
  A13: "강장제",
  A14: "전신용 단백동화제",
  A15: "식욕촉진제",
  A16: "기타 소화관·대사용제",
  // B — 혈액 및 조혈기관
  B01: "항혈전제",
  B02: "지혈제",
  B03: "빈혈 치료제",
  B05: "혈액대용제 및 수액제",
  B06: "기타 혈액학적 제제",
  // C — 심혈관계
  C01: "심장 치료제",
  C02: "항고혈압제",
  C03: "이뇨제",
  C04: "말초 혈관확장제",
  C05: "혈관보호제",
  C07: "베타차단제",
  C08: "칼슘채널차단제",
  C09: "레닌-안지오텐신계 작용제",
  C10: "지질저하제",
  // D — 피부
  D01: "피부과용 항진균제",
  D02: "피부연화·보호제",
  D03: "상처 및 궤양 치료제",
  D04: "지양제(항히스타민·마취제 등)",
  D05: "건선 치료제",
  D06: "피부과용 항생제 및 화학요법제",
  D07: "피부과용 코르티코스테로이드",
  D08: "소독·살균제",
  D09: "약용 드레싱",
  D10: "여드름 치료제",
  D11: "기타 피부과용제",
  // G — 비뇨생식기계 및 성호르몬
  G01: "부인과 항감염·소독제",
  G02: "기타 부인과용제",
  G03: "성호르몬 및 생식계 조절제",
  G04: "비뇨기계용제",
  // H — 전신호르몬제(성호르몬 제외)
  H01: "뇌하수체·시상하부호르몬 및 유사체",
  H02: "전신용 코르티코스테로이드",
  H03: "갑상선 치료제",
  H04: "췌장호르몬제",
  H05: "칼슘 항상성 조절제",
  // J — 전신 항감염제
  J01: "전신용 항생제",
  J02: "전신용 항진균제",
  J04: "항마이코박테리아제(결핵·나병)",
  J05: "전신용 항바이러스제",
  J06: "면역혈청 및 면역글로불린",
  J07: "백신",
  // L — 항종양제 및 면역조절제
  L01: "항종양제",
  L02: "내분비 요법제(항종양)",
  L03: "면역증강제",
  L04: "면역억제제",
  // M — 근골격계
  M01: "항염·항류마티스제",
  M02: "국소 관절·근육통 치료제",
  M03: "근이완제",
  M04: "통풍 치료제",
  M05: "골질환 치료제",
  M09: "기타 근골격계 질환 치료제",
  // N — 신경계
  N01: "마취제",
  N02: "진통제",
  N03: "항전간제(항경련제)",
  N04: "항파킨슨제",
  N05: "정신억제제(항정신병·항불안·수면진정제)",
  N06: "정신흥분제(항우울·정신자극·치매치료제)",
  N07: "기타 신경계용제",
  // P — 구충·살충·기피제
  P01: "항원충제",
  P02: "구충제(연충)",
  P03: "외부기생충 구제·살충·기피제",
  // R — 호흡기계
  R01: "비강용제",
  R02: "인후 치료제",
  R03: "기도폐쇄질환 치료제",
  R05: "감기·기침약",
  R06: "전신용 항히스타민제",
  R07: "기타 호흡기계용제",
  // S — 감각기관
  S01: "안과용제",
  S02: "이과용제",
  S03: "안이과용제",
  // V — 기타
  V01: "알레르겐",
  V03: "기타 모든 치료용제",
  V04: "진단용제",
  V06: "일반 영양보급제",
  V07: "기타 모든 비치료용 제품",
  V08: "조영제",
  V09: "진단용 방사성의약품",
  V10: "치료용 방사성의약품",
  V20: "수술용 드레싱",
};

/**
 * ATC 2단계(3글자, 치료 소분류) → 한글 분류명. 매칭 없으면 빈 문자열.
 */
export const atcLevel2Name = makeAtcNameLookup(ATC_LEVEL2_CATEGORIES, 3);

/**
 * ATC 5단계(7글자, 개별 성분) → 영문 성분명 매핑.
 * 예: "C09AA02" → "enalapril". WHO ATC 2026 원문(영문) 그대로.
 * 5단계는 식약처 표준 한글 표기가 따로 있어 임의 음역하지 않고 영문명을 그대로 쓴다.
 */
export const ATC_LEVEL5_CATEGORIES: Record<string, string> = {
  // A — 소화관 및 대사
  A01AA01: "sodium fluoride",
  A01AA02: "sodium monofluorophosphate",
  A01AA03: "olaflur",
  A01AA04: "stannous fluoride",
  A01AA30: "combinations",
  A01AA51: "sodium fluoride, combinations",
  A01AB02: "hydrogen peroxide",
  A01AB03: "chlorhexidine",
  A01AB04: "amphotericin B",
  A01AB05: "polynoxylin",
  A01AB06: "domiphen",
  A01AB07: "oxyquinoline",
  A01AB08: "neomycin",
  A01AB09: "miconazole",
  A01AB10: "natamycin",
  A01AB11: "various",
  A01AB12: "hexetidine",
  A01AB13: "tetracycline",
  A01AB14: "benzoxonium chloride",
  A01AB15: "tibezonium iodide",
  A01AB16: "mepartricin",
  A01AB17: "metronidazole",
  A01AB18: "clotrimazole",
  A01AB19: "sodium perborate",
  A01AB21: "chlortetracycline",
  A01AB22: "doxycycline",
  A01AB23: "minocycline",
  A01AB24: "octenidine",
  A01AB25: "oxytetracycline",
  A01AB53: "chlorhexidine and cetylpyridinium",
  A01AC01: "triamcinolone",
  A01AC02: "dexamethasone",
  A01AC03: "hydrocortisone",
  A01AC04: "prednisolone",
  A01AC54: "prednisolone, combinations",
  A01AD01: "epinephrine",
  A01AD02: "benzydamine",
  A01AD05: "acetylsalicylic acid",
  A01AD06: "adrenalone",
  A01AD07: "amlexanox",
  A01AD08: "becaplermin",
  A01AD11: "various",
  A02AA01: "magnesium carbonate",
  A02AA02: "magnesium oxide",
  A02AA03: "magnesium peroxide",
  A02AA04: "magnesium hydroxide",
  A02AA05: "magnesium silicate",
  A02AA10: "combinations",
  A02AB01: "aluminium hydroxide",
  A02AB02: "algeldrate",
  A02AB03: "aluminium phosphate",
  A02AB04: "dihydroxialumini sodium carbonate",
  A02AB05: "aluminium acetoacetate",
  A02AB06: "aloglutamol",
  A02AB07: "aluminium glycinate",
  A02AB10: "combinations",
  A02AC01: "calcium carbonate",
  A02AC02: "calcium silicate",
  A02AC10: "combinations",
  A02AD01: "ordinary salt combinations",
  A02AD02: "magaldrate",
  A02AD03: "almagate",
  A02AD04: "hydrotalcite",
  A02AD05: "almasilate",
  A02AF01: "magaldrate and antiflatulents",
  A02AF02: "ordinary salt combinations and antiflatulents",
  A02BB01: "misoprostol",
  A02BB02: "enprostil",
  A02BC01: "omeprazole",
  A02BC02: "pantoprazole",
  A02BC03: "lansoprazole",
  A02BC04: "rabeprazole",
  A02BC05: "esomeprazole",
  A02BC06: "dexlansoprazole",
  A02BC07: "dexrabeprazole",
  A02BC08: "vonoprazan",
  A02BC09: "tegoprazan",
  A02BC10: "fexuprazan",
  A02BC11: "ilaprazole",
  A02BC12: "zastaprazan",
  A02BC51: "omeprazole, combinations",
  A02BC53: "lansoprazole, combinations",
  A02BC54: "rabeprazole, combinations",
  A02BD01: "omeprazole, amoxicillin and metronidazole",
  A02BD02: "lansoprazole, tetracycline and metronidazole",
  A02BD03: "lansoprazole, amoxicillin and metronidazole",
  A02BD04: "pantoprazole, amoxicillin and clarithromycin",
  A02BD05: "omeprazole, amoxicillin and clarithromycin",
  A02BD06: "esomeprazole, amoxicillin and clarithromycin",
  A02BD07: "lansoprazole, amoxicillin and clarithromycin",
  A02BD08: "bismuth subcitrate, tetracycline and metronidazole",
  A02BD09: "lansoprazole, clarithromycin and tinidazole",
  A02BD10: "lansoprazole, amoxicillin and levofloxacin",
  A02BD11: "pantoprazole, amoxicillin, clarithromycin and metronidazole",
  A02BD12: "rabeprazole, amoxicillin and clarithromycin",
  A02BD13: "rabeprazole, amoxicillin and metronidazole",
  A02BD14: "vonoprazan, amoxicillin and clarithromycin",
  A02BD15: "vonoprazan, amoxicillin and metronidazole",
  A02BD16: "omeprazole, amoxicillin and rifabutin",
  A02BD17: "vonoprazan and amoxicillin",
  A02BX01: "carbenoxolone",
  A02BX02: "sucralfate",
  A02BX03: "pirenzepine",
  A02BX04: "methiosulfonium chloride",
  A02BX05: "bismuth subcitrate",
  A02BX06: "proglumide",
  A02BX07: "gefarnate",
  A02BX08: "sulglicotide",
  A02BX09: "acetoxolone",
  A02BX10: "zolimidine",
  A02BX11: "troxipide",
  A02BX12: "bismuth subnitrate",
  A02BX13: "alginic acid",
  A02BX14: "rebamipide",
  A02BX15: "teprenone",
  A02BX16: "irsogladine",
  A02BX51: "carbenoxolone, combinations excl. psycholeptics",
  A02BX71: "carbenoxolone, combinations with psycholeptics",
  A02BX77: "gefarnate, combinations with psycholeptics",
  A03AA01: "oxyphencyclimine",
  A03AA03: "camylofin",
  A03AA04: "mebeverine",
  A03AA05: "trimebutine",
  A03AA06: "rociverine",
  A03AA07: "dicycloverine",
  A03AA08: "dihexyverine",
  A03AA09: "difemerine",
  A03AA30: "piperidolate",
  A03AB01: "benzilone",
  A03AB02: "glycopyrronium bromide",
  A03AB03: "oxyphenonium",
  A03AB04: "penthienate",
  A03AB05: "propantheline",
  A03AB06: "otilonium bromide",
  A03AB07: "methantheline",
  A03AB08: "tridihexethyl",
  A03AB09: "isopropamide",
  A03AB10: "hexocyclium",
  A03AB11: "poldine",
  A03AB12: "mepenzolate",
  A03AB13: "bevonium",
  A03AB14: "pipenzolate",
  A03AB15: "diphemanil",
  A03AB16: "(2-benzhydryloxyethyl)diethyl-methylammonium iodide",
  A03AB17: "tiemonium iodide",
  A03AB18: "prifinium bromide",
  A03AB19: "timepidium bromide",
  A03AB21: "fenpiverinium",
  A03AB53: "oxyphenonium, combinations",
  A03AC02: "dimethylaminopropionylphenothiazine",
  A03AC04: "nicofetamide",
  A03AC05: "tiropramide",
  A03AD01: "papaverine",
  A03AD02: "drotaverine",
  A03AD30: "moxaverine",
  A03AE01: "alosetron",
  A03AE03: "cilansetron",
  A03AX01: "fenpiprane",
  A03AX02: "diisopromine",
  A03AX03: "chlorbenzoxamine",
  A03AX04: "pinaverium",
  A03AX05: "fenoverine",
  A03AX06: "idanpramine",
  A03AX07: "proxazole",
  A03AX08: "alverine",
  A03AX09: "trepibutone",
  A03AX10: "isometheptene",
  A03AX11: "caroverine",
  A03AX12: "phloroglucinol",
  A03AX13: "silicones",
  A03AX14: "valethamate",
  A03AX15: "menthae piperitae aetheroleum",
  A03AX30: "trimethyldiphenylpropylamine",
  A03AX58: "alverine, combinations",
  A03BA01: "atropine",
  A03BA03: "hyoscyamine",
  A03BA04: "belladonna total alkaloids",
  A03BB01: "butylscopolamine",
  A03BB02: "methylatropine",
  A03BB03: "methylscopolamine",
  A03BB04: "fentonium",
  A03BB05: "cimetropium bromide",
  A03BB06: "homatropine methylbromide",
  A03CA01: "isopropamide and psycholeptics",
  A03CA02: "clidinium and psycholeptics",
  A03CA03: "oxyphencyclimine and psycholeptics",
  A03CA04: "otilonium bromide and psycholeptics",
  A03CA05: "glycopyrronium bromide and psycholeptics",
  A03CA06: "bevonium and psycholeptics",
  A03CA07: "ambutonium and psycholeptics",
  A03CA08: "diphemanil and psycholeptics",
  A03CA09: "pipenzolate and psycholeptics",
  A03CA30: "emepronium and psycholeptics",
  A03CA34: "propantheline and psycholeptics",
  A03CB01: "methylscopolamine and psycholeptics",
  A03CB02: "belladonna total alkaloids and psycholeptics",
  A03CB03: "atropine and psycholeptics",
  A03CB04: "homatropine methylbromide and psycholeptics",
  A03CB31: "hyoscyamine and psycholeptics",
  A03DA01: "tropenzilone and analgesics",
  A03DA02: "pitofenone and analgesics",
  A03DA03: "bevonium and analgesics",
  A03DA04: "ciclonium and analgesics",
  A03DA05: "camylofin and analgesics",
  A03DA06: "trospium and analgesics",
  A03DA07: "tiemonium iodide and analgesics",
  A03DB04: "butylscopolamine and analgesics",
  A03FA01: "metoclopramide",
  A03FA02: "cisapride",
  A03FA03: "domperidone",
  A03FA04: "bromopride",
  A03FA05: "alizapride",
  A03FA06: "clebopride",
  A03FA07: "itopride",
  A03FA08: "cinitapride",
  A03FA09: "mosapride",
  A03FA10: "acotiamide",
  A04AA01: "ondansetron",
  A04AA02: "granisetron",
  A04AA03: "tropisetron",
  A04AA04: "dolasetron",
  A04AA05: "palonosetron",
  A04AA55: "palonosetron, combinations",
  A04AD01: "scopolamine",
  A04AD02: "cerium oxalate",
  A04AD04: "chlorobutanol",
  A04AD05: "metopimazine",
  A04AD10: "dronabinol",
  A04AD11: "nabilone",
  A04AD12: "aprepitant",
  A04AD13: "casopitant",
  A04AD14: "rolapitant",
  A04AD51: "scopolamine, combinations",
  A04AD54: "chlorobutanol, combinations",
  A05AA01: "chenodeoxycholic acid",
  A05AA02: "ursodeoxycholic acid",
  A05AA03: "cholic acid",
  A05AA04: "obeticholic acid",
  A05AA05: "ursodoxicoltaurine",
  A05AA06: "norucholic acid",
  A05AB01: "nicotinyl methylamide",
  A05AX01: "piprozolin",
  A05AX02: "hymecromone",
  A05AX03: "cyclobutyrol",
  A05AX04: "maralixibat chloride",
  A05AX05: "odevixibat",
  A05AX06: "elafibranor",
  A05AX07: "seladelpar",
  A05AX08: "linerixibat",
  A05BA01: "arginine glutamate",
  A05BA03: "silymarin",
  A05BA04: "citiolone",
  A05BA05: "epomediol",
  A05BA06: "ornithine oxoglurate",
  A05BA07: "tidiacic arginine",
  A05BA08: "glycyrrhizic acid",
  A05BA09: "metadoxine",
  A05BA10: "phospholipids",
  A05BA11: "resmetirom",
  A06AA01: "liquid paraffin",
  A06AA02: "docusate sodium",
  A06AA51: "liquid paraffin, combinations",
  A06AB01: "oxyphenisatine",
  A06AB02: "bisacodyl",
  A06AB03: "dantron",
  A06AB04: "phenolphthalein",
  A06AB05: "castor oil",
  A06AB06: "senna glycosides",
  A06AB07: "cascara",
  A06AB08: "sodium picosulfate",
  A06AB09: "bisoxatin",
  A06AB20: "contact laxatives in combination",
  A06AB30: "contact laxatives in combination with belladonna alkaloids",
  A06AB52: "bisacodyl, combinations",
  A06AB53: "dantron, combinations",
  A06AB56: "senna glycosides, combinations",
  A06AB57: "cascara, combinations",
  A06AB58: "sodium picosulfate, combinations",
  A06AC01: "ispaghula (psylla seeds)",
  A06AC02: "ethulose",
  A06AC03: "sterculia",
  A06AC05: "linseed",
  A06AC06: "methylcellulose",
  A06AC07: "triticum (wheat fibre)",
  A06AC08: "polycarbophil calcium",
  A06AC51: "ispaghula, combinations",
  A06AC53: "sterculia, combinations",
  A06AC55: "linseed, combinations",
  A06AD01: "magnesium carbonate",
  A06AD02: "magnesium oxide",
  A06AD03: "magnesium peroxide",
  A06AD04: "magnesium sulfate",
  A06AD10: "mineral salts in combination",
  A06AD11: "lactulose",
  A06AD12: "lactitol",
  A06AD13: "sodium sulfate",
  A06AD14: "pentaerithrityl",
  A06AD15: "macrogol",
  A06AD16: "mannitol",
  A06AD17: "sodium phosphate",
  A06AD18: "sorbitol",
  A06AD19: "magnesium citrate",
  A06AD21: "sodium tartrate",
  A06AD61: "lactulose, combinations",
  A06AD65: "macrogol, combinations",
  A06AG01: "sodium phosphate",
  A06AG02: "bisacodyl",
  A06AG03: "dantron, incl. combinations",
  A06AG04: "glycerol",
  A06AG06: "oil",
  A06AG07: "sorbitol",
  A06AG10: "docusate sodium, incl. combinations",
  A06AG11: "sodium lauryl sulfoacetate, incl. combinations",
  A06AG20: "combinations",
  A06AH01: "methylnaltrexone bromide",
  A06AH02: "alvimopan",
  A06AH03: "naloxegol",
  A06AH04: "naloxone",
  A06AH05: "naldemedine",
  A06AX01: "glycerol",
  A06AX02: "carbon dioxide producing drugs",
  A06AX03: "lubiprostone",
  A06AX04: "linaclotide",
  A06AX05: "prucalopride",
  A06AX06: "tegaserod",
  A06AX07: "plecanatide",
  A06AX08: "tenapanor",
  A06AX09: "elobixibat",
  A07AA01: "neomycin",
  A07AA02: "nystatin",
  A07AA03: "natamycin",
  A07AA04: "streptomycin",
  A07AA05: "polymyxin B",
  A07AA06: "paromomycin",
  A07AA07: "amphotericin B",
  A07AA08: "kanamycin",
  A07AA09: "vancomycin",
  A07AA10: "colistin",
  A07AA11: "rifaximin",
  A07AA12: "fidaxomicin",
  A07AA13: "rifamycin",
  A07AA51: "neomycin, combinations",
  A07AA54: "streptomycin, combinations",
  A07AB02: "phthalylsulfathiazole",
  A07AB03: "sulfaguanidine",
  A07AB04: "succinylsulfathiazole",
  A07AC01: "miconazole",
  A07AX01: "broxyquinoline",
  A07AX02: "acetarsol",
  A07AX03: "nifuroxazide",
  A07AX04: "nifurzide",
  A07BA01: "medicinal charcoal",
  A07BA51: "medicinal charcoal, combinations",
  A07BC01: "pectin",
  A07BC02: "kaolin",
  A07BC03: "crospovidone",
  A07BC04: "attapulgite",
  A07BC05: "diosmectite",
  A07BC30: "combinations",
  A07BC54: "attapulgite, combinations",
  A07DA01: "diphenoxylate",
  A07DA02: "opium",
  A07DA03: "loperamide",
  A07DA04: "difenoxin",
  A07DA05: "loperamide oxide",
  A07DA06: "eluxadoline",
  A07DA52: "morphine, combinations",
  A07DA53: "loperamide, combinations",
  A07EA01: "prednisolone",
  A07EA02: "hydrocortisone",
  A07EA03: "prednisone",
  A07EA04: "betamethasone",
  A07EA05: "tixocortol",
  A07EA06: "budesonide",
  A07EA07: "beclometasone",
  A07EB01: "cromoglicic acid",
  A07EC01: "sulfasalazine",
  A07EC02: "mesalazine",
  A07EC03: "olsalazine",
  A07EC04: "balsalazide",
  A07FA01: "lactic acid producing organisms",
  A07FA02: "saccharomyces boulardii",
  A07FA03: "escherichia coli",
  A07FA51: "lactic acid producing organisms, combinations",
  A07XA01: "albumin tannate",
  A07XA02: "ceratonia",
  A07XA03: "calcium compounds",
  A07XA04: "racecadotril",
  A07XA06: "crofelemer",
  A07XA51: "albumin tannate, combinations",
  A08AA01: "phentermine",
  A08AA02: "fenfluramine",
  A08AA03: "amfepramone",
  A08AA04: "dexfenfluramine",
  A08AA05: "mazindol",
  A08AA06: "etilamfetamine",
  A08AA07: "cathine",
  A08AA08: "clobenzorex",
  A08AA09: "mefenorex",
  A08AA10: "sibutramine",
  A08AA11: "lorcaserin",
  A08AA12: "setmelanotide",
  A08AA51: "phentermine and topiramate",
  A08AA56: "ephedrine, combinations",
  A08AA62: "bupropion and naltrexone",
  A08AB01: "orlistat",
  A08AX01: "rimonabant",
  A09AA01: "diastase",
  A09AA02: "multienzymes (lipase, protease etc.)",
  A09AA03: "pepsin",
  A09AA04: "tilactase",
  A09AB01: "glutamic acid hydrochloride",
  A09AB02: "betaine hydrochloride",
  A09AB03: "hydrochloric acid",
  A09AB04: "citric acid",
  A09AC01: "pepsin and acid preparations",
  A09AC02: "multienzymes and acid preparations",
  A10AB01: "insulin (human)",
  A10AB02: "insulin (beef)",
  A10AB03: "insulin (pork)",
  A10AB04: "insulin lispro",
  A10AB05: "insulin aspart",
  A10AB06: "insulin glulisine",
  A10AB30: "combinations",
  A10AC01: "insulin (human)",
  A10AC02: "insulin (beef)",
  A10AC03: "insulin (pork)",
  A10AC04: "insulin lispro",
  A10AC30: "combinations",
  A10AD01: "insulin (human)",
  A10AD02: "insulin (beef)",
  A10AD03: "insulin (pork)",
  A10AD04: "insulin lispro",
  A10AD05: "insulin aspart",
  A10AD06: "insulin degludec and insulin aspart",
  A10AD30: "combinations",
  A10AE01: "insulin (human)",
  A10AE02: "insulin (beef)",
  A10AE03: "insulin (pork)",
  A10AE04: "insulin glargine",
  A10AE05: "insulin detemir",
  A10AE06: "insulin degludec",
  A10AE07: "insulin icodec",
  A10AE30: "combinations",
  A10AE54: "insulin glargine and lixisenatide",
  A10AE56: "insulin degludec and liraglutide",
  A10AE57: "insulin icodec and semaglutide",
  A10AF01: "insulin (human)",
  A10BA01: "phenformin",
  A10BA02: "metformin",
  A10BA03: "buformin",
  A10BB01: "glibenclamide",
  A10BB02: "chlorpropamide",
  A10BB03: "tolbutamide",
  A10BB04: "glibornuride",
  A10BB05: "tolazamide",
  A10BB06: "carbutamide",
  A10BB07: "glipizide",
  A10BB08: "gliquidone",
  A10BB09: "gliclazide",
  A10BB10: "metahexamide",
  A10BB11: "glisoxepide",
  A10BB12: "glimepiride",
  A10BB31: "acetohexamide",
  A10BC01: "glymidine",
  A10BD01: "phenformin and sulfonylureas",
  A10BD02: "metformin and sulfonylureas",
  A10BD03: "metformin and rosiglitazone",
  A10BD04: "glimepiride and rosiglitazone",
  A10BD05: "metformin and pioglitazone",
  A10BD06: "glimepiride and pioglitazone",
  A10BD07: "metformin and sitagliptin",
  A10BD08: "metformin and vildagliptin",
  A10BD09: "pioglitazone and alogliptin",
  A10BD10: "metformin and saxagliptin",
  A10BD11: "metformin and linagliptin",
  A10BD12: "pioglitazone and sitagliptin",
  A10BD13: "metformin and alogliptin",
  A10BD14: "metformin and repaglinide",
  A10BD15: "metformin and dapagliflozin",
  A10BD16: "metformin and canagliflozin",
  A10BD17: "metformin and acarbose",
  A10BD18: "metformin and gemigliptin",
  A10BD19: "linagliptin and empagliflozin",
  A10BD20: "metformin and empagliflozin",
  A10BD21: "saxagliptin and dapagliflozin",
  A10BD22: "metformin and evogliptin",
  A10BD23: "metformin and ertugliflozin",
  A10BD24: "sitagliptin and ertugliflozin",
  A10BD25: "metformin, saxagliptin and dapagliflozin",
  A10BD26: "metformin and lobeglitazone",
  A10BD27: "metformin, linagliptin and empagliflozin",
  A10BD28: "metformin and teneligliptin",
  A10BD29: "sitagliptin and dapagliflozin",
  A10BD30: "gemigliptin and dapagliflozin",
  A10BD31: "metformin, sitagliptin and dapagliflozin",
  A10BD32: "glimepiride and dapagliflozin",
  A10BD33: "pioglitazone and dapagliflozin",
  A10BD34: "metformin and enavogliflozin",
  A10BF01: "acarbose",
  A10BF02: "miglitol",
  A10BF03: "voglibose",
  A10BG01: "troglitazone",
  A10BG02: "rosiglitazone",
  A10BG03: "pioglitazone",
  A10BG04: "lobeglitazone",
  A10BH01: "sitagliptin",
  A10BH02: "vildagliptin",
  A10BH03: "saxagliptin",
  A10BH04: "alogliptin",
  A10BH05: "linagliptin",
  A10BH06: "gemigliptin",
  A10BH07: "evogliptin",
  A10BH08: "teneligliptin",
  A10BH51: "sitagliptin and simvastatin",
  A10BH52: "gemigliptin and rosuvastatin",
  A10BJ01: "exenatide",
  A10BJ02: "liraglutide",
  A10BJ03: "lixisenatide",
  A10BJ04: "albiglutide",
  A10BJ05: "dulaglutide",
  A10BJ06: "semaglutide",
  A10BJ07: "beinaglutide",
  A10BK01: "dapagliflozin",
  A10BK02: "canagliflozin",
  A10BK03: "empagliflozin",
  A10BK04: "ertugliflozin",
  A10BK05: "ipragliflozin",
  A10BK06: "sotagliflozin",
  A10BK07: "luseogliflozin",
  A10BK08: "bexagliflozin",
  A10BK09: "enavogliflozin",
  A10BX01: "guar gum",
  A10BX02: "repaglinide",
  A10BX03: "nateglinide",
  A10BX05: "pramlintide",
  A10BX06: "benfluorex",
  A10BX08: "mitiglinide",
  A10BX15: "imeglimin",
  A10BX16: "tirzepatide",
  A10BX17: "carfloglitazar",
  A10BX18: "dorzagliatin",
  A10XA01: "tolrestat",
  A10XX01: "teplizumab",
  A10XX02: "donislecel",
  A11AA01: "multivitamins and iron",
  A11AA02: "multivitamins and calcium",
  A11AA03: "multivitamins and other minerals, incl. combinations",
  A11AA04: "multivitamins and trace elements",
  A11CA01: "retinol (vit A)",
  A11CA02: "betacarotene",
  A11CC01: "ergocalciferol",
  A11CC02: "dihydrotachysterol",
  A11CC03: "alfacalcidol",
  A11CC04: "calcitriol",
  A11CC05: "colecalciferol",
  A11CC06: "calcifediol",
  A11CC20: "combinations",
  A11CC55: "colecalciferol, combinations",
  A11DA01: "thiamine (vit B1)",
  A11DA02: "sulbutiamine",
  A11DA03: "benfotiamine",
  A11GA01: "ascorbic acid (vit C)",
  A11GB01: "ascorbic acid (vit C) and calcium",
  A11HA01: "nicotinamide",
  A11HA02: "pyridoxine (vit B6)",
  A11HA03: "tocopherol (vit E)",
  A11HA04: "riboflavin (vit B2)",
  A11HA05: "biotin",
  A11HA06: "pyridoxal phosphate",
  A11HA07: "inositol",
  A11HA08: "tocofersolan",
  A11HA30: "dexpanthenol",
  A11HA31: "calcium pantothenate",
  A11HA32: "pantethine",
  A12AA01: "calcium phosphate",
  A12AA02: "calcium glubionate",
  A12AA03: "calcium gluconate",
  A12AA04: "calcium carbonate",
  A12AA05: "calcium lactate",
  A12AA06: "calcium lactate gluconate",
  A12AA07: "calcium chloride",
  A12AA08: "calcium glycerylphosphate",
  A12AA09: "calcium citrate lysine complex",
  A12AA10: "calcium glucoheptonate",
  A12AA11: "calcium pangamate",
  A12AA13: "calcium citrate",
  A12AA20: "calcium (different salts in combination)",
  A12AA30: "calcium laevulate",
  A12BA01: "potassium chloride",
  A12BA02: "potassium citrate",
  A12BA03: "potassium hydrogentartrate",
  A12BA04: "potassium hydrogencarbonate",
  A12BA05: "potassium gluconate",
  A12BA30: "potassium (different salts in combination)",
  A12BA51: "potassium, combinations",
  A12CA01: "sodium chloride",
  A12CA02: "sodium sulfate",
  A12CB01: "zinc sulfate",
  A12CB02: "zinc gluconate",
  A12CB03: "zinc protein complex",
  A12CB04: "zinc orotate",
  A12CC01: "magnesium chloride",
  A12CC02: "magnesium sulfate",
  A12CC03: "magnesium gluconate",
  A12CC04: "magnesium citrate",
  A12CC05: "magnesium aspartate",
  A12CC06: "magnesium lactate",
  A12CC07: "magnesium levulinate",
  A12CC08: "magnesium pidolate",
  A12CC09: "magnesium orotate",
  A12CC10: "magnesium oxide",
  A12CC30: "magnesium (different salts in combination)",
  A12CD01: "sodium fluoride",
  A12CD02: "sodium monofluorophosphate",
  A12CD51: "fluoride, combinations",
  A12CE01: "sodium selenate",
  A12CE02: "sodium selenite",
  A14AA01: "androstanolone",
  A14AA02: "stanozolol",
  A14AA03: "metandienone",
  A14AA04: "metenolone",
  A14AA05: "oxymetholone",
  A14AA06: "quinbolone",
  A14AA07: "prasterone",
  A14AA08: "oxandrolone",
  A14AA09: "norethandrolone",
  A14AB01: "nandrolone",
  A14AB02: "ethylestrenol",
  A14AB03: "oxabolone cipionate",
  A16AA01: "levocarnitine",
  A16AA02: "ademetionine",
  A16AA03: "glutamine",
  A16AA04: "mercaptamine",
  A16AA05: "carglumic acid",
  A16AA06: "betaine",
  A16AA07: "metreleptin",
  A16AB01: "alglucerase",
  A16AB02: "imiglucerase",
  A16AB03: "agalsidase alfa",
  A16AB04: "agalsidase beta",
  A16AB05: "laronidase",
  A16AB06: "sacrosidase",
  A16AB07: "alglucosidase alfa",
  A16AB08: "galsulfase",
  A16AB09: "idursulfase",
  A16AB10: "velaglucerase alfa",
  A16AB11: "taliglucerase alfa",
  A16AB12: "elosulfase alfa",
  A16AB13: "asfotase alfa",
  A16AB14: "sebelipase alfa",
  A16AB15: "velmanase alfa",
  A16AB16: "idursulfase beta",
  A16AB17: "cerliponase alfa",
  A16AB18: "vestronidase alfa",
  A16AB19: "pegvaliase",
  A16AB20: "pegunigalsidase alfa",
  A16AB21: "atidarsagene autotemcel",
  A16AB22: "avalglucosidase alfa",
  A16AB23: "cipaglucosidase alfa",
  A16AB24: "pegzilarginase",
  A16AB25: "olipudase alfa",
  A16AB26: "eladocagene exuparvovec",
  A16AB27: "pabinafusp alfa",
  A16AB28: "verenafusp alfa",
  A16AB29: "rebisufligene etisparvovec",
  A16AX01: "thioctic acid",
  A16AX02: "anethole trithione",
  A16AX03: "sodium phenylbutyrate",
  A16AX04: "nitisinone",
  A16AX05: "zinc acetate",
  A16AX06: "miglustat",
  A16AX07: "sapropterin",
  A16AX08: "teduglutide",
  A16AX09: "glycerol phenylbutyrate",
  A16AX10: "eliglustat",
  A16AX11: "sodium benzoate",
  A16AX12: "trientine",
  A16AX13: "uridine triacetate",
  A16AX14: "migalastat",
  A16AX15: "telotristat",
  A16AX16: "givosiran",
  A16AX17: "triheptanoin",
  A16AX18: "lumasiran",
  A16AX19: "fosdenopterin",
  A16AX20: "lonafarnib",
  A16AX21: "elivaldogene autotemcel",
  A16AX22: "tiomolibdic acid",
  A16AX23: "leriglitazone",
  A16AX24: "govorestat",
  A16AX25: "nedosiran",
  A16AX26: "glepaglutide",
  A16AX27: "apraglutide",
  A16AX28: "sepiapterin",
  A16AX29: "doxecitine and doxribtimine",
  A16AX30: "sodium benzoate and sodium phenylacetate",
  // B — 혈액 및 조혈기관
  B01AA01: "dicoumarol",
  B01AA02: "phenindione",
  B01AA03: "warfarin",
  B01AA04: "phenprocoumon",
  B01AA07: "acenocoumarol",
  B01AA08: "ethyl biscoumacetate",
  B01AA09: "clorindione",
  B01AA10: "diphenadione",
  B01AA11: "tioclomarol",
  B01AA12: "fluindione",
  B01AB01: "heparin",
  B01AB02: "antithrombin III",
  B01AB04: "dalteparin",
  B01AB05: "enoxaparin",
  B01AB06: "nadroparin",
  B01AB07: "parnaparin",
  B01AB08: "reviparin",
  B01AB09: "danaparoid",
  B01AB10: "tinzaparin",
  B01AB11: "sulodexide",
  B01AB12: "bemiparin",
  B01AB51: "heparin, combinations",
  B01AC01: "ditazole",
  B01AC02: "cloricromen",
  B01AC03: "picotamide",
  B01AC04: "clopidogrel",
  B01AC05: "ticlopidine",
  B01AC06: "acetylsalicylic acid",
  B01AC07: "dipyridamole",
  B01AC08: "carbasalate calcium",
  B01AC09: "epoprostenol",
  B01AC10: "indobufen",
  B01AC11: "iloprost",
  B01AC13: "abciximab",
  B01AC15: "aloxiprin",
  B01AC16: "eptifibatide",
  B01AC17: "tirofiban",
  B01AC18: "triflusal",
  B01AC19: "beraprost",
  B01AC21: "treprostinil",
  B01AC22: "prasugrel",
  B01AC23: "cilostazol",
  B01AC24: "ticagrelor",
  B01AC25: "cangrelor",
  B01AC26: "vorapaxar",
  B01AC27: "selexipag",
  B01AC28: "limaprost",
  B01AC30: "combinations",
  B01AC56: "acetylsalicylic acid, combinations with proton pump inhibitors",
  B01AD01: "streptokinase",
  B01AD02: "alteplase",
  B01AD03: "anistreplase",
  B01AD04: "urokinase",
  B01AD05: "fibrinolysin",
  B01AD06: "brinase",
  B01AD07: "reteplase",
  B01AD08: "saruplase",
  B01AD09: "ancrod",
  B01AD10: "drotrecogin alfa (activated)",
  B01AD11: "tenecteplase",
  B01AD12: "protein C",
  B01AD13: "apadamtase alfa and cinaxadamtase alfa",
  B01AE01: "desirudin",
  B01AE02: "lepirudin",
  B01AE03: "argatroban",
  B01AE04: "melagatran",
  B01AE05: "ximelagatran",
  B01AE06: "bivalirudin",
  B01AE07: "dabigatran etexilate",
  B01AF01: "rivaroxaban",
  B01AF02: "apixaban",
  B01AF03: "edoxaban",
  B01AF04: "betrixaban",
  B01AF51: "rivaroxaban and acetylsalicylic acid",
  B01AX01: "defibrotide",
  B01AX04: "dermatan sulfate",
  B01AX05: "fondaparinux",
  B01AX07: "caplacizumab",
  B02AA01: "aminocaproic acid",
  B02AA02: "tranexamic acid",
  B02AA03: "aminomethylbenzoic acid",
  B02AB01: "aprotinin",
  B02AB02: "alfa1 antitrypsin",
  B02AB04: "camostat",
  B02AB05: "ulinastatin",
  B02BA01: "phytomenadione",
  B02BA02: "menadione",
  B02BB01: "fibrinogen, human",
  B02BC01: "absorbable gelatin sponge",
  B02BC02: "oxidized cellulose",
  B02BC03: "tetragalacturonic acid hydroxymethylester",
  B02BC05: "adrenalone",
  B02BC06: "thrombin",
  B02BC07: "collagen",
  B02BC08: "calcium alginate",
  B02BC09: "epinephrine",
  B02BC30: "combinations",
  B02BD01: "coagulation factor IX, II, VII and X in combination",
  B02BD02: "coagulation factor VIII",
  B02BD03: "factor VIII inhibitor bypassing activity",
  B02BD04: "coagulation factor IX",
  B02BD05: "coagulation factor VII",
  B02BD06: "von Willebrand factor and coagulation factor VIII in combination",
  B02BD07: "coagulation factor XIII",
  B02BD08: "coagulation factor VIIa",
  B02BD10: "von Willebrand factor",
  B02BD11: "catridecacog",
  B02BD13: "coagulation factor X",
  B02BD14: "susoctocog alfa",
  B02BD15: "valoctocogene roxaparvovec",
  B02BD16: "etranacogene dezaparvovec",
  B02BD17: "fidanacogene elaparvovec",
  B02BD18: "arvenacogene sanparvovec",
  B02BD30: "thrombin",
  B02BX01: "etamsylate",
  B02BX02: "carbazochrome",
  B02BX03: "batroxobin",
  B02BX04: "romiplostim",
  B02BX05: "eltrombopag",
  B02BX06: "emicizumab",
  B02BX07: "lusutrombopag",
  B02BX08: "avatrombopag",
  B02BX09: "fostamatinib",
  B02BX10: "concizumab",
  B02BX11: "marstacimab",
  B02BX12: "fitusiran",
  B03AA01: "ferrous glycine sulfate",
  B03AA02: "ferrous fumarate",
  B03AA03: "ferrous gluconate",
  B03AA04: "ferrous carbonate",
  B03AA05: "ferrous chloride",
  B03AA06: "ferrous succinate",
  B03AA07: "ferrous sulfate",
  B03AA08: "ferrous tartrate",
  B03AA09: "ferrous aspartate",
  B03AA10: "ferrous ascorbate",
  B03AA11: "ferrous iodine",
  B03AA12: "ferrous sodium citrate",
  B03AB01: "ferric sodium citrate",
  B03AB02: "saccharated iron oxide",
  B03AB03: "sodium feredetate",
  B03AB04: "ferric hydroxide",
  B03AB05: "ferric oxide polymaltose complexes",
  B03AB07: "chondroitin sulfate-iron complex",
  B03AB08: "ferric acetyl transferrin",
  B03AB09: "ferric proteinsuccinylate",
  B03AB10: "ferric maltol",
  B03AD01: "ferrous amino acid complex and folic acid",
  B03AD02: "ferrous fumarate and folic acid",
  B03AD03: "ferrous sulfate and folic acid",
  B03AD04: "ferric oxide polymaltose complexes and folic acid",
  B03AD05: "ferrous gluconate and folic acid",
  B03AE01: "iron, vitamin B12 and folic acid",
  B03AE02: "iron, multivitamins and folic acid",
  B03AE03: "iron and multivitamins",
  B03AE04: "iron, multivitamins and minerals",
  B03AE10: "various combinations",
  B03BA01: "cyanocobalamin",
  B03BA02: "cyanocobalamin tannin complex",
  B03BA03: "hydroxocobalamin",
  B03BA04: "cobamamide",
  B03BA05: "mecobalamin",
  B03BA51: "cyanocobalamin, combinations",
  B03BA53: "hydroxocobalamin, combinations",
  B03BB01: "folic acid",
  B03BB51: "folic acid, combinations",
  B03XA01: "erythropoietin",
  B03XA02: "darbepoetin alfa",
  B03XA03: "methoxy polyethylene glycol-epoetin beta",
  B03XA04: "peginesatide",
  B03XA05: "roxadustat",
  B03XA06: "luspatercept",
  B03XA07: "daprodustat",
  B03XA08: "vadadustat",
  B03XA09: "molidustat",
  B03XA10: "efepoetin alfa",
  B05AA01: "albumin",
  B05AA02: "other plasma protein fractions",
  B05AA03: "fluorocarbon blood substitutes",
  B05AA05: "dextran",
  B05AA06: "gelatin agents",
  B05AA07: "hydroxyethylstarch",
  B05AA08: "hemoglobin crosfumaril",
  B05AA09: "hemoglobin raffimer",
  B05AA10: "hemoglobin glutamer (bovine)",
  B05AX01: "erythrocytes",
  B05AX02: "thrombocytes",
  B05AX03: "blood plasma",
  B05AX04: "stem cells from umbilical cord blood",
  B05BA01: "amino acids",
  B05BA02: "fat emulsions",
  B05BA03: "carbohydrates",
  B05BA04: "protein hydrolysates",
  B05BA10: "combinations",
  B05BB01: "electrolytes",
  B05BB02: "electrolytes with carbohydrates",
  B05BB03: "trometamol",
  B05BB04: "electrolytes in combination with other drugs",
  B05BC01: "mannitol",
  B05BC02: "carbamide",
  B05CA01: "cetylpyridinium",
  B05CA02: "chlorhexidine",
  B05CA03: "nitrofural",
  B05CA04: "sulfamethizole",
  B05CA05: "taurolidine",
  B05CA06: "mandelic acid",
  B05CA07: "noxytiolin",
  B05CA08: "ethacridine lactate",
  B05CA09: "neomycin",
  B05CA10: "combinations",
  B05CB01: "sodium chloride",
  B05CB02: "sodium citrate",
  B05CB03: "magnesium citrate",
  B05CB04: "sodium bicarbonate",
  B05CB10: "combinations",
  B05CX01: "glucose",
  B05CX02: "sorbitol",
  B05CX03: "glycine",
  B05CX04: "mannitol",
  B05CX10: "combinations",
  B05XA01: "potassium chloride",
  B05XA02: "sodium bicarbonate",
  B05XA03: "sodium chloride",
  B05XA04: "ammonium chloride",
  B05XA05: "magnesium sulfate",
  B05XA06: "potassium phosphate, incl. combinations with other potassium salts",
  B05XA07: "calcium chloride",
  B05XA08: "sodium acetate",
  B05XA09: "sodium phosphate",
  B05XA10: "magnesium phosphate",
  B05XA11: "magnesium chloride",
  B05XA12: "zinc chloride",
  B05XA13: "hydrochloric acid",
  B05XA14: "sodium glycerophosphate",
  B05XA15: "potassium lactate",
  B05XA16: "cardioplegia solutions",
  B05XA17: "potassium acetate",
  B05XA18: "zinc sulfate",
  B05XA19: "calcium gluconate",
  B05XA20: "sodium selenite",
  B05XA30: "combinations of electrolytes",
  B05XA31: "electrolytes in combination with other drugs",
  B05XB01: "arginine hydrochloride",
  B05XB02: "alanyl glutamine",
  B05XB03: "lysine",
  B05XX02: "trometamol",
  B06AA02: "fibrinolysin and desoxyribonuclease",
  B06AA03: "hyaluronidase",
  B06AA04: "chymotrypsin",
  B06AA07: "trypsin",
  B06AA10: "desoxyribonuclease",
  B06AA55: "streptokinase, combinations",
  B06AB01: "hemin",
  B06AC01: "c1-inhibitor, plasma derived",
  B06AC02: "icatibant",
  B06AC03: "ecallantide",
  B06AC04: "conestat alfa",
  B06AC05: "lanadelumab",
  B06AC06: "berotralstat",
  B06AC07: "garadacimab",
  B06AC08: "sebetralstat",
  B06AC09: "donidalorsen",
  B06AX01: "crizanlizumab",
  B06AX02: "betibeglogene autotemcel",
  B06AX03: "voxelotor",
  B06AX04: "mitapivat",
  B06AX05: "exagamglogene autotemcel",
  B06AX06: "lovotibeglogene autotemcel",
  B06AX07: "mozafancogene autotemcel",
  // C — 심혈관계
  C01AA01: "acetyldigitoxin",
  C01AA02: "acetyldigoxin",
  C01AA03: "digitalis leaves",
  C01AA04: "digitoxin",
  C01AA05: "digoxin",
  C01AA06: "lanatoside C",
  C01AA07: "deslanoside",
  C01AA08: "metildigoxin",
  C01AA09: "gitoformate",
  C01AA52: "acetyldigoxin, combinations",
  C01AB01: "proscillaridin",
  C01AB51: "proscillaridin, combinations",
  C01AC01: "g-strophanthin",
  C01AC03: "cymarin",
  C01AX02: "peruvoside",
  C01BA01: "quinidine",
  C01BA02: "procainamide",
  C01BA03: "disopyramide",
  C01BA04: "sparteine",
  C01BA05: "ajmaline",
  C01BA08: "prajmaline",
  C01BA12: "lorajmine",
  C01BA13: "hydroquinidine",
  C01BA51: "quinidine, combinations excl. psycholeptics",
  C01BA71: "quinidine, combinations with psycholeptics",
  C01BB01: "lidocaine",
  C01BB02: "mexiletine",
  C01BB03: "tocainide",
  C01BB04: "aprindine",
  C01BC03: "propafenone",
  C01BC04: "flecainide",
  C01BC07: "lorcainide",
  C01BC08: "encainide",
  C01BC09: "ethacizine",
  C01BD01: "amiodarone",
  C01BD02: "bretylium tosilate",
  C01BD03: "bunaftine",
  C01BD04: "dofetilide",
  C01BD05: "ibutilide",
  C01BD06: "tedisamil",
  C01BD07: "dronedarone",
  C01BG01: "moracizine",
  C01BG07: "cibenzoline",
  C01BG11: "vernakalant",
  C01CA01: "etilefrine",
  C01CA02: "isoprenaline",
  C01CA03: "norepinephrine",
  C01CA04: "dopamine",
  C01CA05: "norfenefrine",
  C01CA06: "phenylephrine",
  C01CA07: "dobutamine",
  C01CA08: "oxedrine",
  C01CA09: "metaraminol",
  C01CA10: "methoxamine",
  C01CA11: "mephentermine",
  C01CA12: "dimetofrine",
  C01CA13: "prenalterol",
  C01CA14: "dopexamine",
  C01CA15: "gepefrine",
  C01CA16: "ibopamine",
  C01CA17: "midodrine",
  C01CA18: "octopamine",
  C01CA19: "fenoldopam",
  C01CA21: "cafedrine",
  C01CA22: "arbutamine",
  C01CA23: "theodrenaline",
  C01CA24: "epinephrine",
  C01CA25: "amezinium metilsulfate",
  C01CA26: "ephedrine",
  C01CA27: "droxidopa",
  C01CA28: "centhaquine",
  C01CA30: "combinations",
  C01CA51: "etilefrine, combinations",
  C01CE01: "amrinone",
  C01CE02: "milrinone",
  C01CE03: "enoximone",
  C01CE04: "bucladesine",
  C01CX06: "angiotensinamide",
  C01CX07: "xamoterol",
  C01CX08: "levosimendan",
  C01CX09: "angiotensin II",
  C01CX10: "omecamtiv mecarbil",
  C01DA02: "glyceryl trinitrate",
  C01DA04: "methylpropylpropanediol dinitrate",
  C01DA05: "pentaerithrityl tetranitrate",
  C01DA07: "propatylnitrate",
  C01DA08: "isosorbide dinitrate",
  C01DA09: "trolnitrate",
  C01DA13: "eritrityl tetranitrate",
  C01DA14: "isosorbide mononitrate",
  C01DA20: "organic nitrates in combination",
  C01DA38: "tenitramine",
  C01DA52: "glyceryl trinitrate, combinations",
  C01DA54: "methylpropylpropanediol dinitrate, combinations",
  C01DA55: "pentaerithrityl tetranitrate, combinations",
  C01DA57: "propatylnitrate, combinations",
  C01DA58: "isosorbide dinitrate, combinations",
  C01DA59: "trolnitrate, combinations",
  C01DA63: "eritrityl tetranitrate, combinations",
  C01DA70: "organic nitrates in combination with psycholeptics",
  C01DB01: "flosequinan",
  C01DX01: "itramin tosilate",
  C01DX02: "prenylamine",
  C01DX03: "oxyfedrine",
  C01DX04: "benziodarone",
  C01DX05: "carbocromen",
  C01DX06: "hexobendine",
  C01DX07: "etafenone",
  C01DX08: "heptaminol",
  C01DX09: "imolamine",
  C01DX10: "dilazep",
  C01DX11: "trapidil",
  C01DX12: "molsidomine",
  C01DX13: "efloxate",
  C01DX14: "cinepazet",
  C01DX15: "cloridarol",
  C01DX16: "nicorandil",
  C01DX18: "linsidomine",
  C01DX19: "nesiritide",
  C01DX21: "serelaxin",
  C01DX22: "vericiguat",
  C01DX51: "itramin tosilate, combinations",
  C01DX52: "prenylamine, combinations",
  C01DX53: "oxyfedrine, combinations",
  C01DX54: "benziodarone, combinations",
  C01EA01: "alprostadil",
  C01EB02: "camphora",
  C01EB03: "indometacin",
  C01EB04: "crataegus glycosides",
  C01EB05: "creatinolfosfate",
  C01EB06: "fosfocreatine",
  C01EB07: "fructose 1,6-diphosphate",
  C01EB09: "ubidecarenone",
  C01EB10: "adenosine",
  C01EB11: "tiracizine",
  C01EB13: "acadesine",
  C01EB15: "trimetazidine",
  C01EB16: "ibuprofen",
  C01EB17: "ivabradine",
  C01EB18: "ranolazine",
  C01EB21: "regadenoson",
  C01EB22: "meldonium",
  C01EB23: "tiazotic acid",
  C01EB24: "mavacamten",
  C01EB25: "acoramidis",
  C02AA01: "rescinnamine",
  C02AA02: "reserpine",
  C02AA03: "combinations of rauwolfia alkaloids",
  C02AA04: "rauwolfia alkaloids, whole root",
  C02AA05: "deserpidine",
  C02AA06: "methoserpidine",
  C02AA07: "bietaserpine",
  C02AA52: "reserpine, combinations",
  C02AA53: "combinations of rauwolfia alkoloids, combinations",
  C02AA57: "bietaserpine, combinations",
  C02AB01: "methyldopa (levorotatory)",
  C02AB02: "methyldopa (racemic)",
  C02AC01: "clonidine",
  C02AC02: "guanfacine",
  C02AC04: "tolonidine",
  C02AC05: "moxonidine",
  C02AC06: "rilmenidine",
  C02BA01: "trimetaphan",
  C02BB01: "mecamylamine",
  C02CA01: "prazosin",
  C02CA02: "indoramin",
  C02CA03: "trimazosin",
  C02CA04: "doxazosin",
  C02CA06: "urapidil",
  C02CC01: "betanidine",
  C02CC02: "guanethidine",
  C02CC03: "guanoxan",
  C02CC04: "debrisoquine",
  C02CC05: "guanoclor",
  C02CC06: "guanazodine",
  C02CC07: "guanoxabenz",
  C02DA01: "diazoxide",
  C02DB01: "dihydralazine",
  C02DB02: "hydralazine",
  C02DB03: "endralazine",
  C02DB04: "cadralazine",
  C02DC01: "minoxidil",
  C02DD01: "nitroprusside",
  C02DG01: "pinacidil",
  C02KA01: "veratrum",
  C02KB01: "metirosine",
  C02KC01: "pargyline",
  C02KD01: "ketanserin",
  C02KN01: "aprocitentan",
  C02KN02: "baxdrostat",
  C02KX01: "bosentan",
  C02KX02: "ambrisentan",
  C02KX03: "sitaxentan",
  C02KX04: "macitentan",
  C02KX05: "riociguat",
  C02KX06: "sotatercept",
  C02KX52: "ambrisentan and tadalafil",
  C02KX54: "macitentan and tadalafil",
  C02LA01: "reserpine and diuretics",
  C02LA02: "rescinnamine and diuretics",
  C02LA03: "deserpidine and diuretics",
  C02LA04: "methoserpidine and diuretics",
  C02LA07: "bietaserpine and diuretics",
  C02LA08: "rauwolfia alkaloids, whole root and diuretics",
  C02LA09: "syrosingopine and diuretics",
  C02LA50: "combination of rauwolfia alkaloids and diuretics incl. other combinations",
  C02LA51: "reserpine and diuretics, combinations with other drugs",
  C02LA52: "rescinnamine and diuretics, combinations with other drugs",
  C02LA71: "reserpine and diuretics, combinations with psycholeptics",
  C02LB01: "methyldopa (levorotatory) and diuretics",
  C02LC01: "clonidine and diuretics",
  C02LC05: "moxonidine and diuretics",
  C02LC51: "clonidine and diuretics, combinations with other drugs",
  C02LE01: "prazosin and diuretics",
  C02LF01: "guanethidine and diuretics",
  C02LG01: "dihydralazine and diuretics",
  C02LG02: "hydralazine and diuretics",
  C02LG03: "picodralazine and diuretics",
  C02LG51: "dihydralazine and diuretics, combinations with other drugs",
  C02LG73: "picodralazine and diuretics, combinations with psycholeptics",
  C02LK01: "veratrum and diuretics",
  C02LL01: "pargyline and diuretics",
  C02LX01: "pinacidil and diuretics",
  C03AA01: "bendroflumethiazide",
  C03AA02: "hydroflumethiazide",
  C03AA03: "hydrochlorothiazide",
  C03AA04: "chlorothiazide",
  C03AA05: "polythiazide",
  C03AA06: "trichlormethiazide",
  C03AA07: "cyclopenthiazide",
  C03AA08: "methyclothiazide",
  C03AA09: "cyclothiazide",
  C03AA13: "mebutizide",
  C03AB01: "bendroflumethiazide and potassium",
  C03AB02: "hydroflumethiazide and potassium",
  C03AB03: "hydrochlorothiazide and potassium",
  C03AB04: "chlorothiazide and potassium",
  C03AB05: "polythiazide and potassium",
  C03AB06: "trichlormethiazide and potassium",
  C03AB07: "cyclopenthiazide and potassium",
  C03AB08: "methyclothiazide and potassium",
  C03AB09: "cyclothiazide and potassium",
  C03AH01: "chlorothiazide, combinations",
  C03AH02: "hydroflumethiazide, combinations",
  C03AX01: "hydrochlorothiazide, combinations",
  C03BA02: "quinethazone",
  C03BA03: "clopamide",
  C03BA04: "chlortalidone",
  C03BA05: "mefruside",
  C03BA07: "clofenamide",
  C03BA08: "metolazone",
  C03BA09: "meticrane",
  C03BA10: "xipamide",
  C03BA11: "indapamide",
  C03BA12: "clorexolone",
  C03BA13: "fenquizone",
  C03BA82: "clorexolone, combinations with psycholeptics",
  C03BB02: "quinethazone and potassium",
  C03BB03: "clopamide and potassium",
  C03BB04: "chlortalidone and potassium",
  C03BB05: "mefruside and potassium",
  C03BB07: "clofenamide and potassium",
  C03BC01: "mersalyl",
  C03BD01: "theobromine",
  C03BX03: "cicletanine",
  C03CA01: "furosemide",
  C03CA02: "bumetanide",
  C03CA03: "piretanide",
  C03CA04: "torasemide",
  C03CB01: "furosemide and potassium",
  C03CB02: "bumetanide and potassium",
  C03CC01: "etacrynic acid",
  C03CC02: "tienilic acid",
  C03CD01: "muzolimine",
  C03CX01: "etozolin",
  C03DA01: "spironolactone",
  C03DA02: "potassium canrenoate",
  C03DA03: "canrenone",
  C03DA04: "eplerenone",
  C03DA05: "finerenone",
  C03DA54: "eplerenone and dapagliflozin",
  C03DB01: "amiloride",
  C03DB02: "triamterene",
  C03EA01: "hydrochlorothiazide and potassium-sparing agents",
  C03EA02: "trichlormethiazide and potassium-sparing agents",
  C03EA03: "epitizide and potassium-sparing agents",
  C03EA04: "altizide and potassium-sparing agents",
  C03EA05: "mebutizide and potassium-sparing agents",
  C03EA06: "chlortalidone and potassium-sparing agents",
  C03EA07: "cyclopenthiazide and potassium-sparing agents",
  C03EA12: "metolazone and potassium-sparing agents",
  C03EA13: "bendroflumethiazide and potassium-sparing agents",
  C03EA14: "butizide and potassium-sparing agents",
  C03EB01: "furosemide and potassium-sparing agents",
  C03EB02: "bumetanide and potassium-sparing agents",
  C03EB03: "torasemide and potassium-sparing agents",
  C03XA01: "tolvaptan",
  C03XA02: "conivaptan",
  C04AA01: "isoxsuprine",
  C04AA02: "buphenine",
  C04AA31: "bamethan",
  C04AB01: "phentolamine",
  C04AB02: "tolazoline",
  C04AC01: "nicotinic acid",
  C04AC02: "nicotinyl alcohol (pyridylcarbinol)",
  C04AC03: "inositol nicotinate",
  C04AC07: "ciclonicate",
  C04AD01: "pentifylline",
  C04AD02: "xantinol nicotinate",
  C04AD03: "pentoxifylline",
  C04AD04: "etofylline nicotinate",
  C04AE01: "ergoloid mesylates",
  C04AE02: "nicergoline",
  C04AE04: "dihydroergocristine",
  C04AE51: "ergoloid mesylates, combinations",
  C04AE54: "dihydroergocristine, combinations",
  C04AF01: "kallidinogenase",
  C04AX01: "cyclandelate",
  C04AX02: "phenoxybenzamine",
  C04AX07: "vincamine",
  C04AX10: "moxisylyte",
  C04AX11: "bencyclane",
  C04AX17: "vinburnine",
  C04AX19: "suloctidil",
  C04AX20: "buflomedil",
  C04AX21: "naftidrofuryl",
  C04AX23: "butalamine",
  C04AX24: "visnadine",
  C04AX26: "cetiedil",
  C04AX27: "cinepazide",
  C04AX28: "ifenprodil",
  C04AX30: "azapetine",
  C04AX32: "fasudil",
  C04AX33: "clazosentan",
  C05AA01: "hydrocortisone",
  C05AA04: "prednisolone",
  C05AA05: "betamethasone",
  C05AA06: "fluorometholone",
  C05AA08: "fluocortolone",
  C05AA09: "dexamethasone",
  C05AA10: "fluocinolone acetonide",
  C05AA11: "fluocinonide",
  C05AA12: "triamcinolone",
  C05AD01: "lidocaine",
  C05AD02: "tetracaine",
  C05AD03: "benzocaine",
  C05AD04: "cinchocaine",
  C05AD05: "procaine",
  C05AD06: "oxetacaine",
  C05AD07: "pramocaine",
  C05AE01: "glyceryl trinitrate",
  C05AE02: "isosorbide dinitrate",
  C05AE03: "diltiazem",
  C05AX01: "aluminium preparations",
  C05AX02: "bismuth preparations, combinations",
  C05AX03: "other preparations, combinations",
  C05AX04: "zinc preparations",
  C05AX05: "tribenoside",
  C05AX06: "phenylephrine",
  C05BA01: "organo-heparinoid",
  C05BA02: "sodium apolate",
  C05BA03: "heparin",
  C05BA04: "pentosan polysulfate sodium",
  C05BA51: "heparinoid, combinations",
  C05BA53: "heparin, combinations",
  C05BB01: "monoethanolamine oleate",
  C05BB02: "polidocanol",
  C05BB03: "invert sugar",
  C05BB04: "sodium tetradecyl sulfate",
  C05BB05: "phenol",
  C05BB56: "glucose, combinations",
  C05BX01: "calcium dobesilate",
  C05BX51: "calcium dobesilate, combinations",
  C05CA01: "rutoside",
  C05CA02: "monoxerutin",
  C05CA03: "diosmin",
  C05CA04: "troxerutin",
  C05CA05: "hidrosmin",
  C05CA51: "rutoside, combinations",
  C05CA53: "diosmin, combinations",
  C05CA54: "troxerutin, combinations",
  C05CX01: "tribenoside",
  C05CX02: "naftazone",
  C05CX03: "Hippocastani semen",
  C05XX01: "beperminogene perplasmid",
  C07AA01: "alprenolol",
  C07AA02: "oxprenolol",
  C07AA03: "pindolol",
  C07AA05: "propranolol",
  C07AA06: "timolol",
  C07AA07: "sotalol",
  C07AA12: "nadolol",
  C07AA14: "mepindolol",
  C07AA15: "carteolol",
  C07AA16: "tertatolol",
  C07AA17: "bopindolol",
  C07AA19: "bupranolol",
  C07AA23: "penbutolol",
  C07AA27: "cloranolol",
  C07AB01: "practolol",
  C07AB02: "metoprolol",
  C07AB03: "atenolol",
  C07AB04: "acebutolol",
  C07AB05: "betaxolol",
  C07AB06: "bevantolol",
  C07AB07: "bisoprolol",
  C07AB08: "celiprolol",
  C07AB09: "esmolol",
  C07AB10: "epanolol",
  C07AB11: "s-atenolol",
  C07AB12: "nebivolol",
  C07AB13: "talinolol",
  C07AB14: "landiolol",
  C07AG01: "labetalol",
  C07AG02: "carvedilol",
  C07BA02: "oxprenolol and thiazides",
  C07BA05: "propranolol and thiazides",
  C07BA06: "timolol and thiazides",
  C07BA07: "sotalol and thiazides",
  C07BA12: "nadolol and thiazides",
  C07BA68: "metipranolol and thiazides, combinations",
  C07BB02: "metoprolol and thiazides",
  C07BB03: "atenolol and thiazides",
  C07BB04: "acebutolol and thiazides",
  C07BB06: "bevantolol and thiazides",
  C07BB07: "bisoprolol and thiazides",
  C07BB12: "nebivolol and thiazides",
  C07BB52: "metoprolol and thiazides, combinations",
  C07BG01: "labetalol and thiazides",
  C07CA02: "oxprenolol and other diuretics",
  C07CA03: "pindolol and other diuretics",
  C07CA17: "bopindolol and other diuretics",
  C07CA23: "penbutolol and other diuretics",
  C07CB02: "metoprolol and other diuretics",
  C07CB03: "atenolol and other diuretics",
  C07CB53: "atenolol and other diuretics, combinations",
  C07CG01: "labetalol and other diuretics",
  C07DA06: "timolol, thiazides and other diuretics",
  C07DB01: "atenolol, thiazides and other diuretics",
  C07FB02: "metoprolol and felodipine",
  C07FB03: "atenolol and nifedipine",
  C07FB07: "bisoprolol and amlodipine",
  C07FB12: "nebivolol and amlodipine",
  C07FB13: "metoprolol and amlodipine",
  C07FX01: "propranolol and other combinations",
  C07FX02: "sotalol and acetylsalicylic acid",
  C07FX03: "metoprolol and acetylsalicylic acid",
  C07FX04: "bisoprolol and acetylsalicylic acid",
  C07FX05: "metoprolol and ivabradine",
  C07FX06: "carvedilol and ivabradine",
  C08CA01: "amlodipine",
  C08CA02: "felodipine",
  C08CA03: "isradipine",
  C08CA04: "nicardipine",
  C08CA05: "nifedipine",
  C08CA06: "nimodipine",
  C08CA07: "nisoldipine",
  C08CA08: "nitrendipine",
  C08CA09: "lacidipine",
  C08CA10: "nilvadipine",
  C08CA11: "manidipine",
  C08CA12: "barnidipine",
  C08CA13: "lercanidipine",
  C08CA14: "cilnidipine",
  C08CA15: "benidipine",
  C08CA16: "clevidipine",
  C08CA17: "levamlodipine",
  C08CA51: "amlodipine and celecoxib",
  C08CA55: "nifedipine, combinations",
  C08CX01: "mibefradil",
  C08DA01: "verapamil",
  C08DA02: "gallopamil",
  C08DA03: "etripamil",
  C08DA51: "verapamil, combinations",
  C08DB01: "diltiazem",
  C08EA01: "fendiline",
  C08EA02: "bepridil",
  C08EX01: "lidoflazine",
  C08EX02: "perhexiline",
  C08GA01: "nifedipine and diuretics",
  C08GA02: "amlodipine and diuretics",
  C09AA01: "captopril",
  C09AA02: "enalapril",
  C09AA03: "lisinopril",
  C09AA04: "perindopril",
  C09AA05: "ramipril",
  C09AA06: "quinapril",
  C09AA07: "benazepril",
  C09AA08: "cilazapril",
  C09AA09: "fosinopril",
  C09AA10: "trandolapril",
  C09AA11: "spirapril",
  C09AA12: "delapril",
  C09AA13: "moexipril",
  C09AA14: "temocapril",
  C09AA15: "zofenopril",
  C09AA16: "imidapril",
  C09BA01: "captopril and diuretics",
  C09BA02: "enalapril and diuretics",
  C09BA03: "lisinopril and diuretics",
  C09BA04: "perindopril and diuretics",
  C09BA05: "ramipril and diuretics",
  C09BA06: "quinapril and diuretics",
  C09BA07: "benazepril and diuretics",
  C09BA08: "cilazapril and diuretics",
  C09BA09: "fosinopril and diuretics",
  C09BA12: "delapril and diuretics",
  C09BA13: "moexipril and diuretics",
  C09BA15: "zofenopril and diuretics",
  C09BB02: "enalapril and lercanidipine",
  C09BB03: "lisinopril and amlodipine",
  C09BB04: "perindopril and amlodipine",
  C09BB05: "ramipril and felodipine",
  C09BB06: "enalapril and nitrendipine",
  C09BB07: "ramipril and amlodipine",
  C09BB10: "trandolapril and verapamil",
  C09BB12: "delapril and manidipine",
  C09BB13: "benazepril and amlodipine",
  C09BB14: "zofenopril and amlodipine",
  C09BX01: "perindopril, amlodipine and indapamide",
  C09BX02: "perindopril and bisoprolol",
  C09BX03: "ramipril, amlodipine and hydrochlorothiazide",
  C09BX04: "perindopril, bisoprolol and amlodipine",
  C09BX05: "ramipril and bisoprolol",
  C09BX06: "perindopril, bisoprolol, amlodipine and indapamide",
  C09BX07: "zofenopril and nebivolol",
  C09CA01: "losartan",
  C09CA02: "eprosartan",
  C09CA03: "valsartan",
  C09CA04: "irbesartan",
  C09CA05: "tasosartan",
  C09CA06: "candesartan",
  C09CA07: "telmisartan",
  C09CA08: "olmesartan medoxomil",
  C09CA09: "azilsartan medoxomil",
  C09CA10: "fimasartan",
  C09DA01: "losartan and diuretics",
  C09DA02: "eprosartan and diuretics",
  C09DA03: "valsartan and diuretics",
  C09DA04: "irbesartan and diuretics",
  C09DA06: "candesartan and diuretics",
  C09DA07: "telmisartan and diuretics",
  C09DA08: "olmesartan medoxomil and diuretics",
  C09DA09: "azilsartan medoxomil and diuretics",
  C09DA10: "fimasartan and diuretics",
  C09DB01: "valsartan and amlodipine",
  C09DB02: "olmesartan medoxomil and amlodipine",
  C09DB04: "telmisartan and amlodipine",
  C09DB05: "irbesartan and amlodipine",
  C09DB06: "losartan and amlodipine",
  C09DB07: "candesartan and amlodipine",
  C09DB08: "valsartan and lercanidipine",
  C09DB09: "fimasartan and amlodipine",
  C09DX01: "valsartan, amlodipine and hydrochlorothiazide",
  C09DX02: "valsartan and aliskiren",
  C09DX03: "olmesartan medoxomil, amlodipine and hydrochlorothiazide",
  C09DX04: "valsartan and sacubitril",
  C09DX05: "valsartan and nebivolol",
  C09DX06: "candesartan, amlodipine and hydrochlorothiazide",
  C09DX07: "irbesartan, amlodipine and hydrochlorothiazide",
  C09DX08: "telmisartan, amlodipine and hydrochlorothiazide",
  C09DX09: "candesartan, amlodipine and indapamide",
  C09XA01: "remikiren",
  C09XA02: "aliskiren",
  C09XA52: "aliskiren and hydrochlorothiazide",
  C09XA53: "aliskiren and amlodipine",
  C09XA54: "aliskiren, amlodipine and hydrochlorothiazide",
  C09XX01: "sparsentan",
  C10AA01: "simvastatin",
  C10AA02: "lovastatin",
  C10AA03: "pravastatin",
  C10AA04: "fluvastatin",
  C10AA05: "atorvastatin",
  C10AA06: "cerivastatin",
  C10AA07: "rosuvastatin",
  C10AA08: "pitavastatin",
  C10AB01: "clofibrate",
  C10AB02: "bezafibrate",
  C10AB03: "aluminium clofibrate",
  C10AB04: "gemfibrozil",
  C10AB05: "fenofibrate",
  C10AB06: "simfibrate",
  C10AB07: "ronifibrate",
  C10AB08: "ciprofibrate",
  C10AB09: "etofibrate",
  C10AB10: "clofibride",
  C10AB11: "choline fenofibrate",
  C10AB12: "pemafibrate",
  C10AC01: "colestyramine",
  C10AC02: "colestipol",
  C10AC03: "colextran",
  C10AC04: "colesevelam",
  C10AD01: "niceritrol",
  C10AD02: "nicotinic acid",
  C10AD03: "nicofuranose",
  C10AD04: "aluminium nicotinate",
  C10AD05: "nicotinyl alcohol (pyridylcarbinol)",
  C10AD06: "acipimox",
  C10AD52: "nicotinic acid, combinations",
  C10AX01: "dextrothyroxine",
  C10AX02: "probucol",
  C10AX03: "tiadenol",
  C10AX05: "meglutol",
  C10AX06: "omega-3-triglycerides incl. other esters and acids",
  C10AX07: "magnesium pyridoxal 5-phosphate glutamate",
  C10AX08: "policosanol",
  C10AX09: "ezetimibe",
  C10AX10: "alipogene tiparvovec",
  C10AX11: "mipomersen",
  C10AX12: "lomitapide",
  C10AX13: "evolocumab",
  C10AX14: "alirocumab",
  C10AX15: "bempedoic acid",
  C10AX16: "inclisiran",
  C10AX17: "evinacumab",
  C10AX18: "volanesorsen",
  C10AX19: "lerodalcibep",
  C10BA01: "lovastatin and nicotinic acid",
  C10BA02: "simvastatin and ezetimibe",
  C10BA03: "pravastatin and fenofibrate",
  C10BA04: "simvastatin and fenofibrate",
  C10BA05: "atorvastatin and ezetimibe",
  C10BA06: "rosuvastatin and ezetimibe",
  C10BA07: "rosuvastatin and omega-3 fatty acids",
  C10BA08: "atorvastatin and omega-3 fatty acids",
  C10BA09: "rosuvastatin and fenofibrate",
  C10BA10: "bempedoic acid and ezetimibe",
  C10BA11: "pravastatin and ezetimibe",
  C10BA12: "pravastatin, ezetimibe and fenofibrate",
  C10BA13: "pitavastatin and ezetimibe",
  C10BA14: "pitavastatin and fenofibrate",
  C10BA15: "rosuvastatin, ezetimibe and fenofibrate",
  C10BA16: "atorvastatin and fenofibrate",
  C10BX01: "simvastatin and acetylsalicylic acid",
  C10BX02: "pravastatin and acetylsalicylic acid",
  C10BX03: "atorvastatin and amlodipine",
  C10BX04: "simvastatin, acetylsalicylic acid and ramipril",
  C10BX05: "rosuvastatin and acetylsalicylic acid",
  C10BX06: "atorvastatin, acetylsalicylic acid and ramipril",
  C10BX07: "rosuvastatin, amlodipine and lisinopril",
  C10BX08: "atorvastatin and acetylsalicylic acid",
  C10BX09: "rosuvastatin and amlodipine",
  C10BX10: "rosuvastatin and valsartan",
  C10BX11: "atorvastatin, amlodipine and perindopril",
  C10BX12: "atorvastatin, acetylsalicylic acid and perindopril",
  C10BX13: "rosuvastatin, perindopril and indapamide",
  C10BX14: "rosuvastatin, amlodipine and perindopril",
  C10BX15: "atorvastatin and perindopril",
  C10BX16: "rosuvastatin and fimasartan",
  C10BX17: "rosuvastatin and ramipril",
  C10BX18: "atorvastatin, amlodipine and ramipril",
  C10BX19: "atorvastatin, amlodipine and candesartan",
  C10BX20: "rosuvastatin and telmisartan",
  C10BX21: "rosuvastatin and perindopril",
  C10BX22: "rosuvastatin and nebivolol",
  C10BX23: "rosuvastatin, amlodipine and ramipril",
  C10BX24: "rosuvastatin, amlodipine and telmisartan",
  // D — 피부
  D01AA01: "nystatin",
  D01AA02: "natamycin",
  D01AA03: "hachimycin",
  D01AA04: "pecilocin",
  D01AA06: "mepartricin",
  D01AA07: "pyrrolnitrin",
  D01AA08: "griseofulvin",
  D01AA20: "antibiotics in combination with corticosteroids",
  D01AC01: "clotrimazole",
  D01AC02: "miconazole",
  D01AC03: "econazole",
  D01AC04: "chlormidazole",
  D01AC05: "isoconazole",
  D01AC06: "tiabendazole",
  D01AC07: "tioconazole",
  D01AC08: "ketoconazole",
  D01AC09: "sulconazole",
  D01AC10: "bifonazole",
  D01AC11: "oxiconazole",
  D01AC12: "fenticonazole",
  D01AC13: "omoconazole",
  D01AC14: "sertaconazole",
  D01AC15: "fluconazole",
  D01AC16: "flutrimazole",
  D01AC17: "eberconazole",
  D01AC18: "luliconazole",
  D01AC19: "efinaconazole",
  D01AC20: "imidazoles/triazoles in combination with corticosteroids",
  D01AC21: "neticonazole",
  D01AC22: "lanoconazole",
  D01AC52: "miconazole, combinations",
  D01AC60: "bifonazole, combinations",
  D01AE01: "bromochlorosalicylanilide",
  D01AE02: "methylrosaniline",
  D01AE03: "tribromometacresol",
  D01AE04: "undecylenic acid",
  D01AE05: "polynoxylin",
  D01AE06: "2-(4-chlorphenoxy)-ethanol",
  D01AE07: "chlorphenesin",
  D01AE08: "ticlatone",
  D01AE09: "sulbentine",
  D01AE10: "ethyl hydroxybenzoate",
  D01AE11: "haloprogin",
  D01AE12: "salicylic acid",
  D01AE13: "selenium sulfide",
  D01AE14: "ciclopirox",
  D01AE15: "terbinafine",
  D01AE16: "amorolfine",
  D01AE17: "dimazole",
  D01AE18: "tolnaftate",
  D01AE19: "tolciclate",
  D01AE20: "combinations",
  D01AE21: "flucytosine",
  D01AE22: "naftifine",
  D01AE23: "butenafine",
  D01AE24: "tavaborole",
  D01AE25: "liranaftate",
  D01AE54: "undecylenic acid, combinations",
  D01BA01: "griseofulvin",
  D01BA02: "terbinafine",
  D01BA03: "fosravuconazole",
  D02AE01: "carbamide",
  D02AE51: "carbamide, combinations",
  D02BA01: "aminobenzoic acid",
  D02BA02: "octinoxate",
  D02BB01: "betacarotene",
  D02BB02: "afamelanotide",
  D03AX01: "cadexomer iodine",
  D03AX02: "dextranomer",
  D03AX03: "dexpanthenol",
  D03AX04: "calcium pantothenate",
  D03AX05: "hyaluronic acid",
  D03AX06: "becaplermin",
  D03AX09: "crilanomer",
  D03AX10: "enoxolone",
  D03AX11: "sodium chlorite",
  D03AX12: "trolamine",
  D03AX13: "Betulae cortex",
  D03AX14: "Centella asiatica herba, incl. combinations",
  D03AX15: "trafermin",
  D03AX16: "beremagene geperpavec",
  D03AX17: "diperoxochloric acid",
  D03BA01: "trypsin",
  D03BA02: "collagenase",
  D03BA03: "bromelains",
  D03BA52: "collagenase, combinations",
  D04AA01: "thonzylamine",
  D04AA02: "mepyramine",
  D04AA03: "thenalidine",
  D04AA04: "tripelennamine",
  D04AA09: "chloropyramine",
  D04AA10: "promethazine",
  D04AA12: "tolpropamine",
  D04AA13: "dimetindene",
  D04AA14: "clemastine",
  D04AA15: "bamipine",
  D04AA16: "pheniramine",
  D04AA22: "isothipendyl",
  D04AA32: "diphenhydramine",
  D04AA33: "diphenhydramine methylbromide",
  D04AA34: "chlorphenoxamine",
  D04AB01: "lidocaine",
  D04AB02: "cinchocaine",
  D04AB03: "oxybuprocaine",
  D04AB04: "benzocaine",
  D04AB05: "quinisocaine",
  D04AB06: "tetracaine",
  D04AB07: "pramocaine",
  D04AX01: "doxepin",
  D05AC01: "dithranol",
  D05AC51: "dithranol, combinations",
  D05AD01: "trioxysalen",
  D05AD02: "methoxsalen",
  D05AX01: "fumaric acid",
  D05AX02: "calcipotriol",
  D05AX03: "calcitriol",
  D05AX04: "tacalcitol",
  D05AX05: "tazarotene",
  D05AX06: "roflumilast",
  D05AX07: "tapinarof",
  D05AX52: "calcipotriol, combinations",
  D05AX55: "tazarotene and ulobetasol",
  D05BA01: "trioxysalen",
  D05BA02: "methoxsalen",
  D05BA03: "bergapten",
  D05BB01: "etretinate",
  D05BB02: "acitretin",
  D05BX51: "fumaric acid derivatives, combinations",
  D06AA01: "demeclocycline",
  D06AA02: "chlortetracycline",
  D06AA03: "oxytetracycline",
  D06AA04: "tetracycline",
  D06AX01: "fusidic acid",
  D06AX02: "chloramphenicol",
  D06AX04: "neomycin",
  D06AX05: "bacitracin",
  D06AX07: "gentamicin",
  D06AX08: "tyrothricin",
  D06AX09: "mupirocin",
  D06AX10: "virginiamycin",
  D06AX11: "rifaximin",
  D06AX12: "amikacin",
  D06AX13: "retapamulin",
  D06AX14: "ozenoxacin",
  D06AX15: "rifamycin",
  D06BA01: "silver sulfadiazine",
  D06BA02: "sulfathiazole",
  D06BA03: "mafenide",
  D06BA04: "sulfamethizole",
  D06BA05: "sulfanilamide",
  D06BA06: "sulfamerazine",
  D06BA51: "silver sulfadiazine, combinations",
  D06BB01: "idoxuridine",
  D06BB02: "tromantadine",
  D06BB03: "aciclovir",
  D06BB04: "podophyllotoxin",
  D06BB05: "inosine",
  D06BB06: "penciclovir",
  D06BB07: "lysozyme",
  D06BB08: "ibacitabine",
  D06BB09: "edoxudine",
  D06BB10: "imiquimod",
  D06BB11: "docosanol",
  D06BB12: "sinecatechins",
  D06BB13: "berdazimer sodium",
  D06BB53: "aciclovir, combinations",
  D06BX01: "metronidazole",
  D06BX02: "ingenol mebutate",
  D06BX03: "tirbanibulin",
  D07AA01: "methylprednisolone",
  D07AA02: "hydrocortisone",
  D07AA03: "prednisolone",
  D07AB01: "clobetasone",
  D07AB02: "hydrocortisone butyrate",
  D07AB03: "flumetasone",
  D07AB04: "fluocortin",
  D07AB05: "fluperolone",
  D07AB06: "fluorometholone",
  D07AB07: "fluprednidene",
  D07AB08: "desonide",
  D07AB09: "triamcinolone",
  D07AB10: "alclometasone",
  D07AB11: "hydrocortisone buteprate",
  D07AB19: "dexamethasone",
  D07AB21: "clocortolone",
  D07AB30: "combinations of corticosteroids",
  D07AC01: "betamethasone",
  D07AC02: "fluclorolone",
  D07AC03: "desoximetasone",
  D07AC04: "fluocinolone acetonide",
  D07AC05: "fluocortolone",
  D07AC06: "diflucortolone",
  D07AC07: "fludroxycortide",
  D07AC08: "fluocinonide",
  D07AC09: "budesonide",
  D07AC10: "diflorasone",
  D07AC11: "amcinonide",
  D07AC12: "halometasone",
  D07AC13: "mometasone",
  D07AC14: "methylprednisolone aceponate",
  D07AC15: "beclometasone",
  D07AC16: "hydrocortisone aceponate",
  D07AC17: "fluticasone",
  D07AC18: "prednicarbate",
  D07AC19: "difluprednate",
  D07AC21: "ulobetasol",
  D07AD01: "clobetasol",
  D07AD02: "halcinonide",
  D07BA01: "prednisolone and antiseptics",
  D07BA04: "hydrocortisone and antiseptics",
  D07BB01: "flumetasone and antiseptics",
  D07BB02: "desonide and antiseptics",
  D07BB03: "triamcinolone and antiseptics",
  D07BB04: "hydrocortisone butyrate and antiseptics",
  D07BC01: "betamethasone and antiseptics",
  D07BC02: "fluocinolone acetonide and antiseptics",
  D07BC03: "fluocortolone and antiseptics",
  D07BC04: "diflucortolone and antiseptics",
  D07CA01: "hydrocortisone and antibiotics",
  D07CA02: "methylprednisolone and antibiotics",
  D07CA03: "prednisolone and antibiotics",
  D07CB01: "triamcinolone and antibiotics",
  D07CB02: "fluprednidene and antibiotics",
  D07CB03: "fluorometholone and antibiotics",
  D07CB04: "dexamethasone and antibiotics",
  D07CB05: "flumetasone and antibiotics",
  D07CC01: "betamethasone and antibiotics",
  D07CC02: "fluocinolone acetonide and antibiotics",
  D07CC03: "fludroxycortide and antibiotics",
  D07CC04: "beclometasone and antibiotics",
  D07CC05: "fluocinonide and antibiotics",
  D07CC06: "fluocortolone and antibiotics",
  D07CD01: "clobetasol and antibiotics",
  D07XA01: "hydrocortisone",
  D07XA02: "prednisolone",
  D07XB01: "flumetasone",
  D07XB02: "triamcinolone",
  D07XB03: "fluprednidene",
  D07XB04: "fluorometholone",
  D07XB05: "dexamethasone",
  D07XB30: "combinations of corticosteroids",
  D07XC01: "betamethasone",
  D07XC02: "desoximetasone",
  D07XC03: "mometasone",
  D07XC04: "diflucortolone",
  D07XC05: "fluocortolone",
  D08AA01: "ethacridine lactate",
  D08AA02: "aminoacridine",
  D08AA03: "euflavine",
  D08AC01: "dibrompropamidine",
  D08AC02: "chlorhexidine",
  D08AC03: "propamidine",
  D08AC04: "hexamidine",
  D08AC05: "polihexanide",
  D08AC52: "chlorhexidine, combinations",
  D08AE01: "hexachlorophene",
  D08AE02: "policresulen",
  D08AE03: "phenol",
  D08AE04: "triclosan",
  D08AE05: "chloroxylenol",
  D08AE06: "biphenylol",
  D08AF01: "nitrofural",
  D08AG01: "iodine/octylphenoxypolyglycolether",
  D08AG02: "povidone-iodine",
  D08AG03: "iodine",
  D08AG04: "diiodohydroxypropane",
  D08AH01: "dequalinium",
  D08AH02: "chlorquinaldol",
  D08AH03: "oxyquinoline",
  D08AH30: "clioquinol",
  D08AJ01: "benzalkonium",
  D08AJ02: "cetrimonium",
  D08AJ03: "cetylpyridinium",
  D08AJ04: "cetrimide",
  D08AJ05: "benzoxonium chloride",
  D08AJ06: "didecyldimethylammonium chloride",
  D08AJ08: "benzethonium chloride",
  D08AJ10: "decamethoxine",
  D08AJ57: "octenidine, combinations",
  D08AJ58: "benzethonium chloride, combinations",
  D08AJ59: "dodeclonium bromide, combinations",
  D08AK01: "mercuric amidochloride",
  D08AK02: "phenylmercuric borate",
  D08AK03: "mercuric chloride",
  D08AK04: "merbromin",
  D08AK05: "mercury, metallic",
  D08AK06: "thiomersal",
  D08AK30: "mercuric iodide",
  D08AL01: "silver nitrate",
  D08AL30: "silver",
  D08AX01: "hydrogen peroxide",
  D08AX02: "eosin",
  D08AX03: "propanol",
  D08AX04: "tosylchloramide sodium",
  D08AX05: "isopropanol",
  D08AX06: "potassium permanganate",
  D08AX07: "sodium hypochlorite",
  D08AX08: "ethanol",
  D08AX53: "propanol, combinations",
  D09AA01: "framycetin",
  D09AA02: "fusidic acid",
  D09AA03: "nitrofural",
  D09AA04: "phenylmercuric nitrate",
  D09AA05: "benzododecinium",
  D09AA06: "triclosan",
  D09AA07: "cetylpyridinium",
  D09AA08: "aluminium chlorohydrate",
  D09AA09: "povidone-iodine",
  D09AA10: "clioquinol",
  D09AA11: "benzalkonium",
  D09AA12: "chlorhexidine",
  D09AA13: "iodoform",
  D09AB01: "zinc bandage without supplements",
  D09AB02: "zinc bandage with supplements",
  D10AA01: "fluorometholone",
  D10AA02: "methylprednisolone",
  D10AA03: "dexamethasone",
  D10AB01: "bithionol",
  D10AB02: "sulfur",
  D10AB03: "tioxolone",
  D10AB05: "mesulfen",
  D10AD01: "tretinoin",
  D10AD02: "retinol",
  D10AD03: "adapalene",
  D10AD04: "isotretinoin",
  D10AD05: "motretinide",
  D10AD06: "trifarotene",
  D10AD51: "tretinoin, combinations",
  D10AD53: "adapalene, combinations",
  D10AD54: "isotretinoin, combinations",
  D10AE01: "benzoyl peroxide",
  D10AE51: "benzoyl peroxide, combinations",
  D10AF01: "clindamycin",
  D10AF02: "erythromycin",
  D10AF03: "chloramphenicol",
  D10AF04: "meclocycline",
  D10AF05: "nadifloxacin",
  D10AF06: "sulfacetamide",
  D10AF07: "minocycline",
  D10AF51: "clindamycin, combinations",
  D10AF52: "erythromycin, combinations",
  D10AX01: "aluminium chloride",
  D10AX02: "resorcinol",
  D10AX03: "azelaic acid",
  D10AX04: "aluminium oxide",
  D10AX05: "dapsone",
  D10AX06: "clascoterone",
  D10AX30: "various combinations",
  D10BA01: "isotretinoin",
  D10BX01: "ichtasol",
  D11AA01: "glycopyrronium",
  D11AA02: "sofpironium bromide",
  D11AC01: "cetrimide",
  D11AC02: "cadmium compounds",
  D11AC03: "selenium compounds",
  D11AC06: "povidone-iodine",
  D11AC08: "sulfur compounds",
  D11AC09: "xenysalate",
  D11AC30: "others",
  D11AE01: "metandienone",
  D11AH01: "tacrolimus",
  D11AH02: "pimecrolimus",
  D11AH03: "cromoglicic acid",
  D11AH04: "alitretinoin",
  D11AH05: "dupilumab",
  D11AH06: "crisaborole",
  D11AH07: "tralokinumab",
  D11AH08: "abrocitinib",
  D11AH09: "ruxolitinib",
  D11AH10: "lebrikizumab",
  D11AH11: "delgocitinib",
  D11AH12: "nemolizumab",
  D11AX01: "minoxidil",
  D11AX02: "gamolenic acid",
  D11AX03: "calcium gluconate",
  D11AX04: "lithium succinate",
  D11AX05: "magnesium sulfate",
  D11AX06: "mequinol",
  D11AX08: "tiratricol",
  D11AX09: "oxaceprol",
  D11AX10: "finasteride",
  D11AX11: "hydroquinone",
  D11AX12: "pyrithione zinc",
  D11AX13: "monobenzone",
  D11AX16: "eflornithine",
  D11AX18: "diclofenac",
  D11AX21: "brimonidine",
  D11AX22: "ivermectin",
  D11AX23: "aminobenzoate potassium",
  D11AX24: "deoxycholic acid",
  D11AX25: "hydrogen peroxide",
  D11AX26: "caffeine",
  D11AX27: "oxymetazoline",
  D11AX52: "gamolenic acid, combinations",
  D11AX57: "collagen, combinations",
  // G — 비뇨생식기계 및 성호르몬
  G01AA01: "nystatin",
  G01AA02: "natamycin",
  G01AA03: "amphotericin B",
  G01AA04: "candicidin",
  G01AA05: "chloramphenicol",
  G01AA06: "hachimycin",
  G01AA07: "oxytetracycline",
  G01AA08: "carfecillin",
  G01AA09: "mepartricin",
  G01AA10: "clindamycin",
  G01AA11: "pentamycin",
  G01AA51: "nystatin, combinations",
  G01AB01: "acetarsol",
  G01AC01: "diiodohydroxyquinoline",
  G01AC02: "clioquinol",
  G01AC03: "chlorquinaldol",
  G01AC05: "dequalinium",
  G01AC06: "broxyquinoline",
  G01AC30: "oxyquinoline",
  G01AD01: "lactic acid",
  G01AD02: "acetic acid",
  G01AD03: "ascorbic acid",
  G01AE01: "sulfatolamide",
  G01AE10: "combinations of sulfonamides",
  G01AF01: "metronidazole",
  G01AF02: "clotrimazole",
  G01AF04: "miconazole",
  G01AF05: "econazole",
  G01AF06: "ornidazole",
  G01AF07: "isoconazole",
  G01AF08: "tioconazole",
  G01AF11: "ketoconazole",
  G01AF12: "fenticonazole",
  G01AF13: "azanidazole",
  G01AF14: "propenidazole",
  G01AF15: "butoconazole",
  G01AF16: "omoconazole",
  G01AF17: "oxiconazole",
  G01AF18: "flutrimazole",
  G01AF19: "sertaconazole",
  G01AF20: "combinations of imidazole derivatives",
  G01AF21: "tinidazole",
  G01AF55: "econazole, combinations",
  G01AG02: "terconazole",
  G01AX01: "clodantoin",
  G01AX02: "inosine",
  G01AX03: "policresulen",
  G01AX05: "nifuratel",
  G01AX06: "furazolidone",
  G01AX09: "methylrosaniline",
  G01AX11: "povidone-iodine",
  G01AX12: "ciclopirox",
  G01AX13: "protiofate",
  G01AX14: "lactobacillus",
  G01AX15: "copper usnate",
  G01AX16: "hexetidine",
  G01AX17: "dapivirine",
  G01AX66: "octenidine, combinations",
  G02AB01: "methylergometrine",
  G02AB02: "ergot alkaloids",
  G02AB03: "ergometrine",
  G02AC01: "methylergometrine and oxytocin",
  G02AD01: "dinoprost",
  G02AD02: "dinoprostone",
  G02AD03: "gemeprost",
  G02AD04: "carboprost",
  G02AD05: "sulprostone",
  G02AD06: "misoprostol",
  G02BA01: "plastic IUD",
  G02BA02: "plastic IUD with copper",
  G02BA03: "plastic IUD with progestogen",
  G02BB01: "vaginal ring with progestogen and estrogen",
  G02BB02: "vaginal ring with progestogen",
  G02CA01: "ritodrine",
  G02CA02: "buphenine",
  G02CA03: "fenoterol",
  G02CB01: "bromocriptine",
  G02CB02: "lisuride",
  G02CB03: "cabergoline",
  G02CB04: "quinagolide",
  G02CB05: "metergoline",
  G02CB06: "terguride",
  G02CC01: "ibuprofen",
  G02CC02: "naproxen",
  G02CC03: "benzydamine",
  G02CC04: "flunoxaprofen",
  G02CX01: "atosiban",
  G02CX02: "flibanserin",
  G02CX03: "Agni casti fructus",
  G02CX04: "Cimicifugae rhizoma",
  G02CX05: "bremelanotide",
  G02CX06: "fezolinetant",
  G02CX07: "elinzanetant",
  G03AA01: "etynodiol and ethinylestradiol",
  G03AA02: "quingestanol and ethinylestradiol",
  G03AA03: "lynestrenol and ethinylestradiol",
  G03AA04: "megestrol and ethinylestradiol",
  G03AA05: "norethisterone and ethinylestradiol",
  G03AA06: "norgestrel and ethinylestradiol",
  G03AA07: "levonorgestrel and ethinylestradiol",
  G03AA08: "medroxyprogesterone and ethinylestradiol",
  G03AA09: "desogestrel and ethinylestradiol",
  G03AA10: "gestodene and ethinylestradiol",
  G03AA11: "norgestimate and ethinylestradiol",
  G03AA12: "drospirenone and ethinylestradiol",
  G03AA13: "norelgestromin and ethinylestradiol",
  G03AA14: "nomegestrol and estradiol",
  G03AA15: "chlormadinone and ethinylestradiol",
  G03AA16: "dienogest and ethinylestradiol",
  G03AA17: "medroxyprogesterone and estradiol",
  G03AA18: "drospirenone and estetrol",
  G03AB01: "megestrol and ethinylestradiol",
  G03AB02: "lynestrenol and ethinylestradiol",
  G03AB03: "levonorgestrel and ethinylestradiol",
  G03AB04: "norethisterone and ethinylestradiol",
  G03AB05: "desogestrel and ethinylestradiol",
  G03AB06: "gestodene and ethinylestradiol",
  G03AB07: "chlormadinone and ethinylestradiol",
  G03AB08: "dienogest and estradiol",
  G03AB09: "norgestimate and ethinylestradiol",
  G03AC01: "norethisterone",
  G03AC02: "lynestrenol",
  G03AC03: "levonorgestrel",
  G03AC04: "quingestanol",
  G03AC05: "megestrol",
  G03AC06: "medroxyprogesterone",
  G03AC07: "norgestrienone",
  G03AC08: "etonogestrel",
  G03AC09: "desogestrel",
  G03AC10: "drospirenone",
  G03AD01: "levonorgestrel",
  G03AD02: "ulipristal",
  G03BA01: "fluoxymesterone",
  G03BA02: "methyltestosterone",
  G03BA03: "testosterone",
  G03BB01: "mesterolone",
  G03BB02: "androstanolone",
  G03CA01: "ethinylestradiol",
  G03CA03: "estradiol",
  G03CA04: "estriol",
  G03CA06: "chlorotrianisene",
  G03CA07: "estrone",
  G03CA09: "promestriene",
  G03CA10: "estetrol",
  G03CA53: "estradiol, combinations",
  G03CA57: "conjugated estrogens",
  G03CB01: "dienestrol",
  G03CB02: "diethylstilbestrol",
  G03CB03: "methallenestril",
  G03CB04: "moxestrol",
  G03CC02: "dienestrol",
  G03CC03: "methallenestril",
  G03CC04: "estrone",
  G03CC05: "diethylstilbestrol",
  G03CC06: "estriol",
  G03CC07: "conjugated estrogens and bazedoxifene",
  G03CX01: "tibolone",
  G03DA01: "gestonorone",
  G03DA02: "medroxyprogesterone",
  G03DA03: "hydroxyprogesterone",
  G03DA04: "progesterone",
  G03DB01: "dydrogesterone",
  G03DB02: "megestrol",
  G03DB03: "medrogestone",
  G03DB04: "nomegestrol",
  G03DB05: "demegestone",
  G03DB06: "chlormadinone",
  G03DB07: "promegestone",
  G03DB08: "dienogest",
  G03DC01: "allylestrenol",
  G03DC02: "norethisterone",
  G03DC03: "lynestrenol",
  G03DC04: "ethisterone",
  G03DC06: "etynodiol",
  G03DC31: "methylestrenolone",
  G03EA01: "methyltestosterone and estrogen",
  G03EA02: "testosterone and estrogen",
  G03EA03: "prasterone and estrogen",
  G03EK01: "methyltestosterone",
  G03FA01: "norethisterone and estrogen",
  G03FA02: "hydroxyprogesterone and estrogen",
  G03FA03: "ethisterone and estrogen",
  G03FA04: "progesterone and estrogen",
  G03FA05: "methylnortestosterone and estrogen",
  G03FA06: "etynodiol and estrogen",
  G03FA07: "lynestrenol and estrogen",
  G03FA08: "megestrol and estrogen",
  G03FA09: "noretynodrel and estrogen",
  G03FA10: "norgestrel and estrogen",
  G03FA11: "levonorgestrel and estrogen",
  G03FA12: "medroxyprogesterone and estrogen",
  G03FA13: "norgestimate and estrogen",
  G03FA14: "dydrogesterone and estrogen",
  G03FA15: "dienogest and estrogen",
  G03FA16: "trimegestone and estrogen",
  G03FA17: "drospirenone and estrogen",
  G03FB01: "norgestrel and estrogen",
  G03FB02: "lynestrenol and estrogen",
  G03FB03: "chlormadinone and estrogen",
  G03FB04: "megestrol and estrogen",
  G03FB05: "norethisterone and estrogen",
  G03FB06: "medroxyprogesterone and estrogen",
  G03FB07: "medrogestone and estrogen",
  G03FB08: "dydrogesterone and estrogen",
  G03FB09: "levonorgestrel and estrogen",
  G03FB10: "desogestrel and estrogen",
  G03FB11: "trimegestone and estrogen",
  G03FB12: "nomegestrol and estrogen",
  G03GA01: "chorionic gonadotrophin",
  G03GA02: "human menopausal gonadotrophin",
  G03GA03: "serum gonadotrophin",
  G03GA04: "urofollitropin",
  G03GA05: "follitropin alfa",
  G03GA06: "follitropin beta",
  G03GA07: "lutropin alfa",
  G03GA08: "choriogonadotropin alfa",
  G03GA09: "corifollitropin alfa",
  G03GA10: "follitropin delta",
  G03GA30: "combinations",
  G03GB01: "cyclofenil",
  G03GB02: "clomifene",
  G03GB03: "epimestrol",
  G03HA01: "cyproterone",
  G03HB01: "cyproterone and estrogen",
  G03XA01: "danazol",
  G03XA02: "gestrinone",
  G03XB01: "mifepristone",
  G03XB02: "ulipristal",
  G03XB51: "mifepristone, combinations",
  G03XC01: "raloxifene",
  G03XC02: "bazedoxifene",
  G03XC03: "lasofoxifene",
  G03XC04: "ormeloxifene",
  G03XC05: "ospemifene",
  G03XX01: "prasterone",
  G04BA01: "ammonium chloride",
  G04BA03: "calcium chloride",
  G04BD01: "emepronium",
  G04BD02: "flavoxate",
  G04BD03: "meladrazine",
  G04BD04: "oxybutynin",
  G04BD05: "terodiline",
  G04BD06: "propiverine",
  G04BD07: "tolterodine",
  G04BD08: "solifenacin",
  G04BD09: "trospium",
  G04BD10: "darifenacin",
  G04BD11: "fesoterodine",
  G04BD12: "mirabegron",
  G04BD13: "desfesoterodine",
  G04BD14: "imidafenacin",
  G04BD15: "vibegron",
  G04BE01: "alprostadil",
  G04BE02: "papaverine",
  G04BE03: "sildenafil",
  G04BE04: "yohimbine",
  G04BE06: "moxisylyte",
  G04BE07: "apomorphine",
  G04BE08: "tadalafil",
  G04BE09: "vardenafil",
  G04BE10: "avanafil",
  G04BE11: "udenafil",
  G04BE30: "combinations",
  G04BE52: "papaverine, combinations",
  G04BX01: "magnesium hydroxide",
  G04BX03: "acetohydroxamic acid",
  G04BX06: "phenazopyridine",
  G04BX10: "succinimide",
  G04BX11: "collagen",
  G04BX12: "phenyl salicylate",
  G04BX13: "dimethyl sulfoxide",
  G04BX14: "dapoxetine",
  G04BX15: "pentosan polysulfate sodium",
  G04BX16: "tiopronin",
  G04BX17: "sodium salicylate and methenamine",
  G04CA01: "alfuzosin",
  G04CA02: "tamsulosin",
  G04CA03: "terazosin",
  G04CA04: "silodosin",
  G04CA51: "alfuzosin and finasteride",
  G04CA52: "tamsulosin and dutasteride",
  G04CA53: "tamsulosin and solifenacin",
  G04CA54: "tamsulosin and tadalafil",
  G04CA55: "doxazosin and finasteride",
  G04CB01: "finasteride",
  G04CB02: "dutasteride",
  G04CB51: "finasteride and tadalafil",
  G04CX01: "Prunus africanae cortex",
  G04CX02: "Sabalis serrulatae fructus",
  G04CX03: "mepartricin",
  G04CX04: "fexapotide",
  // H — 전신호르몬제(성호르몬 제외)
  H01AA01: "corticotropin",
  H01AA02: "tetracosactide",
  H01AB01: "thyrotropin alfa",
  H01AC01: "somatropin",
  H01AC02: "somatrem",
  H01AC03: "mecasermin",
  H01AC04: "sermorelin",
  H01AC05: "mecasermin rinfabate",
  H01AC06: "tesamorelin",
  H01AC07: "somapacitan",
  H01AC08: "somatrogon",
  H01AC09: "lonapegsomatropin",
  H01AX01: "pegvisomant",
  H01BA01: "vasopressin (argipressin)",
  H01BA02: "desmopressin",
  H01BA03: "lypressin",
  H01BA04: "terlipressin",
  H01BA05: "ornipressin",
  H01BB01: "demoxytocin",
  H01BB02: "oxytocin",
  H01BB03: "carbetocin",
  H01CA01: "gonadorelin",
  H01CA02: "nafarelin",
  H01CB01: "somatostatin",
  H01CB02: "octreotide",
  H01CB03: "lanreotide",
  H01CB04: "vapreotide",
  H01CB05: "pasireotide",
  H01CB06: "paltusotine",
  H01CC01: "ganirelix",
  H01CC02: "cetrorelix",
  H01CC03: "elagolix",
  H01CC04: "linzagolix",
  H01CC53: "elagolix, estradiol and norethisterone",
  H01CC54: "relugolix, estradiol and norethisterone",
  H02AA01: "aldosterone",
  H02AA02: "fludrocortisone",
  H02AA03: "desoxycortone",
  H02AB01: "betamethasone",
  H02AB02: "dexamethasone",
  H02AB03: "fluocortolone",
  H02AB04: "methylprednisolone",
  H02AB05: "paramethasone",
  H02AB06: "prednisolone",
  H02AB07: "prednisone",
  H02AB08: "triamcinolone",
  H02AB09: "hydrocortisone",
  H02AB10: "cortisone",
  H02AB11: "prednylidene",
  H02AB12: "rimexolone",
  H02AB13: "deflazacort",
  H02AB14: "cloprednol",
  H02AB15: "meprednisone",
  H02AB17: "cortivazol",
  H02AB18: "vamorolone",
  H02BX01: "methylprednisolone, combinations",
  H02CA01: "trilostane",
  H02CA02: "osilodrostat",
  H02CA03: "ketoconazole",
  H02CA04: "levoketoconazole",
  H03AA01: "levothyroxine sodium",
  H03AA02: "liothyronine sodium",
  H03AA03: "combinations of levothyroxine and liothyronine",
  H03AA04: "tiratricol",
  H03AA05: "thyroid gland preparations",
  H03AA51: "levothyroxine sodium and iodine compounds",
  H03BA01: "methylthiouracil",
  H03BA02: "propylthiouracil",
  H03BA03: "benzylthiouracil",
  H03BB01: "carbimazole",
  H03BB02: "thiamazole",
  H03BB52: "thiamazole, combinations",
  H03BC01: "potassium perchlorate",
  H03BX01: "diiodotyrosine",
  H03BX02: "dibromotyrosine",
  H04AA01: "glucagon",
  H04AA02: "dasiglucagon",
  H05AA01: "parathyroid gland extract",
  H05AA02: "teriparatide",
  H05AA03: "parathyroid hormone",
  H05AA04: "abaloparatide",
  H05AA05: "palopegteriparatide",
  H05BA01: "calcitonin (salmon synthetic)",
  H05BA02: "calcitonin (pork natural)",
  H05BA03: "calcitonin (human synthetic)",
  H05BA04: "elcatonin",
  H05BX01: "cinacalcet",
  H05BX02: "paricalcitol",
  H05BX03: "doxercalciferol",
  H05BX04: "etelcalcetide",
  H05BX05: "calcifediol",
  H05BX06: "evocalcet",
  // J — 전신 항감염제
  J01AA01: "demeclocycline",
  J01AA02: "doxycycline",
  J01AA03: "chlortetracycline",
  J01AA04: "lymecycline",
  J01AA05: "metacycline",
  J01AA06: "oxytetracycline",
  J01AA07: "tetracycline",
  J01AA08: "minocycline",
  J01AA09: "rolitetracycline",
  J01AA10: "penimepicycline",
  J01AA11: "clomocycline",
  J01AA12: "tigecycline",
  J01AA13: "eravacycline",
  J01AA14: "sarecycline",
  J01AA15: "omadacycline",
  J01AA20: "combinations of tetracyclines",
  J01AA56: "oxytetracycline, combinations",
  J01BA01: "chloramphenicol",
  J01BA02: "thiamphenicol",
  J01BA52: "thiamphenicol, combinations",
  J01CA01: "ampicillin",
  J01CA02: "pivampicillin",
  J01CA03: "carbenicillin",
  J01CA04: "amoxicillin",
  J01CA05: "carindacillin",
  J01CA06: "bacampicillin",
  J01CA07: "epicillin",
  J01CA08: "pivmecillinam",
  J01CA09: "azlocillin",
  J01CA10: "mezlocillin",
  J01CA11: "mecillinam",
  J01CA12: "piperacillin",
  J01CA13: "ticarcillin",
  J01CA14: "metampicillin",
  J01CA15: "talampicillin",
  J01CA16: "sulbenicillin",
  J01CA17: "temocillin",
  J01CA18: "hetacillin",
  J01CA19: "aspoxicillin",
  J01CA20: "combinations",
  J01CA51: "ampicillin, combinations",
  J01CE01: "benzylpenicillin",
  J01CE02: "phenoxymethylpenicillin",
  J01CE03: "propicillin",
  J01CE04: "azidocillin",
  J01CE05: "pheneticillin",
  J01CE06: "penamecillin",
  J01CE07: "clometocillin",
  J01CE08: "benzathine benzylpenicillin",
  J01CE09: "procaine benzylpenicillin",
  J01CE10: "benzathine phenoxymethylpenicillin",
  J01CE30: "combinations",
  J01CF01: "dicloxacillin",
  J01CF02: "cloxacillin",
  J01CF03: "meticillin",
  J01CF04: "oxacillin",
  J01CF05: "flucloxacillin",
  J01CF06: "nafcillin",
  J01CG01: "sulbactam",
  J01CG02: "tazobactam",
  J01CG30: "sulbactam and durlobactam",
  J01CR01: "ampicillin and beta-lactamase inhibitor",
  J01CR02: "amoxicillin and beta-lactamase inhibitor",
  J01CR03: "ticarcillin and beta-lactamase inhibitor",
  J01CR04: "sultamicillin",
  J01CR05: "piperacillin and beta-lactamase inhibitor",
  J01CR50: "combinations of penicillins",
  J01DB01: "cefalexin",
  J01DB02: "cefaloridine",
  J01DB03: "cefalotin",
  J01DB04: "cefazolin",
  J01DB05: "cefadroxil",
  J01DB06: "cefazedone",
  J01DB07: "cefatrizine",
  J01DB08: "cefapirin",
  J01DB09: "cefradine",
  J01DB10: "cefacetrile",
  J01DB11: "cefroxadine",
  J01DB12: "ceftezole",
  J01DC01: "cefoxitin",
  J01DC02: "cefuroxime",
  J01DC03: "cefamandole",
  J01DC04: "cefaclor",
  J01DC05: "cefotetan",
  J01DC06: "cefonicid",
  J01DC07: "cefotiam",
  J01DC08: "loracarbef",
  J01DC09: "cefmetazole",
  J01DC10: "cefprozil",
  J01DC11: "ceforanide",
  J01DC12: "cefminox",
  J01DC13: "cefbuperazone",
  J01DC14: "flomoxef",
  J01DC52: "cefuroxime and beta-lactamase inhibitor",
  J01DD01: "cefotaxime",
  J01DD02: "ceftazidime",
  J01DD03: "cefsulodin",
  J01DD04: "ceftriaxone",
  J01DD05: "cefmenoxime",
  J01DD06: "latamoxef",
  J01DD07: "ceftizoxime",
  J01DD08: "cefixime",
  J01DD09: "cefodizime",
  J01DD10: "cefetamet",
  J01DD11: "cefpiramide",
  J01DD12: "cefoperazone",
  J01DD13: "cefpodoxime",
  J01DD14: "ceftibuten",
  J01DD15: "cefdinir",
  J01DD16: "cefditoren",
  J01DD17: "cefcapene",
  J01DD18: "cefteram",
  J01DD51: "cefotaxime and beta-lactamase inhibitor",
  J01DD52: "ceftazidime and beta-lactamase inhibitor",
  J01DD54: "ceftriaxone, combinations",
  J01DD58: "cefixime and beta-lactamase inhibitor",
  J01DD62: "cefoperazone and beta-lactamase inhibitor",
  J01DD63: "ceftriaxone and beta-lactamase inhibitor",
  J01DD64: "cefpodoxime and beta-lactamase inhibitor",
  J01DE01: "cefepime",
  J01DE02: "cefpirome",
  J01DE03: "cefozopran",
  J01DE51: "cefepime and beta-lactamase inhibitor",
  J01DF01: "aztreonam",
  J01DF02: "carumonam",
  J01DF51: "aztreonam and beta-lactamase inhibitor",
  J01DH02: "meropenem",
  J01DH03: "ertapenem",
  J01DH04: "doripenem",
  J01DH05: "biapenem",
  J01DH06: "tebipenem pivoxil",
  J01DH51: "imipenem and cilastatin",
  J01DH52: "meropenem and vaborbactam",
  J01DH55: "panipenem and betamipron",
  J01DH56: "imipenem, cilastatin and relebactam",
  J01DI01: "ceftobiprole medocaril",
  J01DI02: "ceftaroline fosamil",
  J01DI03: "faropenem",
  J01DI04: "cefiderocol",
  J01DI54: "ceftolozane and beta-lactamase inhibitor",
  J01DI55: "sulopenem and probenecid",
  J01EA01: "trimethoprim",
  J01EA02: "brodimoprim",
  J01EA03: "iclaprim",
  J01EB01: "sulfaisodimidine",
  J01EB02: "sulfamethizole",
  J01EB03: "sulfadimidine",
  J01EB04: "sulfapyridine",
  J01EB05: "sulfafurazole",
  J01EB06: "sulfanilamide",
  J01EB07: "sulfathiazole",
  J01EB08: "sulfathiourea",
  J01EB20: "combinations",
  J01EC01: "sulfamethoxazole",
  J01EC02: "sulfadiazine",
  J01EC03: "sulfamoxole",
  J01EC20: "combinations",
  J01ED01: "sulfadimethoxine",
  J01ED02: "sulfalene",
  J01ED03: "sulfametomidine",
  J01ED04: "sulfametoxydiazine",
  J01ED05: "sulfamethoxypyridazine",
  J01ED06: "sulfaperin",
  J01ED07: "sulfamerazine",
  J01ED08: "sulfaphenazole",
  J01ED09: "sulfamazone",
  J01ED20: "combinations",
  J01EE01: "sulfamethoxazole and trimethoprim",
  J01EE02: "sulfadiazine and trimethoprim",
  J01EE03: "sulfametrole and trimethoprim",
  J01EE04: "sulfamoxole and trimethoprim",
  J01EE05: "sulfadimidine and trimethoprim",
  J01EE06: "sulfadiazine and tetroxoprim",
  J01EE07: "sulfamerazine and trimethoprim",
  J01FA01: "erythromycin",
  J01FA02: "spiramycin",
  J01FA03: "midecamycin",
  J01FA05: "oleandomycin",
  J01FA06: "roxithromycin",
  J01FA07: "josamycin",
  J01FA08: "troleandomycin",
  J01FA09: "clarithromycin",
  J01FA10: "azithromycin",
  J01FA11: "miocamycin",
  J01FA12: "rokitamycin",
  J01FA13: "dirithromycin",
  J01FA14: "flurithromycin",
  J01FA15: "telithromycin",
  J01FA16: "solithromycin",
  J01FA17: "nafithromycin",
  J01FF01: "clindamycin",
  J01FF02: "lincomycin",
  J01FG01: "pristinamycin",
  J01FG02: "quinupristin/dalfopristin",
  J01GA01: "streptomycin",
  J01GA02: "streptoduocin",
  J01GB01: "tobramycin",
  J01GB03: "gentamicin",
  J01GB04: "kanamycin",
  J01GB05: "neomycin",
  J01GB06: "amikacin",
  J01GB07: "netilmicin",
  J01GB08: "sisomicin",
  J01GB09: "dibekacin",
  J01GB10: "ribostamycin",
  J01GB11: "isepamicin",
  J01GB12: "arbekacin",
  J01GB13: "bekanamycin",
  J01GB14: "plazomicin",
  J01MA01: "ofloxacin",
  J01MA02: "ciprofloxacin",
  J01MA03: "pefloxacin",
  J01MA04: "enoxacin",
  J01MA05: "temafloxacin",
  J01MA06: "norfloxacin",
  J01MA07: "lomefloxacin",
  J01MA08: "fleroxacin",
  J01MA09: "sparfloxacin",
  J01MA10: "rufloxacin",
  J01MA11: "grepafloxacin",
  J01MA12: "levofloxacin",
  J01MA13: "trovafloxacin",
  J01MA14: "moxifloxacin",
  J01MA15: "gemifloxacin",
  J01MA16: "gatifloxacin",
  J01MA17: "prulifloxacin",
  J01MA18: "pazufloxacin",
  J01MA19: "garenoxacin",
  J01MA21: "sitafloxacin",
  J01MA22: "tosufloxacin",
  J01MA23: "delafloxacin",
  J01MA24: "levonadifloxacin",
  J01MA25: "lascufloxacin",
  J01MB01: "rosoxacin",
  J01MB02: "nalidixic acid",
  J01MB03: "piromidic acid",
  J01MB04: "pipemidic acid",
  J01MB05: "oxolinic acid",
  J01MB06: "cinoxacin",
  J01MB07: "flumequine",
  J01MB08: "nemonoxacin",
  J01RA01: "penicillins, combinations with other antibacterials",
  J01RA02: "sulfonamides, combinations with other antibacterials (excl. trimethoprim)",
  J01RA03: "cefuroxime and metronidazole",
  J01RA04: "spiramycin and metronidazole",
  J01RA05: "levofloxacin and ornidazole",
  J01RA06: "cefepime and amikacin",
  J01RA07: "azithromycin, fluconazole and secnidazole",
  J01RA08: "tetracycline and oleandomycin",
  J01RA09: "ofloxacin and ornidazole",
  J01RA10: "ciprofloxacin and metronidazole",
  J01RA11: "ciprofloxacin and tinidazole",
  J01RA12: "ciprofloxacin and ornidazole",
  J01RA13: "norfloxacin and tinidazole",
  J01RA14: "norfloxacin and metronidazole",
  J01RA15: "cefixime and ornidazole",
  J01RA16: "cefixime and azithromycin",
  J01RA17: "ofloxacin and nitazoxanide",
  J01RA18: "ofloxacin and tinidazole",
  J01RA19: "tetracycline and nystatin",
  J01XA01: "vancomycin",
  J01XA02: "teicoplanin",
  J01XA03: "telavancin",
  J01XA04: "dalbavancin",
  J01XA05: "oritavancin",
  J01XB01: "colistin",
  J01XB02: "polymyxin B",
  J01XC01: "fusidic acid",
  J01XD01: "metronidazole",
  J01XD02: "tinidazole",
  J01XD03: "ornidazole",
  J01XE01: "nitrofurantoin",
  J01XE02: "nifurtoinol",
  J01XE03: "furazidin",
  J01XE51: "nitrofurantoin, combinations",
  J01XX01: "fosfomycin",
  J01XX02: "xibornol",
  J01XX03: "clofoctol",
  J01XX04: "spectinomycin",
  J01XX05: "methenamine",
  J01XX06: "mandelic acid",
  J01XX07: "nitroxoline",
  J01XX08: "linezolid",
  J01XX09: "daptomycin",
  J01XX10: "bacitracin",
  J01XX11: "tedizolid",
  J01XX12: "lefamulin",
  J01XX13: "gepotidacin",
  J02AA01: "amphotericin B",
  J02AA02: "hachimycin",
  J02AB01: "miconazole",
  J02AB02: "ketoconazole",
  J02AC01: "fluconazole",
  J02AC02: "itraconazole",
  J02AC03: "voriconazole",
  J02AC04: "posaconazole",
  J02AC05: "isavuconazole",
  J02AC06: "oteseconazole",
  J02AX01: "flucytosine",
  J02AX04: "caspofungin",
  J02AX05: "micafungin",
  J02AX06: "anidulafungin",
  J02AX07: "ibrexafungerp",
  J02AX08: "rezafungin acetate",
  J04AA01: "4-aminosalicylic acid",
  J04AA02: "sodium aminosalicylate",
  J04AA03: "calcium aminosalicylate",
  J04AB01: "cycloserine",
  J04AB02: "rifampicin",
  J04AB03: "rifamycin",
  J04AB04: "rifabutin",
  J04AB05: "rifapentine",
  J04AB06: "enviomycin",
  J04AB30: "capreomycin",
  J04AC01: "isoniazid",
  J04AC02: "ftivazide",
  J04AC51: "isoniazid, combinations",
  J04AD01: "protionamide",
  J04AD02: "tiocarlide",
  J04AD03: "ethionamide",
  J04AK01: "pyrazinamide",
  J04AK02: "ethambutol",
  J04AK03: "terizidone",
  J04AK04: "morinamide",
  J04AK05: "bedaquiline",
  J04AK06: "delamanid",
  J04AK07: "thioacetazone",
  J04AK08: "pretomanid",
  J04AM01: "streptomycin and isoniazid",
  J04AM02: "rifampicin and isoniazid",
  J04AM03: "ethambutol and isoniazid",
  J04AM04: "thioacetazone and isoniazid",
  J04AM05: "rifampicin, pyrazinamide and isoniazid",
  J04AM06: "rifampicin, pyrazinamide, ethambutol and isoniazid",
  J04AM07: "rifampicin, ethambutol and isoniazid",
  J04AM08: "isoniazid, sulfamethoxazole and trimethoprim",
  J04AM09: "pyrazinamide, ethambutol, isoniazid and lomefloxacin",
  J04AM10: "pyrazinamide, ethambutol, protionamide and lomefloxacin",
  J04AM11: "rifabutin, pyrazinamide and protionamide",
  J04AM12: "rifampicin, pyrazinamide, isoniazid and levofloxacin",
  J04BA01: "clofazimine",
  J04BA02: "dapsone",
  J04BA03: "aldesulfone sodium",
  J04BA50: "dapsone and rifampicin",
  J04BA51: "dapsone, rifampicin and clofazimine",
  J05AA01: "metisazone",
  J05AB01: "aciclovir",
  J05AB02: "idoxuridine",
  J05AB03: "vidarabine",
  J05AB06: "ganciclovir",
  J05AB09: "famciclovir",
  J05AB11: "valaciclovir",
  J05AB12: "cidofovir",
  J05AB13: "penciclovir",
  J05AB14: "valganciclovir",
  J05AB15: "brivudine",
  J05AB16: "remdesivir",
  J05AB17: "brincidofovir",
  J05AB18: "molnupiravir",
  J05AC02: "rimantadine",
  J05AC03: "tromantadine",
  J05AD01: "foscarnet",
  J05AD02: "fosfonet",
  J05AE01: "saquinavir",
  J05AE02: "indinavir",
  J05AE03: "ritonavir",
  J05AE04: "nelfinavir",
  J05AE05: "amprenavir",
  J05AE07: "fosamprenavir",
  J05AE08: "atazanavir",
  J05AE09: "tipranavir",
  J05AE10: "darunavir",
  J05AE16: "ensitrelvir",
  J05AE30: "nirmatrelvir and ritonavir",
  J05AF01: "zidovudine",
  J05AF02: "didanosine",
  J05AF03: "zalcitabine",
  J05AF04: "stavudine",
  J05AF05: "lamivudine",
  J05AF06: "abacavir",
  J05AF07: "tenofovir disoproxil",
  J05AF08: "adefovir dipivoxil",
  J05AF09: "emtricitabine",
  J05AF10: "entecavir",
  J05AF11: "telbivudine",
  J05AF12: "clevudine",
  J05AF13: "tenofovir alafenamide",
  J05AG01: "nevirapine",
  J05AG02: "delavirdine",
  J05AG03: "efavirenz",
  J05AG04: "etravirine",
  J05AG05: "rilpivirine",
  J05AG06: "doravirine",
  J05AG07: "elsulfavirine",
  J05AH01: "zanamivir",
  J05AH02: "oseltamivir",
  J05AH03: "peramivir",
  J05AH04: "laninamivir",
  J05AJ01: "raltegravir",
  J05AJ02: "elvitegravir",
  J05AJ03: "dolutegravir",
  J05AJ04: "cabotegravir",
  J05AP01: "ribavirin",
  J05AP02: "telaprevir",
  J05AP03: "boceprevir",
  J05AP04: "faldaprevir",
  J05AP05: "simeprevir",
  J05AP06: "asunaprevir",
  J05AP07: "daclatasvir",
  J05AP08: "sofosbuvir",
  J05AP09: "dasabuvir",
  J05AP10: "elbasvir",
  J05AP11: "grazoprevir",
  J05AP12: "coblopasvir",
  J05AP13: "ravidasvir",
  J05AP14: "narlaprevir",
  J05AP51: "sofosbuvir and ledipasvir",
  J05AP52: "dasabuvir, ombitasvir, paritaprevir and ritonavir",
  J05AP53: "ombitasvir, paritaprevir and ritonavir",
  J05AP54: "elbasvir and grazoprevir",
  J05AP55: "sofosbuvir and velpatasvir",
  J05AP56: "sofosbuvir, velpatasvir and voxilaprevir",
  J05AP57: "glecaprevir and pibrentasvir",
  J05AP58: "daclatasvir, asunaprevir and beclabuvir",
  J05AR01: "zidovudine and lamivudine",
  J05AR02: "lamivudine and abacavir",
  J05AR03: "tenofovir disoproxil and emtricitabine",
  J05AR04: "zidovudine, lamivudine and abacavir",
  J05AR05: "zidovudine, lamivudine and nevirapine",
  J05AR06: "emtricitabine, tenofovir disoproxil and efavirenz",
  J05AR07: "stavudine, lamivudine and nevirapine",
  J05AR08: "emtricitabine, tenofovir disoproxil and rilpivirine",
  J05AR09: "emtricitabine, tenofovir disoproxil, elvitegravir and cobicistat",
  J05AR10: "lopinavir and ritonavir",
  J05AR11: "lamivudine, tenofovir disoproxil and efavirenz",
  J05AR12: "lamivudine and tenofovir disoproxil",
  J05AR13: "lamivudine, abacavir and dolutegravir",
  J05AR14: "darunavir and cobicistat",
  J05AR15: "atazanavir and cobicistat",
  J05AR16: "lamivudine and raltegravir",
  J05AR17: "emtricitabine and tenofovir alafenamide",
  J05AR18: "emtricitabine, tenofovir alafenamide, elvitegravir and cobicistat",
  J05AR19: "emtricitabine, tenofovir alafenamide and rilpivirine",
  J05AR20: "emtricitabine, tenofovir alafenamide and bictegravir",
  J05AR21: "dolutegravir and rilpivirine",
  J05AR22: "emtricitabine, tenofovir alafenamide, darunavir and cobicistat",
  J05AR23: "atazanavir and ritonavir",
  J05AR24: "lamivudine, tenofovir disoproxil and doravirine",
  J05AR25: "lamivudine and dolutegravir",
  J05AR26: "darunavir and ritonavir",
  J05AR27: "lamivudine, tenofovir disoproxil and dolutegravir",
  J05AR28: "stavudine and lamivudine",
  J05AR29: "emtricitabine, tenofovir alafenamide and dolutegravir",
  J05AX01: "moroxydine",
  J05AX02: "lysozyme",
  J05AX05: "inosine pranobex",
  J05AX06: "pleconaril",
  J05AX07: "enfuvirtide",
  J05AX09: "maraviroc",
  J05AX10: "maribavir",
  J05AX13: "umifenovir",
  J05AX17: "enisamium iodide",
  J05AX18: "letermovir",
  J05AX19: "tilorone",
  J05AX21: "pentanedioic acid imidazolyl ethanamide",
  J05AX23: "ibalizumab",
  J05AX24: "tecovirimat",
  J05AX25: "baloxavir marboxil",
  J05AX26: "amenamevir",
  J05AX27: "favipiravir",
  J05AX28: "bulevirtide",
  J05AX29: "fostemsavir",
  J05AX31: "lenacapavir",
  J05AX32: "riamilovir",
  J05AX33: "labuvirtide",
  J06AA01: "diphtheria antitoxin",
  J06AA02: "tetanus antitoxin",
  J06AA03: "snake venom antiserum",
  J06AA04: "botulinum antitoxin",
  J06AA05: "gas-gangrene sera",
  J06AA06: "rabies serum",
  J06BA01: "immunoglobulins, normal human, for extravascular adm.",
  J06BA02: "immunoglobulins, normal human, for intravascular adm.",
  J06BB01: "anti-D (rh) immunoglobulin",
  J06BB02: "tetanus immunoglobulin",
  J06BB03: "varicella/zoster immunoglobulin",
  J06BB04: "hepatitis B immunoglobulin",
  J06BB05: "rabies immunoglobulin",
  J06BB06: "rubella immunoglobulin",
  J06BB07: "vaccinia immunoglobulin",
  J06BB08: "staphylococcus immunoglobulin",
  J06BB09: "cytomegalovirus immunoglobulin",
  J06BB10: "diphtheria immunoglobulin",
  J06BB11: "hepatitis A immunoglobulin",
  J06BB12: "encephalitis, tick borne immunoglobulin",
  J06BB13: "pertussis immunoglobulin",
  J06BB14: "measles immunoglobulin",
  J06BB15: "mumps immunoglobulin",
  J06BB19: "anthrax immunoglobulin",
  J06BB30: "combinations",
  J06BC01: "nebacumab",
  J06BC02: "raxibacumab",
  J06BC03: "bezlotoxumab",
  J06BC04: "obiltoxaximab",
  J06BD01: "palivizumab",
  J06BD02: "motavizumab",
  J06BD03: "tixagevimab and cilgavimab",
  J06BD04: "ansuvimab",
  J06BD05: "sotrovimab",
  J06BD06: "regdanvimab",
  J06BD07: "casirivimab and imdevimab",
  J06BD08: "nirsevimab",
  J06BD09: "sipavibart",
  J06BD10: "clesrovimab",
  J06BD11: "libevitug",
  J07AC01: "anthrax antigen",
  J07AD01: "brucella antigen",
  J07AE01: "cholera, inactivated, whole cell",
  J07AE02: "cholera, live attenuated",
  J07AE51: "cholera, combinations with typhoid vaccine, inactivated, whole cell",
  J07AF01: "diphtheria toxoid",
  J07AG01: "haemophilus influenzae B, purified antigen conjugated",
  J07AG51: "haemophilus influenzae B, combinations with toxoids",
  J07AG52: "haemophilus influenzae B, combinations with pertussis and toxoids",
  J07AG53: "haemophilus influenzae B, combinations with meningococcus C, conjugated",
  J07AG54: "haemophilus influenza B, combinations with meningococcus C,Y, conjugated",
  J07AH01: "meningococcus A, purified polysaccharides antigen",
  J07AH02: "other meningococcal monovalent purified polysaccharides antigen",
  J07AH03: "meningococcus A,C, bivalent purified polysaccharides antigen",
  J07AH04: "meningococcus A,C,Y,W-135, tetravalent purified polysaccharides antigen",
  J07AH05: "other meningococcal polyvalent purified polysaccharides antigen",
  J07AH06: "meningococcus B, outer membrane vesicle vaccine",
  J07AH07: "meningococcus C, purified polysaccharides antigen conjugated",
  J07AH08: "meningococcus A,C,Y,W-135, tetravalent purified polysaccharides antigen conjugated",
  J07AH09: "meningococcus B, multicomponent vaccine",
  J07AH10: "meningococcus A, purified polysaccharides antigen conjugated",
  J07AH11: "meningococcus A,B,C,Y,W-135, pentavalent purified polysaccharides antigen conjugated and factor H binding protein",
  J07AJ01: "pertussis, inactivated, whole cell",
  J07AJ02: "pertussis, purified antigen",
  J07AJ51: "pertussis, inactivated, whole cell, combinations with toxoids",
  J07AJ52: "pertussis, purified antigen, combinations with toxoids",
  J07AK01: "plague, inactivated, whole cell",
  J07AL01: "pneumococcus, purified polysaccharides antigen",
  J07AL02: "pneumococcus, purified polysaccharides antigen conjugated",
  J07AL52: "pneumococcus purified polysaccharides antigen and haemophilus influenzae, conjugated",
  J07AM01: "tetanus toxoid",
  J07AM51: "tetanus toxoid, combinations with diphtheria toxoid",
  J07AM52: "tetanus toxoid, combinations with tetanus immunoglobulin",
  J07AN01: "tuberculosis, live attenuated",
  J07AP01: "typhoid, oral, live attenuated",
  J07AP02: "typhoid, inactivated, whole cell",
  J07AP03: "typhoid, purified polysaccharide antigen",
  J07AP10: "typhoid, combinations with paratyphi types",
  J07AR01: "typhus exanthematicus, inactivated, whole cell",
  J07AX01: "leptospira vaccines",
  J07BA01: "encephalitis, tick borne, inactivated, whole virus",
  J07BA02: "encephalitis, Japanese, inactivated, whole virus",
  J07BA03: "encephalitis, Japanese, live attenuated",
  J07BB01: "influenza, inactivated, whole virus",
  J07BB02: "influenza, inactivated, split virus or surface antigen",
  J07BB03: "influenza, live attenuated",
  J07BB04: "influenza, virus like particles",
  J07BB05: "influenza, RNA-based vaccine",
  J07BB55: "influenza and covid-19, RNA-based vaccine",
  J07BC01: "hepatitis B, purified antigen",
  J07BC02: "hepatitis A, inactivated, whole virus",
  J07BC20: "combinations",
  J07BD01: "measles, live attenuated",
  J07BD51: "measles, combinations with mumps, live attenuated",
  J07BD52: "measles, combinations with mumps and rubella, live attenuated",
  J07BD53: "measles, combinations with rubella, live attenuated",
  J07BD54: "measles, combinations with mumps, rubella and varicella, live attenuated",
  J07BE01: "mumps, live attenuated",
  J07BF01: "poliomyelitis oral, monovalent, live attenuated",
  J07BF02: "poliomyelitis oral, trivalent, live attenuated",
  J07BF03: "poliomyelitis, trivalent, inactivated, whole virus",
  J07BF04: "poliomyelitis oral, bivalent, live attenuated",
  J07BG01: "rabies, inactivated, whole virus",
  J07BH01: "rota virus, live attenuated",
  J07BH02: "rota virus, pentavalent, live, reassorted",
  J07BJ01: "rubella, live attenuated",
  J07BJ51: "rubella, combinations with mumps, live attenuated",
  J07BK01: "varicella, live attenuated",
  J07BK02: "zoster, live attenuated",
  J07BK03: "zoster, purified antigen",
  J07BL01: "yellow fever, live attenuated",
  J07BM01: "papillomavirus (human types 6, 11, 16, 18)",
  J07BM02: "papillomavirus (human types 16, 18)",
  J07BM03: "papillomavirus (human types 6, 11, 16, 18, 31, 33, 45, 52, 58)",
  J07BN01: "covid-19, RNA-based vaccine",
  J07BN02: "covid-19, viral vector, non-replicating",
  J07BN03: "covid-19, inactivated virus",
  J07BN04: "covid-19, protein subunit",
  J07BN05: "covid-19, virus-like particles",
  J07BP01: "chikungunya, live attenuated",
  J07BP02: "chikungunya, virus-like particles",
  J07BX01: "smallpox and mpox vaccines",
  J07BX02: "ebola vaccines",
  J07BX04: "dengue virus vaccines",
  J07BX05: "respiratory syncytial virus vaccines",
  J07BX06: "enterovirus 71 vaccines",
  J07CA01: "diphtheria-poliomyelitis-tetanus",
  J07CA02: "diphtheria-pertussis-poliomyelitis-tetanus",
  J07CA03: "diphtheria-rubella-tetanus",
  J07CA04: "haemophilus influenzae B and poliomyelitis",
  J07CA05: "diphtheria-hepatitis B-pertussis-tetanus",
  J07CA06: "diphtheria-haemophilus influenzae B-pertussis-poliomyelitis-tetanus",
  J07CA07: "diphtheria-hepatitis B-tetanus",
  J07CA08: "haemophilus influenzae B and hepatitis B",
  J07CA09: "diphtheria-haemophilus influenzae B-pertussis-poliomyelitis-tetanus-hepatitis B",
  J07CA10: "typhoid-hepatitis A",
  J07CA11: "diphtheria-haemophilus influenzae B-pertussis-tetanus-hepatitis B",
  J07CA12: "diphtheria-pertussis-poliomyelitis-tetanus-hepatitis B",
  J07CA13: "diphtheria-haemophilus influenzae B-pertussis-tetanus-hepatitis B-meningococcus A + C",
  J07XA01: "malaria vaccines",
  // L — 항종양제 및 면역조절제
  L01AA01: "cyclophosphamide",
  L01AA02: "chlorambucil",
  L01AA03: "melphalan",
  L01AA05: "chlormethine",
  L01AA06: "ifosfamide",
  L01AA07: "trofosfamide",
  L01AA08: "prednimustine",
  L01AA09: "bendamustine",
  L01AA10: "melphalan flufenamide",
  L01AB01: "busulfan",
  L01AB02: "treosulfan",
  L01AB03: "mannosulfan",
  L01AC01: "thiotepa",
  L01AC02: "triaziquone",
  L01AC03: "carboquone",
  L01AD01: "carmustine",
  L01AD02: "lomustine",
  L01AD03: "semustine",
  L01AD04: "streptozocin",
  L01AD05: "fotemustine",
  L01AD06: "nimustine",
  L01AD07: "ranimustine",
  L01AD08: "uramustine",
  L01AG01: "etoglucid",
  L01AX01: "mitobronitol",
  L01AX02: "pipobroman",
  L01AX03: "temozolomide",
  L01AX04: "dacarbazine",
  L01BA01: "methotrexate",
  L01BA03: "raltitrexed",
  L01BA04: "pemetrexed",
  L01BA05: "pralatrexate",
  L01BB02: "mercaptopurine",
  L01BB03: "tioguanine",
  L01BB04: "cladribine",
  L01BB05: "fludarabine",
  L01BB06: "clofarabine",
  L01BB07: "nelarabine",
  L01BC01: "cytarabine",
  L01BC02: "fluorouracil",
  L01BC03: "tegafur",
  L01BC04: "carmofur",
  L01BC05: "gemcitabine",
  L01BC06: "capecitabine",
  L01BC07: "azacitidine",
  L01BC08: "decitabine",
  L01BC09: "floxuridine",
  L01BC52: "fluorouracil, combinations",
  L01BC53: "tegafur, combinations",
  L01BC58: "decitabine, combinations",
  L01BC59: "trifluridine, combinations",
  L01CA01: "vinblastine",
  L01CA02: "vincristine",
  L01CA03: "vindesine",
  L01CA04: "vinorelbine",
  L01CA05: "vinflunine",
  L01CA06: "vintafolide",
  L01CB01: "etoposide",
  L01CB02: "teniposide",
  L01CC01: "demecolcine",
  L01CD01: "paclitaxel",
  L01CD02: "docetaxel",
  L01CD03: "paclitaxel poliglumex",
  L01CD04: "cabazitaxel",
  L01CD51: "paclitaxel and encequidar",
  L01CE01: "topotecan",
  L01CE02: "irinotecan",
  L01CE03: "etirinotecan pegol",
  L01CE04: "belotecan",
  L01CX01: "trabectedin",
  L01DA01: "dactinomycin",
  L01DB01: "doxorubicin",
  L01DB02: "daunorubicin",
  L01DB03: "epirubicin",
  L01DB04: "aclarubicin",
  L01DB05: "zorubicin",
  L01DB06: "idarubicin",
  L01DB07: "mitoxantrone",
  L01DB08: "pirarubicin",
  L01DB09: "valrubicin",
  L01DB10: "amrubicin",
  L01DB11: "pixantrone",
  L01DC01: "bleomycin",
  L01DC02: "plicamycin",
  L01DC03: "mitomycin",
  L01DC04: "ixabepilone",
  L01DC05: "utidelone",
  L01EA01: "imatinib",
  L01EA02: "dasatinib",
  L01EA03: "nilotinib",
  L01EA04: "bosutinib",
  L01EA05: "ponatinib",
  L01EA06: "asciminib",
  L01EB01: "gefitinib",
  L01EB02: "erlotinib",
  L01EB03: "afatinib",
  L01EB04: "osimertinib",
  L01EB05: "rociletinib",
  L01EB06: "olmutinib",
  L01EB07: "dacomitinib",
  L01EB08: "icotinib",
  L01EB09: "lazertinib",
  L01EB10: "mobocertinib",
  L01EB11: "aumolertinib",
  L01EB12: "firmonertinib",
  L01EC01: "vemurafenib",
  L01EC02: "dabrafenib",
  L01EC03: "encorafenib",
  L01EC04: "tovorafenib",
  L01ED01: "crizotinib",
  L01ED02: "ceritinib",
  L01ED03: "alectinib",
  L01ED04: "brigatinib",
  L01ED05: "lorlatinib",
  L01EE01: "trametinib",
  L01EE02: "cobimetinib",
  L01EE03: "binimetinib",
  L01EE04: "selumetinib",
  L01EE05: "mirdametinib",
  L01EE06: "tunlametinib",
  L01EF01: "palbociclib",
  L01EF02: "ribociclib",
  L01EF03: "abemaciclib",
  L01EG01: "temsirolimus",
  L01EG02: "everolimus",
  L01EG03: "ridaforolimus",
  L01EG04: "sirolimus",
  L01EH01: "lapatinib",
  L01EH02: "neratinib",
  L01EH03: "tucatinib",
  L01EH04: "zongertinib",
  L01EH05: "mifanertinib",
  L01EH06: "sevabertinib",
  L01EJ01: "ruxolitinib",
  L01EJ02: "fedratinib",
  L01EJ03: "pacritinib",
  L01EJ04: "momelotinib",
  L01EK01: "axitinib",
  L01EK02: "cediranib",
  L01EK03: "tivozanib",
  L01EK04: "fruquintinib",
  L01EK05: "rivoceranib",
  L01EL01: "ibrutinib",
  L01EL02: "acalabrutinib",
  L01EL03: "zanubrutinib",
  L01EL04: "orelabrutinib",
  L01EL05: "pirtobrutinib",
  L01EL06: "tirabrutinib",
  L01EM01: "idelalisib",
  L01EM02: "copanlisib",
  L01EM03: "alpelisib",
  L01EM04: "duvelisib",
  L01EM05: "parsaclisib",
  L01EM06: "inavolisib",
  L01EN01: "erdafitinib",
  L01EN02: "pemigatinib",
  L01EN03: "infigratinib",
  L01EN04: "futibatinib",
  L01EP01: "capmatinib",
  L01EP02: "tepotinib",
  L01EP03: "savolitinib",
  L01EX01: "sunitinib",
  L01EX02: "sorafenib",
  L01EX03: "pazopanib",
  L01EX04: "vandetanib",
  L01EX05: "regorafenib",
  L01EX06: "masitinib",
  L01EX07: "cabozantinib",
  L01EX08: "lenvatinib",
  L01EX09: "nintedanib",
  L01EX10: "midostaurin",
  L01EX11: "quizartinib",
  L01EX12: "larotrectinib",
  L01EX13: "gilteritinib",
  L01EX14: "entrectinib",
  L01EX15: "pexidartinib",
  L01EX18: "avapritinib",
  L01EX19: "ripretinib",
  L01EX22: "selpercatinib",
  L01EX23: "pralsetinib",
  L01EX24: "surufatinib",
  L01EX25: "umbralisib",
  L01EX26: "sitravatinib",
  L01EX27: "capivasertib",
  L01EX28: "repotrectinib",
  L01EX29: "vimseltinib",
  L01FA01: "rituximab",
  L01FA02: "ofatumumab",
  L01FA03: "obinutuzumab",
  L01FB01: "inotuzumab ozogamicin",
  L01FB02: "moxetumomab pasudotox",
  L01FC01: "daratumumab",
  L01FC02: "isatuximab",
  L01FD01: "trastuzumab",
  L01FD02: "pertuzumab",
  L01FD03: "trastuzumab emtansine",
  L01FD04: "trastuzumab deruxtecan",
  L01FD05: "trastuzumab duocarmazine",
  L01FD06: "margetuximab",
  L01FD07: "zanidatamab",
  L01FE01: "cetuximab",
  L01FE02: "panitumumab",
  L01FE03: "necitumumab",
  L01FF01: "nivolumab",
  L01FF02: "pembrolizumab",
  L01FF03: "durvalumab",
  L01FF04: "avelumab",
  L01FF05: "atezolizumab",
  L01FF06: "cemiplimab",
  L01FF07: "dostarlimab",
  L01FF08: "prolgolimab",
  L01FF09: "tislelizumab",
  L01FF10: "retifanlimab",
  L01FF11: "sugemalimab",
  L01FF12: "serplulimab",
  L01FF13: "toripalimab",
  L01FF14: "camrelizumab",
  L01FF15: "envafolimab",
  L01FF16: "sasanlimab",
  L01FG01: "bevacizumab",
  L01FG02: "ramucirumab",
  L01FX01: "edrecolomab",
  L01FX02: "gemtuzumab ozogamicin",
  L01FX03: "catumaxomab",
  L01FX04: "ipilimumab",
  L01FX05: "brentuximab vedotin",
  L01FX06: "dinutuximab beta",
  L01FX07: "blinatumomab",
  L01FX08: "elotuzumab",
  L01FX09: "mogamulizumab",
  L01FX10: "olaratumab",
  L01FX11: "bermekimab",
  L01FX12: "tafasitamab",
  L01FX13: "enfortumab vedotin",
  L01FX14: "polatuzumab vedotin",
  L01FX15: "belantamab mafodotin",
  L01FX16: "oportuzumab monatox",
  L01FX17: "sacituzumab govitecan",
  L01FX18: "amivantamab",
  L01FX19: "sabatolimab",
  L01FX20: "tremelimumab",
  L01FX21: "naxitamab",
  L01FX22: "loncastuximab tesirine",
  L01FX23: "tisotumab vedotin",
  L01FX24: "teclistamab",
  L01FX25: "mosunetuzumab",
  L01FX26: "mirvetuximab soravtansine",
  L01FX27: "epcoritamab",
  L01FX28: "glofitamab",
  L01FX29: "talquetamab",
  L01FX31: "zolbetuximab",
  L01FX32: "elranatamab",
  L01FX33: "tarlatamab",
  L01FX34: "odronextamab",
  L01FX35: "datopotamab deruxtecan",
  L01FX36: "patritumab deruxtecan",
  L01FX37: "linvoseltamab",
  L01FX38: "ifinatamab deruxtecan",
  L01FX39: "bemarituzumab",
  L01FY01: "pertuzumab and trastuzumab",
  L01FY02: "nivolumab and relatlimab",
  L01FY03: "prolgolimab and nurulimab",
  L01XA01: "cisplatin",
  L01XA02: "carboplatin",
  L01XA03: "oxaliplatin",
  L01XA04: "satraplatin",
  L01XA05: "polyplatillen",
  L01XB01: "procarbazine",
  L01XD01: "porfimer sodium",
  L01XD03: "methyl aminolevulinate",
  L01XD04: "aminolevulinic acid",
  L01XD05: "temoporfin",
  L01XD06: "efaproxiral",
  L01XD07: "padeliporfin",
  L01XF01: "tretinoin",
  L01XF02: "alitretinoin",
  L01XF03: "bexarotene",
  L01XG01: "bortezomib",
  L01XG02: "carfilzomib",
  L01XG03: "ixazomib",
  L01XH01: "vorinostat",
  L01XH02: "romidepsin",
  L01XH03: "panobinostat",
  L01XH04: "belinostat",
  L01XH05: "entinostat",
  L01XH06: "tucidinostat",
  L01XH07: "resminostat",
  L01XJ01: "vismodegib",
  L01XJ02: "sonidegib",
  L01XJ03: "glasdegib",
  L01XK01: "olaparib",
  L01XK02: "niraparib",
  L01XK03: "rucaparib",
  L01XK04: "talazoparib",
  L01XK05: "veliparib",
  L01XK06: "pamiparib",
  L01XK07: "senaparib",
  L01XK52: "niraparib and abiraterone",
  L01XL01: "sitimagene ceradenovec",
  L01XL02: "talimogene laherparepvec",
  L01XL03: "axicabtagene ciloleucel",
  L01XL04: "tisagenlecleucel",
  L01XL05: "ciltacabtagene autoleucel",
  L01XL06: "brexucabtagene autoleucel",
  L01XL07: "idecabtagene vicleucel",
  L01XL08: "lisocabtagene maraleucel",
  L01XL09: "tabelecleucel",
  L01XL10: "nadofaragene firadenovec",
  L01XL11: "lifileucel",
  L01XL12: "obecabtagene autoleucel",
  L01XM01: "enasidenib",
  L01XM02: "ivosidenib",
  L01XM03: "olutasidenib",
  L01XM04: "vorasidenib",
  L01XX01: "amsacrine",
  L01XX02: "asparaginase",
  L01XX03: "altretamine",
  L01XX05: "hydroxycarbamide",
  L01XX07: "lonidamine",
  L01XX08: "pentostatin",
  L01XX10: "masoprocol",
  L01XX11: "estramustine",
  L01XX16: "mitoguazone",
  L01XX18: "tiazofurine",
  L01XX23: "mitotane",
  L01XX24: "pegaspargase",
  L01XX27: "arsenic trioxide",
  L01XX29: "denileukin diftitox",
  L01XX33: "celecoxib",
  L01XX35: "anagrelide",
  L01XX36: "oblimersen",
  L01XX40: "omacetaxine mepesuccinate",
  L01XX41: "eribulin",
  L01XX44: "aflibercept",
  L01XX52: "venetoclax",
  L01XX53: "vosaroxin",
  L01XX57: "plitidepsin",
  L01XX58: "epacadostat",
  L01XX66: "selinexor",
  L01XX67: "tagraxofusp",
  L01XX69: "lurbinectedin",
  L01XX72: "tazemetostat",
  L01XX73: "sotorasib",
  L01XX74: "belzutifan",
  L01XX75: "tebentafusp",
  L01XX77: "adagrasib",
  L01XX78: "navitoclax",
  L01XX79: "eflornithine",
  L01XX80: "imetelstat",
  L01XX81: "nirogacestat",
  L01XX82: "darinaparsin",
  L01XX83: "para-toluenesulfonamide",
  L01XX84: "pelabresib",
  L01XX85: "idroxioleic acid",
  L01XX86: "calaspargase pegol",
  L01XX87: "revumenib",
  L01XY01: "cytarabine and daunorubicin",
  L01XY04: "bifikafusp alfa and onfekafusp alfa",
  L02AA01: "diethylstilbestrol",
  L02AA02: "polyestradiol phosphate",
  L02AA03: "ethinylestradiol",
  L02AA04: "fosfestrol",
  L02AB01: "megestrol",
  L02AB02: "medroxyprogesterone",
  L02AB03: "gestonorone",
  L02AE01: "buserelin",
  L02AE02: "leuprorelin",
  L02AE03: "goserelin",
  L02AE04: "triptorelin",
  L02AE05: "histrelin",
  L02AE51: "leuprorelin and bicalutamide",
  L02BA01: "tamoxifen",
  L02BA02: "toremifene",
  L02BA03: "fulvestrant",
  L02BA04: "elacestrant",
  L02BA05: "camizestrant",
  L02BB01: "flutamide",
  L02BB02: "nilutamide",
  L02BB03: "bicalutamide",
  L02BB04: "enzalutamide",
  L02BB05: "apalutamide",
  L02BB06: "darolutamide",
  L02BG01: "aminoglutethimide",
  L02BG02: "formestane",
  L02BG03: "anastrozole",
  L02BG04: "letrozole",
  L02BG05: "vorozole",
  L02BG06: "exemestane",
  L02BX01: "abarelix",
  L02BX02: "degarelix",
  L02BX03: "abiraterone",
  L02BX04: "relugolix",
  L02BX53: "abiraterone and corticosteroids",
  L03AA02: "filgrastim",
  L03AA03: "molgramostim",
  L03AA09: "sargramostim",
  L03AA10: "lenograstim",
  L03AA12: "ancestim",
  L03AA13: "pegfilgrastim",
  L03AA14: "lipegfilgrastim",
  L03AA15: "balugrastim",
  L03AA16: "empegfilgrastim",
  L03AA17: "pegteograstim",
  L03AA18: "efbemalenograstim alfa",
  L03AA19: "eflapegrastim",
  L03AB01: "interferon alfa natural",
  L03AB02: "interferon beta natural",
  L03AB03: "interferon gamma",
  L03AB04: "interferon alfa-2a",
  L03AB05: "interferon alfa-2b",
  L03AB06: "interferon alfa-n1",
  L03AB07: "interferon beta-1a",
  L03AB08: "interferon beta-1b",
  L03AB09: "interferon alfacon-1",
  L03AB10: "peginterferon alfa-2b",
  L03AB11: "peginterferon alfa-2a",
  L03AB12: "albinterferon alfa-2b",
  L03AB13: "peginterferon beta-1a",
  L03AB14: "cepeginterferon alfa-2b",
  L03AB15: "ropeginterferon alfa-2b",
  L03AB16: "peginterferon alfacon-2",
  L03AB17: "sampeginterferon beta-1a",
  L03AB60: "peginterferon alfa-2b, combinations",
  L03AB61: "peginterferon alfa-2a, combinations",
  L03AC01: "aldesleukin",
  L03AC02: "oprelvekin",
  L03AC03: "nogapendekin alfa and inbakicept",
  L03AX01: "lentinan",
  L03AX02: "roquinimex",
  L03AX03: "BCG vaccine",
  L03AX04: "pegademase",
  L03AX05: "pidotimod",
  L03AX07: "poly I:C",
  L03AX08: "poly ICLC",
  L03AX09: "thymopentin",
  L03AX10: "immunocyanin",
  L03AX11: "tasonermin",
  L03AX12: "melanoma vaccine",
  L03AX13: "glatiramer acetate",
  L03AX14: "histamine dihydrochloride",
  L03AX15: "mifamurtide",
  L03AX16: "plerixafor",
  L03AX17: "sipuleucel-T",
  L03AX18: "cridanimod",
  L03AX19: "dasiprotimut-T",
  L03AX21: "elapegademase",
  L03AX22: "leniolisib",
  L03AX23: "motixafortide",
  L03AX24: "mavorixafor",
  L03AX25: "thymalfasin",
  L04AA03: "antilymphocyte immunoglobulin (horse)",
  L04AA04: "antithymocyte immunoglobulin (rabbit)",
  L04AA06: "mycophenolic acid",
  L04AA15: "alefacept",
  L04AA19: "gusperimus",
  L04AA22: "abetimus",
  L04AA24: "abatacept",
  L04AA28: "belatacept",
  L04AA32: "apremilast",
  L04AA40: "cladribine",
  L04AA41: "imlifidase",
  L04AA48: "belumosudil",
  L04AA60: "remibrutinib",
  L04AA61: "nerandomilast",
  L04AA62: "tolebrutinib",
  L04AB01: "etanercept",
  L04AB02: "infliximab",
  L04AB03: "afelimomab",
  L04AB04: "adalimumab",
  L04AB05: "certolizumab pegol",
  L04AB06: "golimumab",
  L04AB07: "opinercept",
  L04AC01: "daclizumab",
  L04AC02: "basiliximab",
  L04AC03: "anakinra",
  L04AC04: "rilonacept",
  L04AC05: "ustekinumab",
  L04AC07: "tocilizumab",
  L04AC08: "canakinumab",
  L04AC09: "briakinumab",
  L04AC10: "secukinumab",
  L04AC11: "siltuximab",
  L04AC12: "brodalumab",
  L04AC13: "ixekizumab",
  L04AC14: "sarilumab",
  L04AC15: "sirukumab",
  L04AC16: "guselkumab",
  L04AC17: "tildrakizumab",
  L04AC18: "risankizumab",
  L04AC19: "satralizumab",
  L04AC20: "netakimab",
  L04AC21: "bimekizumab",
  L04AC22: "spesolimab",
  L04AC23: "olokizumab",
  L04AC24: "mirikizumab",
  L04AC25: "levilimab",
  L04AC26: "goflikicept",
  L04AC27: "cendakimab",
  L04AD01: "ciclosporin",
  L04AD02: "tacrolimus",
  L04AD03: "voclosporin",
  L04AE01: "fingolimod",
  L04AE02: "ozanimod",
  L04AE03: "siponimod",
  L04AE04: "ponesimod",
  L04AE05: "etrasimod",
  L04AF01: "tofacitinib",
  L04AF02: "baricitinib",
  L04AF03: "upadacitinib",
  L04AF04: "filgotinib",
  L04AF05: "itacitinib",
  L04AF06: "peficitinib",
  L04AF07: "deucravacitinib",
  L04AF08: "ritlecitinib",
  L04AF09: "deuruxolitinib",
  L04AG01: "muromonab-CD3",
  L04AG02: "efalizumab",
  L04AG03: "natalizumab",
  L04AG04: "belimumab",
  L04AG05: "vedolizumab",
  L04AG06: "alemtuzumab",
  L04AG07: "begelomab",
  L04AG08: "ocrelizumab",
  L04AG09: "emapalumab",
  L04AG10: "inebilizumab",
  L04AG11: "anifrolumab",
  L04AG12: "ofatumumab",
  L04AG13: "teprotumumab",
  L04AG14: "ublituximab",
  L04AG15: "divozilimab",
  L04AG17: "seniprutug",
  L04AG18: "sibeprenlimab",
  L04AG19: "axatilimab",
  L04AH01: "sirolimus",
  L04AH02: "everolimus",
  L04AJ01: "eculizumab",
  L04AJ02: "ravulizumab",
  L04AJ03: "pegcetacoplan",
  L04AJ04: "sutimlimab",
  L04AJ05: "avacopan",
  L04AJ06: "zilucoplan",
  L04AJ07: "crovalimab",
  L04AJ08: "iptacopan",
  L04AJ09: "danicopan",
  L04AJ10: "vilobelimab",
  L04AJ11: "pozelimab",
  L04AK01: "leflunomide",
  L04AK02: "teriflunomide",
  L04AL01: "efgartigimod alfa",
  L04AL02: "rozanolixizumab",
  L04AL03: "nipocalimab",
  L04AX01: "azathioprine",
  L04AX02: "thalidomide",
  L04AX03: "methotrexate",
  L04AX04: "lenalidomide",
  L04AX05: "pirfenidone",
  L04AX06: "pomalidomide",
  L04AX07: "dimethyl fumarate",
  L04AX08: "darvadstrocel",
  L04AX09: "diroximel fumarate",
  L04AX10: "tegomil fumarate",
  L04AX11: "monomethyl fumarate",
  // M — 근골격계
  M01AA01: "phenylbutazone",
  M01AA02: "mofebutazone",
  M01AA03: "oxyphenbutazone",
  M01AA05: "clofezone",
  M01AA06: "kebuzone",
  M01AB01: "indometacin",
  M01AB02: "sulindac",
  M01AB03: "tolmetin",
  M01AB04: "zomepirac",
  M01AB05: "diclofenac",
  M01AB06: "alclofenac",
  M01AB07: "bumadizone",
  M01AB08: "etodolac",
  M01AB09: "lonazolac",
  M01AB10: "fentiazac",
  M01AB11: "acemetacin",
  M01AB12: "difenpiramide",
  M01AB13: "oxametacin",
  M01AB14: "proglumetacin",
  M01AB15: "ketorolac",
  M01AB16: "aceclofenac",
  M01AB17: "bufexamac",
  M01AB51: "indometacin, combinations",
  M01AB55: "diclofenac, combinations",
  M01AC01: "piroxicam",
  M01AC02: "tenoxicam",
  M01AC04: "droxicam",
  M01AC05: "lornoxicam",
  M01AC06: "meloxicam",
  M01AC56: "meloxicam, combinations",
  M01AE01: "ibuprofen",
  M01AE02: "naproxen",
  M01AE03: "ketoprofen",
  M01AE04: "fenoprofen",
  M01AE05: "fenbufen",
  M01AE06: "benoxaprofen",
  M01AE07: "suprofen",
  M01AE08: "pirprofen",
  M01AE09: "flurbiprofen",
  M01AE10: "indoprofen",
  M01AE11: "tiaprofenic acid",
  M01AE12: "oxaprozin",
  M01AE13: "ibuproxam",
  M01AE14: "dexibuprofen",
  M01AE15: "flunoxaprofen",
  M01AE16: "alminoprofen",
  M01AE17: "dexketoprofen",
  M01AE18: "naproxcinod",
  M01AE19: "loxoprofen",
  M01AE20: "pelubiprofen",
  M01AE51: "ibuprofen, combinations",
  M01AE52: "naproxen and esomeprazole",
  M01AE53: "ketoprofen, combinations",
  M01AE56: "naproxen and misoprostol",
  M01AE57: "naproxen and diphenhydramine",
  M01AG01: "mefenamic acid",
  M01AG02: "tolfenamic acid",
  M01AG03: "flufenamic acid",
  M01AG04: "meclofenamic acid",
  M01AH01: "celecoxib",
  M01AH02: "rofecoxib",
  M01AH03: "valdecoxib",
  M01AH04: "parecoxib",
  M01AH05: "etoricoxib",
  M01AH06: "lumiracoxib",
  M01AH07: "polmacoxib",
  M01AX01: "nabumetone",
  M01AX02: "niflumic acid",
  M01AX04: "azapropazone",
  M01AX05: "glucosamine",
  M01AX07: "benzydamine",
  M01AX12: "glucosaminoglycan polysulfate",
  M01AX13: "proquazone",
  M01AX14: "orgotein",
  M01AX17: "nimesulide",
  M01AX18: "feprazone",
  M01AX21: "diacerein",
  M01AX22: "morniflumate",
  M01AX23: "tenidap",
  M01AX24: "oxaceprol",
  M01AX25: "chondroitin sulfate",
  M01AX26: "avocado and soyabean oil, unsaponifiables",
  M01AX68: "feprazone, combinations",
  M01BA01: "phenylbutazone and corticosteroids",
  M01BA02: "dipyrocetyl and corticosteroids",
  M01BA03: "acetylsalicylic acid and corticosteroids",
  M01CA03: "oxycinchophen",
  M01CB01: "sodium aurothiomalate",
  M01CB02: "sodium aurotiosulfate",
  M01CB03: "auranofin",
  M01CB04: "aurothioglucose",
  M01CB05: "aurotioprol",
  M01CC01: "penicillamine",
  M01CC02: "bucillamine",
  M02AA01: "phenylbutazone",
  M02AA02: "mofebutazone",
  M02AA03: "clofezone",
  M02AA04: "oxyphenbutazone",
  M02AA05: "benzydamine",
  M02AA06: "etofenamate",
  M02AA07: "piroxicam",
  M02AA08: "felbinac",
  M02AA09: "bufexamac",
  M02AA10: "ketoprofen",
  M02AA11: "bendazac",
  M02AA12: "naproxen",
  M02AA13: "ibuprofen",
  M02AA14: "fentiazac",
  M02AA15: "diclofenac",
  M02AA16: "feprazone",
  M02AA17: "niflumic acid",
  M02AA18: "meclofenamic acid",
  M02AA19: "flurbiprofen",
  M02AA21: "tolmetin",
  M02AA22: "suxibuzone",
  M02AA23: "indometacin",
  M02AA24: "nifenazone",
  M02AA25: "aceclofenac",
  M02AA26: "nimesulide",
  M02AA27: "dexketoprofen",
  M02AA28: "piketoprofen",
  M02AA29: "esflurbiprofen",
  M02AA31: "loxoprofen",
  M02AB01: "capsaicin",
  M02AB02: "zucapsaicin",
  M02AX02: "tolazoline",
  M02AX03: "dimethyl sulfoxide",
  M02AX05: "idrocilamide",
  M02AX06: "tolperisone",
  M02AX10: "various",
  M03AA01: "alcuronium",
  M03AA02: "tubocurarine",
  M03AA04: "dimethyltubocurarine",
  M03AB01: "suxamethonium",
  M03AC01: "pancuronium",
  M03AC02: "gallamine",
  M03AC03: "vecuronium",
  M03AC04: "atracurium",
  M03AC05: "hexafluronium",
  M03AC06: "pipecuronium bromide",
  M03AC07: "doxacurium chloride",
  M03AC08: "fazadinium bromide",
  M03AC09: "rocuronium bromide",
  M03AC10: "mivacurium chloride",
  M03AC11: "cisatracurium",
  M03AX01: "botulinum toxin",
  M03BA01: "phenprobamate",
  M03BA02: "carisoprodol",
  M03BA03: "methocarbamol",
  M03BA04: "styramate",
  M03BA05: "febarbamate",
  M03BA51: "phenprobamate, combinations excl. psycholeptics",
  M03BA52: "carisoprodol, combinations excl. psycholeptics",
  M03BA53: "methocarbamol, combinations excl. psycholeptics",
  M03BA71: "phenprobamate, combinations with psycholeptics",
  M03BA72: "carisoprodol, combinations with psycholeptics",
  M03BA73: "methocarbamol, combinations with psycholeptics",
  M03BB02: "chlormezanone",
  M03BB03: "chlorzoxazone",
  M03BB52: "chlormezanone, combinations excl. psycholeptics",
  M03BB53: "chlorzoxazone, combinations excl. psycholeptics",
  M03BB72: "chlormezanone, combinations with psycholeptics",
  M03BB73: "chlorzoxazone, combinations with psycholeptics",
  M03BC01: "orphenadrine (citrate)",
  M03BC51: "orphenadrine, combinations",
  M03BX01: "baclofen",
  M03BX02: "tizanidine",
  M03BX03: "pridinol",
  M03BX04: "tolperisone",
  M03BX05: "thiocolchicoside",
  M03BX06: "mephenesin",
  M03BX07: "tetrazepam",
  M03BX08: "cyclobenzaprine",
  M03BX09: "eperisone",
  M03BX30: "fenyramidol",
  M03BX53: "pridinol, combinations",
  M03BX55: "thiocolchicoside, combinations",
  M03CA01: "dantrolene",
  M04AA01: "allopurinol",
  M04AA02: "tisopurine",
  M04AA03: "febuxostat",
  M04AA51: "allopurinol, combinations",
  M04AB01: "probenecid",
  M04AB02: "sulfinpyrazone",
  M04AB03: "benzbromarone",
  M04AB04: "isobromindione",
  M04AB05: "lesinurad",
  M04AB06: "dotinurad",
  M04AC01: "colchicine",
  M04AC02: "cinchophen",
  M04AC51: "colchicine and probenecid",
  M04AX01: "urate oxidase",
  M04AX02: "pegloticase",
  M05BA01: "etidronic acid",
  M05BA02: "clodronic acid",
  M05BA03: "pamidronic acid",
  M05BA04: "alendronic acid",
  M05BA05: "tiludronic acid",
  M05BA06: "ibandronic acid",
  M05BA07: "risedronic acid",
  M05BA08: "zoledronic acid",
  M05BB01: "etidronic acid and calcium, sequential",
  M05BB02: "risedronic acid and calcium, sequential",
  M05BB03: "alendronic acid and colecalciferol",
  M05BB04: "risedronic acid, calcium and colecalciferol, sequential",
  M05BB05: "alendronic acid, calcium and colecalciferol, sequential",
  M05BB06: "alendronic acid and alfacalcidol, sequential",
  M05BB07: "risedronic acid and colecalciferol",
  M05BB08: "zoledronic acid, calcium and colecalciferol, sequential",
  M05BB09: "ibandronic acid and colecalciferol",
  M05BC01: "dibotermin alfa",
  M05BC02: "eptotermin alfa",
  M05BX01: "ipriflavone",
  M05BX02: "aluminium chlorohydrate",
  M05BX03: "strontium ranelate",
  M05BX04: "denosumab",
  M05BX05: "burosumab",
  M05BX06: "romosozumab",
  M05BX07: "vosoritide",
  M05BX08: "menatetrenone",
  M05BX53: "strontium ranelate and colecalciferol",
  M09AA01: "hydroquinine",
  M09AA72: "quinine, combinations with psycholeptics",
  M09AB01: "chymopapain",
  M09AB02: "collagenase clostridium histolyticum",
  M09AB03: "bromelains",
  M09AB52: "trypsin, combinations",
  M09AX01: "hyaluronic acid",
  M09AX02: "chondrocytes, autologous",
  M09AX03: "ataluren",
  M09AX04: "drisapersen",
  M09AX05: "aceneuramic acid",
  M09AX06: "eteplirsen",
  M09AX07: "nusinersen",
  M09AX08: "golodirsen",
  M09AX09: "onasemnogene abeparvovec",
  M09AX10: "risdiplam",
  M09AX11: "palovarotene",
  M09AX12: "viltolarsen",
  M09AX13: "casimersen",
  M09AX14: "givinostat",
  M09AX15: "delandistrogene moxeparvovec",
  M09AX16: "apitegromab",
  // N — 신경계
  N01AA01: "diethyl ether",
  N01AA02: "vinyl ether",
  N01AB01: "halothane",
  N01AB02: "chloroform",
  N01AB04: "enflurane",
  N01AB05: "trichloroethylene",
  N01AB06: "isoflurane",
  N01AB07: "desflurane",
  N01AB08: "sevoflurane",
  N01AF01: "methohexital",
  N01AF02: "hexobarbital",
  N01AF03: "thiopental",
  N01AG01: "narcobarbital",
  N01AH01: "fentanyl",
  N01AH02: "alfentanil",
  N01AH03: "sufentanil",
  N01AH04: "phenoperidine",
  N01AH05: "anileridine",
  N01AH06: "remifentanil",
  N01AH51: "fentanyl, combinations",
  N01AX03: "ketamine",
  N01AX04: "propanidid",
  N01AX05: "alfaxalone",
  N01AX07: "etomidate",
  N01AX10: "propofol",
  N01AX11: "sodium oxybate",
  N01AX13: "nitrous oxide",
  N01AX14: "esketamine",
  N01AX15: "xenon",
  N01AX63: "nitrous oxide, combinations",
  N01BA01: "metabutethamine",
  N01BA02: "procaine",
  N01BA03: "tetracaine",
  N01BA04: "chloroprocaine",
  N01BA05: "benzocaine",
  N01BA52: "procaine, combinations",
  N01BA53: "tetracaine, combinations",
  N01BB01: "bupivacaine",
  N01BB02: "lidocaine",
  N01BB03: "mepivacaine",
  N01BB04: "prilocaine",
  N01BB05: "butanilicaine",
  N01BB06: "cinchocaine",
  N01BB07: "etidocaine",
  N01BB08: "articaine",
  N01BB09: "ropivacaine",
  N01BB10: "levobupivacaine",
  N01BB20: "combinations",
  N01BB51: "bupivacaine, combinations",
  N01BB52: "lidocaine, combinations",
  N01BB53: "mepivacaine, combinations",
  N01BB54: "prilocaine, combinations",
  N01BB57: "etidocaine, combinations",
  N01BB58: "articaine, combinations",
  N01BB59: "bupivacaine and meloxicam",
  N01BC01: "cocaine",
  N01BX01: "ethyl chloride",
  N01BX02: "dyclonine",
  N01BX03: "phenol",
  N01BX04: "capsaicin",
  N02AA01: "morphine",
  N02AA02: "opium",
  N02AA03: "hydromorphone",
  N02AA04: "nicomorphine",
  N02AA05: "oxycodone",
  N02AA08: "dihydrocodeine",
  N02AA10: "papaveretum",
  N02AA11: "oxymorphone",
  N02AA51: "morphine, combinations",
  N02AA53: "hydromorphone and naloxone",
  N02AA55: "oxycodone and naloxone",
  N02AA56: "oxycodone and naltrexone",
  N02AA58: "dihydrocodeine, combinations",
  N02AA59: "codeine, combinations excl. psycholeptics",
  N02AA79: "codeine, combinations with psycholeptics",
  N02AB01: "ketobemidone",
  N02AB02: "pethidine",
  N02AB03: "fentanyl",
  N02AB52: "pethidine, combinations excl. psycholeptics",
  N02AB72: "pethidine, combinations with psycholeptics",
  N02AC01: "dextromoramide",
  N02AC03: "piritramide",
  N02AC04: "dextropropoxyphene",
  N02AC05: "bezitramide",
  N02AC52: "methadone, combinations excl. psycholeptics",
  N02AC54: "dextropropoxyphene, combinations excl. psycholeptics",
  N02AC74: "dextropropoxyphene, combinations with psycholeptics",
  N02AD01: "pentazocine",
  N02AD02: "phenazocine",
  N02AD51: "pentazocine and naloxone",
  N02AE01: "buprenorphine",
  N02AF01: "butorphanol",
  N02AF02: "nalbuphine",
  N02AG01: "morphine and antispasmodics",
  N02AG02: "ketobemidone and antispasmodics",
  N02AG03: "pethidine and antispasmodics",
  N02AG04: "hydromorphone and antispasmodics",
  N02AJ01: "dihydrocodeine and paracetamol",
  N02AJ02: "dihydrocodeine and acetylsalicylic acid",
  N02AJ03: "dihydrocodeine and other non-opioid analgesics",
  N02AJ06: "codeine and paracetamol",
  N02AJ07: "codeine and acetylsalicylic acid",
  N02AJ08: "codeine and ibuprofen",
  N02AJ09: "codeine and other non-opioid analgesics",
  N02AJ13: "tramadol and paracetamol",
  N02AJ14: "tramadol and dexketoprofen",
  N02AJ15: "tramadol and other non-opioid analgesics",
  N02AJ16: "tramadol and celecoxib",
  N02AJ17: "oxycodone and paracetamol",
  N02AJ18: "oxycodone and acetylsalicylic acid",
  N02AJ19: "oxycodone and ibuprofen",
  N02AJ22: "hydrocodone and paracetamol",
  N02AJ23: "hydrocodone and ibuprofen",
  N02AX01: "tilidine",
  N02AX02: "tramadol",
  N02AX03: "dezocine",
  N02AX05: "meptazinol",
  N02AX06: "tapentadol",
  N02AX07: "oliceridine",
  N02AX51: "tilidine and naloxone",
  N02BA01: "acetylsalicylic acid",
  N02BA02: "aloxiprin",
  N02BA03: "choline salicylate",
  N02BA04: "sodium salicylate",
  N02BA05: "salicylamide",
  N02BA06: "salsalate",
  N02BA07: "ethenzamide",
  N02BA08: "morpholine salicylate",
  N02BA09: "dipyrocetyl",
  N02BA10: "benorilate",
  N02BA11: "diflunisal",
  N02BA12: "potassium salicylate",
  N02BA14: "guacetisal",
  N02BA15: "carbasalate calcium",
  N02BA16: "imidazole salicylate",
  N02BA51: "acetylsalicylic acid, combinations excl. psycholeptics",
  N02BA55: "salicylamide, combinations excl. psycholeptics",
  N02BA57: "ethenzamide, combinations excl. psycholeptics",
  N02BA59: "dipyrocetyl, combinations excl. psycholeptics",
  N02BA65: "carbasalate calcium combinations excl. psycholeptics",
  N02BA67: "magnesium salicylate, combinations excl. psycholeptics",
  N02BA71: "acetylsalicylic acid, combinations with psycholeptics",
  N02BA75: "salicylamide, combinations with psycholeptics",
  N02BA77: "ethenzamide, combinations with psycholeptics",
  N02BA79: "dipyrocetyl, combinations with psycholeptics",
  N02BB01: "phenazone",
  N02BB02: "metamizole sodium",
  N02BB03: "aminophenazone",
  N02BB04: "propyphenazone",
  N02BB05: "nifenazone",
  N02BB51: "phenazone, combinations excl. psycholeptics",
  N02BB52: "metamizole sodium, combinations excl. psycholeptics",
  N02BB53: "aminophenazone, combinations excl. psycholeptics",
  N02BB54: "propyphenazone, combinations excl. psycholeptics",
  N02BB71: "phenazone, combinations with psycholeptics",
  N02BB72: "metamizole sodium, combinations with psycholeptics",
  N02BB73: "aminophenazone, combinations with psycholeptics",
  N02BB74: "propyphenazone, combinations with psycholeptics",
  N02BE01: "paracetamol",
  N02BE03: "phenacetin",
  N02BE04: "bucetin",
  N02BE05: "propacetamol",
  N02BE51: "paracetamol, combinations excl. psycholeptics",
  N02BE53: "phenacetin, combinations excl. psycholeptics",
  N02BE54: "bucetin, combinations excl. psycholeptics",
  N02BE71: "paracetamol, combinations with psycholeptics",
  N02BE73: "phenacetin, combinations with psycholeptics",
  N02BE74: "bucetin, combinations with psycholeptics",
  N02BF01: "gabapentin",
  N02BF02: "pregabalin",
  N02BF03: "mirogabalin",
  N02BG02: "rimazolium",
  N02BG03: "glafenine",
  N02BG04: "floctafenine",
  N02BG05: "viminol",
  N02BG06: "nefopam",
  N02BG07: "flupirtine",
  N02BG08: "ziconotide",
  N02BG09: "methoxyflurane",
  N02BG10: "cannabinoids",
  N02BG12: "tanezumab",
  N02CA01: "dihydroergotamine",
  N02CA02: "ergotamine",
  N02CA04: "methysergide",
  N02CA07: "lisuride",
  N02CA51: "dihydroergotamine, combinations",
  N02CA52: "ergotamine, combinations excl. psycholeptics",
  N02CA72: "ergotamine, combinations with psycholeptics",
  N02CB01: "flumedroxone",
  N02CC01: "sumatriptan",
  N02CC02: "naratriptan",
  N02CC03: "zolmitriptan",
  N02CC04: "rizatriptan",
  N02CC05: "almotriptan",
  N02CC06: "eletriptan",
  N02CC07: "frovatriptan",
  N02CC08: "lasmiditan",
  N02CC51: "sumatriptan and naproxen",
  N02CD01: "erenumab",
  N02CD02: "galcanezumab",
  N02CD03: "fremanezumab",
  N02CD04: "ubrogepant",
  N02CD05: "eptinezumab",
  N02CD06: "rimegepant",
  N02CD07: "atogepant",
  N02CD08: "zavegepant",
  N02CX01: "pizotifen",
  N02CX02: "clonidine",
  N02CX03: "iprazochrome",
  N02CX05: "dimetotiazine",
  N02CX06: "oxetorone",
  N03AA01: "methylphenobarbital",
  N03AA02: "phenobarbital",
  N03AA03: "primidone",
  N03AA04: "barbexaclone",
  N03AA30: "metharbital",
  N03AB01: "ethotoin",
  N03AB02: "phenytoin",
  N03AB03: "amino(diphenylhydantoin) valeric acid",
  N03AB04: "mephenytoin",
  N03AB05: "fosphenytoin",
  N03AB52: "phenytoin, combinations",
  N03AB54: "mephenytoin, combinations",
  N03AC01: "paramethadione",
  N03AC02: "trimethadione",
  N03AC03: "ethadione",
  N03AD01: "ethosuximide",
  N03AD02: "phensuximide",
  N03AD03: "mesuximide",
  N03AD51: "ethosuximide, combinations",
  N03AE01: "clonazepam",
  N03AF01: "carbamazepine",
  N03AF02: "oxcarbazepine",
  N03AF03: "rufinamide",
  N03AF04: "eslicarbazepine",
  N03AG01: "valproic acid",
  N03AG02: "valpromide",
  N03AG03: "aminobutyric acid",
  N03AG04: "vigabatrin",
  N03AG05: "progabide",
  N03AG06: "tiagabine",
  N03AX03: "sultiame",
  N03AX07: "phenacemide",
  N03AX09: "lamotrigine",
  N03AX10: "felbamate",
  N03AX11: "topiramate",
  N03AX13: "pheneturide",
  N03AX14: "levetiracetam",
  N03AX15: "zonisamide",
  N03AX17: "stiripentol",
  N03AX18: "lacosamide",
  N03AX19: "carisbamate",
  N03AX21: "retigabine",
  N03AX22: "perampanel",
  N03AX23: "brivaracetam",
  N03AX24: "cannabidiol",
  N03AX25: "cenobamate",
  N03AX26: "fenfluramine",
  N03AX27: "ganaxolone",
  N03AX30: "beclamide",
  N04AA01: "trihexyphenidyl",
  N04AA02: "biperiden",
  N04AA03: "metixene",
  N04AA04: "procyclidine",
  N04AA05: "profenamine",
  N04AA08: "dexetimide",
  N04AA09: "phenglutarimide",
  N04AA10: "mazaticol",
  N04AA11: "bornaprine",
  N04AA12: "tropatepine",
  N04AB01: "etanautine",
  N04AB02: "orphenadrine (chloride)",
  N04AC01: "benzatropine",
  N04AC30: "etybenzatropine",
  N04BA01: "levodopa",
  N04BA02: "levodopa and decarboxylase inhibitor",
  N04BA03: "levodopa, decarboxylase inhibitor and COMT inhibitor",
  N04BA04: "melevodopa",
  N04BA05: "melevodopa and decarboxylase inhibitor",
  N04BA06: "etilevodopa and decarboxylase inhibitor",
  N04BA07: "foslevodopa and decarboxylase inhibitor",
  N04BB01: "amantadine",
  N04BC01: "bromocriptine",
  N04BC02: "pergolide",
  N04BC03: "dihydroergocryptine mesylate",
  N04BC04: "ropinirole",
  N04BC05: "pramipexole",
  N04BC06: "cabergoline",
  N04BC07: "apomorphine",
  N04BC08: "piribedil",
  N04BC09: "rotigotine",
  N04BD01: "selegiline",
  N04BD02: "rasagiline",
  N04BD03: "safinamide",
  N04BX01: "tolcapone",
  N04BX02: "entacapone",
  N04BX03: "budipine",
  N04BX04: "opicapone",
  N04CX01: "istradefylline",
  N05AA01: "chlorpromazine",
  N05AA02: "levomepromazine",
  N05AA03: "promazine",
  N05AA04: "acepromazine",
  N05AA05: "triflupromazine",
  N05AA06: "cyamemazine",
  N05AA07: "chlorproethazine",
  N05AB01: "dixyrazine",
  N05AB02: "fluphenazine",
  N05AB03: "perphenazine",
  N05AB04: "prochlorperazine",
  N05AB05: "thiopropazate",
  N05AB06: "trifluoperazine",
  N05AB07: "acetophenazine",
  N05AB08: "thioproperazine",
  N05AB09: "butaperazine",
  N05AB10: "perazine",
  N05AC01: "periciazine",
  N05AC02: "thioridazine",
  N05AC03: "mesoridazine",
  N05AC04: "pipotiazine",
  N05AD01: "haloperidol",
  N05AD02: "trifluperidol",
  N05AD03: "melperone",
  N05AD04: "moperone",
  N05AD05: "pipamperone",
  N05AD06: "bromperidol",
  N05AD07: "benperidol",
  N05AD08: "droperidol",
  N05AD09: "fluanisone",
  N05AD10: "lumateperone",
  N05AE01: "oxypertine",
  N05AE02: "molindone",
  N05AE03: "sertindole",
  N05AE04: "ziprasidone",
  N05AE05: "lurasidone",
  N05AF01: "flupentixol",
  N05AF02: "clopenthixol",
  N05AF03: "chlorprothixene",
  N05AF04: "tiotixene",
  N05AF05: "zuclopenthixol",
  N05AG01: "fluspirilene",
  N05AG02: "pimozide",
  N05AG03: "penfluridol",
  N05AH01: "loxapine",
  N05AH02: "clozapine",
  N05AH03: "olanzapine",
  N05AH04: "quetiapine",
  N05AH05: "asenapine",
  N05AH06: "clotiapine",
  N05AH53: "olanzapine and samidorphan",
  N05AL01: "sulpiride",
  N05AL02: "sultopride",
  N05AL03: "tiapride",
  N05AL04: "remoxipride",
  N05AL05: "amisulpride",
  N05AL06: "veralipride",
  N05AL07: "levosulpiride",
  N05AN01: "lithium",
  N05AX07: "prothipendyl",
  N05AX08: "risperidone",
  N05AX10: "mosapramine",
  N05AX11: "zotepine",
  N05AX12: "aripiprazole",
  N05AX13: "paliperidone",
  N05AX14: "iloperidone",
  N05AX15: "cariprazine",
  N05AX16: "brexpiprazole",
  N05AX17: "pimavanserin",
  N05AX50: "xanomeline and trospium",
  N05BA01: "diazepam",
  N05BA02: "chlordiazepoxide",
  N05BA03: "medazepam",
  N05BA04: "oxazepam",
  N05BA05: "potassium clorazepate",
  N05BA06: "lorazepam",
  N05BA07: "adinazolam",
  N05BA08: "bromazepam",
  N05BA09: "clobazam",
  N05BA10: "ketazolam",
  N05BA11: "prazepam",
  N05BA12: "alprazolam",
  N05BA13: "halazepam",
  N05BA14: "pinazepam",
  N05BA15: "camazepam",
  N05BA16: "nordazepam",
  N05BA17: "fludiazepam",
  N05BA18: "ethyl loflazepate",
  N05BA19: "etizolam",
  N05BA21: "clotiazepam",
  N05BA22: "cloxazolam",
  N05BA23: "tofisopam",
  N05BA24: "bentazepam",
  N05BA25: "mexazolam",
  N05BA56: "lorazepam, combinations",
  N05BB01: "hydroxyzine",
  N05BB02: "captodiame",
  N05BB51: "hydroxyzine, combinations",
  N05BC01: "meprobamate",
  N05BC03: "emylcamate",
  N05BC04: "mebutamate",
  N05BC51: "meprobamate, combinations",
  N05BD01: "benzoctamine",
  N05BE01: "buspirone",
  N05BX01: "mephenoxalone",
  N05BX02: "gedocarnil",
  N05BX03: "etifoxine",
  N05BX04: "fabomotizole",
  N05BX05: "Lavandulae aetheroleum",
  N05CA01: "pentobarbital",
  N05CA02: "amobarbital",
  N05CA03: "butobarbital",
  N05CA04: "barbital",
  N05CA05: "aprobarbital",
  N05CA06: "secobarbital",
  N05CA07: "talbutal",
  N05CA08: "vinylbital",
  N05CA09: "vinbarbital",
  N05CA10: "cyclobarbital",
  N05CA11: "heptabarbital",
  N05CA12: "reposal",
  N05CA15: "methohexital",
  N05CA16: "hexobarbital",
  N05CA19: "thiopental",
  N05CA20: "etallobarbital",
  N05CA21: "allobarbital",
  N05CA22: "proxibarbal",
  N05CB01: "combinations of barbiturates",
  N05CB02: "barbiturates in combination with other drugs",
  N05CC01: "chloral hydrate",
  N05CC02: "chloralodol",
  N05CC03: "acetylglycinamide chloral hydrate",
  N05CC04: "dichloralphenazone",
  N05CC05: "paraldehyde",
  N05CD01: "flurazepam",
  N05CD02: "nitrazepam",
  N05CD03: "flunitrazepam",
  N05CD04: "estazolam",
  N05CD05: "triazolam",
  N05CD06: "lormetazepam",
  N05CD07: "temazepam",
  N05CD08: "midazolam",
  N05CD09: "brotizolam",
  N05CD10: "quazepam",
  N05CD11: "loprazolam",
  N05CD12: "doxefazepam",
  N05CD13: "cinolazepam",
  N05CD14: "remimazolam",
  N05CD15: "nimetazepam",
  N05CD16: "dimdazenil",
  N05CE01: "glutethimide",
  N05CE02: "methyprylon",
  N05CE03: "pyrithyldione",
  N05CF01: "zopiclone",
  N05CF02: "zolpidem",
  N05CF03: "zaleplon",
  N05CF04: "eszopiclone",
  N05CH01: "melatonin",
  N05CH02: "ramelteon",
  N05CH03: "tasimelteon",
  N05CJ01: "suvorexant",
  N05CJ02: "lemborexant",
  N05CJ03: "daridorexant",
  N05CM01: "methaqualone",
  N05CM02: "clomethiazole",
  N05CM03: "bromisoval",
  N05CM04: "carbromal",
  N05CM05: "scopolamine",
  N05CM06: "propiomazine",
  N05CM07: "triclofos",
  N05CM08: "ethchlorvynol",
  N05CM09: "Valerianae radix",
  N05CM10: "hexapropymate",
  N05CM11: "bromides",
  N05CM12: "apronal",
  N05CM13: "valnoctamide",
  N05CM15: "methylpentynol",
  N05CM16: "niaprazine",
  N05CM18: "dexmedetomidine",
  N05CX01: "meprobamate, combinations",
  N05CX02: "methaqualone, combinations",
  N05CX03: "methylpentynol, combinations",
  N05CX04: "clomethiazole, combinations",
  N05CX05: "emepronium, combinations",
  N05CX06: "dipiperonylaminoethanol, combinations",
  N06AA01: "desipramine",
  N06AA02: "imipramine",
  N06AA03: "imipramine oxide",
  N06AA04: "clomipramine",
  N06AA05: "opipramol",
  N06AA06: "trimipramine",
  N06AA07: "lofepramine",
  N06AA08: "dibenzepin",
  N06AA09: "amitriptyline",
  N06AA10: "nortriptyline",
  N06AA11: "protriptyline",
  N06AA12: "doxepin",
  N06AA13: "iprindole",
  N06AA14: "melitracen",
  N06AA15: "butriptyline",
  N06AA16: "dosulepin",
  N06AA17: "amoxapine",
  N06AA18: "dimetacrine",
  N06AA19: "amineptine",
  N06AA21: "maprotiline",
  N06AA23: "quinupramine",
  N06AB02: "zimeldine",
  N06AB03: "fluoxetine",
  N06AB04: "citalopram",
  N06AB05: "paroxetine",
  N06AB06: "sertraline",
  N06AB07: "alaproclate",
  N06AB08: "fluvoxamine",
  N06AB09: "etoperidone",
  N06AB10: "escitalopram",
  N06AF01: "isocarboxazid",
  N06AF02: "nialamide",
  N06AF03: "phenelzine",
  N06AF04: "tranylcypromine",
  N06AF05: "iproniazide",
  N06AF06: "iproclozide",
  N06AG02: "moclobemide",
  N06AG03: "toloxatone",
  N06AX01: "oxitriptan",
  N06AX02: "tryptophan",
  N06AX03: "mianserin",
  N06AX04: "nomifensine",
  N06AX05: "trazodone",
  N06AX06: "nefazodone",
  N06AX07: "minaprine",
  N06AX08: "bifemelane",
  N06AX09: "viloxazine",
  N06AX10: "oxaflozane",
  N06AX11: "mirtazapine",
  N06AX12: "bupropion",
  N06AX13: "medifoxamine",
  N06AX14: "tianeptine",
  N06AX15: "pivagabine",
  N06AX16: "venlafaxine",
  N06AX17: "milnacipran",
  N06AX18: "reboxetine",
  N06AX19: "gepirone",
  N06AX21: "duloxetine",
  N06AX22: "agomelatine",
  N06AX23: "desvenlafaxine",
  N06AX24: "vilazodone",
  N06AX25: "Hyperici herba",
  N06AX26: "vortioxetine",
  N06AX27: "esketamine",
  N06AX28: "levomilnacipran",
  N06AX29: "brexanolone",
  N06AX31: "zuranolone",
  N06AX62: "bupropion and dextromethorphan",
  N06BA01: "amfetamine",
  N06BA02: "dexamfetamine",
  N06BA03: "metamfetamine",
  N06BA04: "methylphenidate",
  N06BA05: "pemoline",
  N06BA06: "fencamfamin",
  N06BA07: "modafinil",
  N06BA08: "fenozolone",
  N06BA09: "atomoxetine",
  N06BA10: "fenetylline",
  N06BA11: "dexmethylphenidate",
  N06BA12: "lisdexamfetamine",
  N06BA13: "armodafinil",
  N06BA14: "solriamfetol",
  N06BA15: "dexmethylphenidate and serdexmethylphenidate",
  N06BC01: "caffeine",
  N06BC02: "propentofylline",
  N06BX01: "meclofenoxate",
  N06BX02: "pyritinol",
  N06BX03: "piracetam",
  N06BX04: "deanol",
  N06BX05: "fipexide",
  N06BX06: "citicoline",
  N06BX07: "oxiracetam",
  N06BX08: "pirisudanol",
  N06BX09: "linopirdine",
  N06BX10: "nizofenone",
  N06BX11: "aniracetam",
  N06BX12: "acetylcarnitine",
  N06BX13: "idebenone",
  N06BX14: "prolintane",
  N06BX15: "pipradrol",
  N06BX16: "pramiracetam",
  N06BX17: "adrafinil",
  N06BX18: "vinpocetine",
  N06BX21: "temgicoluril",
  N06BX22: "phenibut",
  N06BX53: "piracetam and cinnarizine",
  N06CA01: "amitriptyline and psycholeptics",
  N06CA02: "melitracen and psycholeptics",
  N06CA03: "fluoxetine and psycholeptics",
  N06DA01: "tacrine",
  N06DA02: "donepezil",
  N06DA03: "rivastigmine",
  N06DA04: "galantamine",
  N06DA05: "ipidacrine",
  N06DA06: "benzgalantamine",
  N06DA52: "donepezil and memantine",
  N06DA53: "donepezil, memantine and Ginkgo folium",
  N06DX01: "memantine",
  N06DX02: "Ginkgo folium",
  N06DX03: "aducanumab",
  N06DX04: "lecanemab",
  N06DX05: "donanemab",
  N06DX06: "blarcamesine",
  N06DX07: "hydromethylthionine",
  N06DX30: "combinations",
  N07AA01: "neostigmine",
  N07AA02: "pyridostigmine",
  N07AA03: "distigmine",
  N07AA30: "ambenonium",
  N07AA51: "neostigmine, combinations",
  N07AB01: "carbachol",
  N07AB02: "bethanechol",
  N07AX01: "pilocarpine",
  N07AX02: "choline alfoscerate",
  N07AX03: "cevimeline",
  N07BA01: "nicotine",
  N07BA03: "varenicline",
  N07BA04: "cytisinicline",
  N07BB01: "disulfiram",
  N07BB02: "calcium carbimide",
  N07BB03: "acamprosate",
  N07BB04: "naltrexone",
  N07BB05: "nalmefene",
  N07BB06: "ondelopran",
  N07BC01: "buprenorphine",
  N07BC02: "methadone",
  N07BC03: "levacetylmethadol",
  N07BC04: "lofexidine",
  N07BC05: "levomethadone",
  N07BC06: "diamorphine",
  N07BC51: "buprenorphine, combinations",
  N07CA01: "betahistine",
  N07CA02: "cinnarizine",
  N07CA03: "flunarizine",
  N07CA04: "acetylleucine",
  N07CA52: "cinnarizine, combinations",
  N07XX01: "tirilazad",
  N07XX02: "riluzole",
  N07XX03: "xaliproden",
  N07XX04: "sodium oxybate",
  N07XX05: "amifampridine",
  N07XX06: "tetrabenazine",
  N07XX07: "fampridine",
  N07XX08: "tafamidis",
  N07XX10: "laquinimod",
  N07XX11: "pitolisant",
  N07XX12: "patisiran",
  N07XX13: "valbenazine",
  N07XX14: "edaravone",
  N07XX15: "inotersen",
  N07XX16: "deutetrabenazine",
  N07XX17: "arimoclomol",
  N07XX18: "vutrisiran",
  N07XX19: "sodium phenylbutyrate and ursodoxicoltaurine",
  N07XX21: "eplontersen",
  N07XX22: "tofersen",
  N07XX23: "troriluzole",
  N07XX24: "trofinetide",
  N07XX25: "omaveloxolone",
  N07XX26: "pridopidine",
  N07XX27: "levacetylleucine",
  N07XX59: "dextromethorphan, combinations",
  // P — 구충·살충·기피제
  P01AA01: "broxyquinoline",
  P01AA02: "clioquinol",
  P01AA04: "chlorquinaldol",
  P01AA05: "tilbroquinol",
  P01AA30: "tilbroquinol and tiliquinol",
  P01AA52: "clioquinol, combinations",
  P01AB01: "metronidazole",
  P01AB02: "tinidazole",
  P01AB03: "ornidazole",
  P01AB04: "azanidazole",
  P01AB05: "propenidazole",
  P01AB06: "nimorazole",
  P01AB07: "secnidazole",
  P01AB08: "satranidazole",
  P01AB51: "metronidazole and furazolidone",
  P01AB52: "metronidazole and diloxanide",
  P01AB53: "tinidazole and diloxanide",
  P01AC01: "diloxanide",
  P01AC02: "clefamide",
  P01AC03: "etofamide",
  P01AC04: "teclozan",
  P01AR01: "arsthinol",
  P01AR02: "difetarsone",
  P01AR03: "glycobiarsol",
  P01AR53: "glycobiarsol, combinations",
  P01AX01: "chiniofon",
  P01AX02: "emetine",
  P01AX04: "phanquinone",
  P01AX05: "mepacrine",
  P01AX06: "atovaquone",
  P01AX07: "trimetrexate",
  P01AX08: "tenonitrozole",
  P01AX09: "dehydroemetine",
  P01AX10: "fumagillin",
  P01AX11: "nitazoxanide",
  P01AX52: "emetine, combinations",
  P01BA01: "chloroquine",
  P01BA02: "hydroxychloroquine",
  P01BA03: "primaquine",
  P01BA06: "amodiaquine",
  P01BA07: "tafenoquine",
  P01BB01: "proguanil",
  P01BB02: "cycloguanil embonate",
  P01BB51: "proguanil and atovaquone",
  P01BB52: "chloroquine and proguanil",
  P01BC01: "quinine",
  P01BC02: "mefloquine",
  P01BD01: "pyrimethamine",
  P01BD51: "pyrimethamine, combinations",
  P01BE01: "artemisinin",
  P01BE02: "artemether",
  P01BE03: "artesunate",
  P01BE04: "artemotil",
  P01BE05: "artenimol",
  P01BF01: "artemether and lumefantrine",
  P01BF02: "artesunate and mefloquine",
  P01BF03: "artesunate and amodiaquine",
  P01BF04: "artesunate, sulfalene and pyrimethamine",
  P01BF05: "artenimol and piperaquine",
  P01BF06: "artesunate and pyronaridine",
  P01BF07: "artemisinin and piperaquine",
  P01BF08: "artemisinin and naphthoquine",
  P01BF09: "artesunate, sulfadoxine and pyrimethamine",
  P01BX01: "halofantrine",
  P01BX02: "arterolane and piperaquine",
  P01CA02: "benznidazole",
  P01CA03: "fexinidazole",
  P01CB01: "meglumine antimonate",
  P01CB02: "sodium stibogluconate",
  P01CC01: "nifurtimox",
  P01CC02: "nitrofural",
  P01CD01: "melarsoprol",
  P01CD02: "acetarsol",
  P01CX01: "pentamidine isethionate",
  P01CX02: "suramin sodium",
  P01CX03: "eflornithine",
  P01CX04: "miltefosine",
  P02BA01: "praziquantel",
  P02BA02: "oxamniquine",
  P02BA03: "arpraziquantel",
  P02BB01: "metrifonate",
  P02BX01: "bithionol",
  P02BX02: "niridazole",
  P02BX03: "stibophen",
  P02BX04: "triclabendazole",
  P02CA01: "mebendazole",
  P02CA02: "tiabendazole",
  P02CA03: "albendazole",
  P02CA04: "ciclobendazole",
  P02CA05: "flubendazole",
  P02CA06: "fenbendazole",
  P02CA51: "mebendazole, combinations",
  P02CA53: "albendazole and ivermectin",
  P02CB01: "piperazine",
  P02CB02: "diethylcarbamazine",
  P02CC01: "pyrantel",
  P02CC02: "oxantel",
  P02CE01: "levamisole",
  P02CF01: "ivermectin",
  P02CX01: "pyrvinium",
  P02CX02: "bephenium",
  P02CX03: "moxidectin",
  P02DA01: "niclosamide",
  P02DX01: "desaspidin",
  P02DX02: "dichlorophen",
  P03AA01: "dixanthogen",
  P03AA02: "potassium polysulfide",
  P03AA03: "mesulfen",
  P03AA04: "disulfiram",
  P03AA05: "thiram",
  P03AA54: "disulfiram, combinations",
  P03AB01: "clofenotane",
  P03AB02: "lindane",
  P03AB51: "clofenotane, combinations",
  P03AC01: "pyrethrum",
  P03AC02: "bioallethrin",
  P03AC03: "phenothrin",
  P03AC04: "permethrin",
  P03AC51: "pyrethrum, combinations",
  P03AC52: "bioallethrin, combinations",
  P03AC53: "phenothrin, combinations",
  P03AC54: "permethrin, combinations",
  P03AX01: "benzyl benzoate",
  P03AX02: "copper oleinate",
  P03AX03: "malathion",
  P03AX04: "quassia",
  P03AX05: "dimeticone",
  P03AX06: "benzyl alcohol",
  P03AX07: "abametapir",
  P03BA01: "cyfluthrin",
  P03BA02: "cypermethrin",
  P03BA03: "decamethrin",
  P03BA04: "tetramethrin",
  P03BX01: "diethyltoluamide",
  P03BX02: "dimethylphthalate",
  P03BX03: "dibutylphthalate",
  P03BX04: "dibutylsuccinate",
  P03BX05: "dimethylcarbate",
  P03BX06: "etohexadiol",
  // R — 호흡기계
  R01AA02: "cyclopentamine",
  R01AA03: "ephedrine",
  R01AA04: "phenylephrine",
  R01AA05: "oxymetazoline",
  R01AA06: "tetryzoline",
  R01AA07: "xylometazoline",
  R01AA08: "naphazoline",
  R01AA09: "tramazoline",
  R01AA10: "metizoline",
  R01AA11: "tuaminoheptane",
  R01AA12: "fenoxazoline",
  R01AA13: "tymazoline",
  R01AA14: "epinephrine",
  R01AA15: "indanazoline",
  R01AB01: "phenylephrine",
  R01AB02: "naphazoline",
  R01AB03: "tetryzoline",
  R01AB05: "ephedrine",
  R01AB06: "xylometazoline",
  R01AB07: "oxymetazoline",
  R01AB08: "tuaminoheptane",
  R01AC01: "cromoglicic acid",
  R01AC02: "levocabastine",
  R01AC03: "azelastine",
  R01AC04: "antazoline",
  R01AC05: "spaglumic acid",
  R01AC06: "thonzylamine",
  R01AC07: "nedocromil",
  R01AC08: "olopatadine",
  R01AC51: "cromoglicic acid, combinations",
  R01AD01: "beclometasone",
  R01AD02: "prednisolone",
  R01AD03: "dexamethasone",
  R01AD04: "flunisolide",
  R01AD05: "budesonide",
  R01AD06: "betamethasone",
  R01AD07: "tixocortol",
  R01AD08: "fluticasone",
  R01AD09: "mometasone",
  R01AD11: "triamcinolone",
  R01AD12: "fluticasone furoate",
  R01AD13: "ciclesonide",
  R01AD52: "prednisolone, combinations",
  R01AD53: "dexamethasone, combinations",
  R01AD57: "tixocortol, combinations",
  R01AD58: "fluticasone, combinations",
  R01AD59: "mometasone, combinations",
  R01AD60: "hydrocortisone, combinations",
  R01AX01: "calcium hexamine thiocyanate",
  R01AX02: "retinol",
  R01AX03: "ipratropium bromide",
  R01AX05: "ritiometan",
  R01AX06: "mupirocin",
  R01AX07: "hexamidine",
  R01AX08: "framycetin",
  R01AX09: "hyaluronic acid",
  R01AX10: "various",
  R01AX30: "combinations",
  R01BA01: "phenylpropanolamine",
  R01BA02: "pseudoephedrine",
  R01BA03: "phenylephrine",
  R01BA51: "phenylpropanolamine, combinations",
  R01BA52: "pseudoephedrine, combinations",
  R01BA53: "phenylephrine, combinations",
  R02AA01: "ambazone",
  R02AA02: "dequalinium",
  R02AA03: "dichlorobenzyl alcohol",
  R02AA05: "chlorhexidine",
  R02AA06: "cetylpyridinium",
  R02AA09: "benzethonium",
  R02AA10: "myristyl-benzalkonium",
  R02AA11: "chlorquinaldol",
  R02AA12: "hexylresorcinol",
  R02AA13: "acriflavinium chloride",
  R02AA14: "oxyquinoline",
  R02AA15: "povidone-iodine",
  R02AA16: "benzalkonium",
  R02AA17: "cetrimonium",
  R02AA18: "hexamidine",
  R02AA19: "phenol",
  R02AA20: "various",
  R02AA21: "octenidine",
  R02AB01: "neomycin",
  R02AB02: "tyrothricin",
  R02AB03: "fusafungine",
  R02AB04: "bacitracin",
  R02AB30: "gramicidin",
  R02AD01: "benzocaine",
  R02AD02: "lidocaine",
  R02AD03: "cocaine",
  R02AD04: "dyclonine",
  R02AD05: "ambroxol",
  R02AX01: "flurbiprofen",
  R02AX02: "ibuprofen",
  R02AX03: "benzydamine",
  R03AA01: "epinephrine",
  R03AB02: "isoprenaline",
  R03AB03: "orciprenaline",
  R03AC02: "salbutamol",
  R03AC03: "terbutaline",
  R03AC04: "fenoterol",
  R03AC05: "rimiterol",
  R03AC06: "hexoprenaline",
  R03AC07: "isoetarine",
  R03AC08: "pirbuterol",
  R03AC09: "tretoquinol",
  R03AC10: "carbuterol",
  R03AC11: "tulobuterol",
  R03AC12: "salmeterol",
  R03AC13: "formoterol",
  R03AC14: "clenbuterol",
  R03AC15: "reproterol",
  R03AC16: "procaterol",
  R03AC17: "bitolterol",
  R03AC18: "indacaterol",
  R03AC19: "olodaterol",
  R03AK01: "epinephrine and other drugs for obstructive airway diseases",
  R03AK02: "isoprenaline and other drugs for obstructive airway diseases",
  R03AK04: "salbutamol and sodium cromoglicate",
  R03AK05: "reproterol and sodium cromoglicate",
  R03AK06: "salmeterol and fluticasone",
  R03AK07: "formoterol and budesonide",
  R03AK08: "formoterol and beclometasone",
  R03AK09: "formoterol and mometasone",
  R03AK10: "vilanterol and fluticasone furoate",
  R03AK11: "formoterol and fluticasone",
  R03AK12: "salmeterol and budesonide",
  R03AK13: "salbutamol and beclometasone",
  R03AK14: "indacaterol and mometasone",
  R03AK15: "salbutamol and budesonide",
  R03AL01: "fenoterol and ipratropium bromide",
  R03AL02: "salbutamol and ipratropium bromide",
  R03AL03: "vilanterol and umeclidinium bromide",
  R03AL04: "indacaterol and glycopyrronium bromide",
  R03AL05: "formoterol and aclidinium bromide",
  R03AL06: "olodaterol and tiotropium bromide",
  R03AL07: "formoterol and glycopyrronium bromide",
  R03AL08: "vilanterol, umeclidinium bromide and fluticasone furoate",
  R03AL09: "formoterol, glycopyrronium bromide and beclometasone",
  R03AL10: "formoterol and tiotropium bromide",
  R03AL11: "formoterol, glycopyrronium bromide and budesonide",
  R03AL12: "indacaterol, glycopyrronium bromide and mometasone",
  R03BA01: "beclometasone",
  R03BA02: "budesonide",
  R03BA03: "flunisolide",
  R03BA04: "betamethasone",
  R03BA05: "fluticasone",
  R03BA06: "triamcinolone",
  R03BA07: "mometasone",
  R03BA08: "ciclesonide",
  R03BA09: "fluticasone furoate",
  R03BB01: "ipratropium bromide",
  R03BB02: "oxitropium bromide",
  R03BB03: "stramoni preparations",
  R03BB04: "tiotropium bromide",
  R03BB05: "aclidinium bromide",
  R03BB06: "glycopyrronium bromide",
  R03BB07: "umeclidinium bromide",
  R03BB08: "revefenacin",
  R03BB54: "tiotropium bromide, combinations",
  R03BC01: "cromoglicic acid",
  R03BC03: "nedocromil",
  R03BX01: "fenspiride",
  R03BX02: "ensifentrine",
  R03CA02: "ephedrine",
  R03CB01: "isoprenaline",
  R03CB02: "methoxyphenamine",
  R03CB03: "orciprenaline",
  R03CB51: "isoprenaline, combinations",
  R03CB53: "orciprenaline, combinations",
  R03CC02: "salbutamol",
  R03CC03: "terbutaline",
  R03CC04: "fenoterol",
  R03CC05: "hexoprenaline",
  R03CC06: "isoetarine",
  R03CC07: "pirbuterol",
  R03CC08: "procaterol",
  R03CC09: "tretoquinol",
  R03CC10: "carbuterol",
  R03CC11: "tulobuterol",
  R03CC12: "bambuterol",
  R03CC13: "clenbuterol",
  R03CC14: "reproterol",
  R03CC15: "formoterol",
  R03CC53: "terbutaline, combinations",
  R03CC63: "clenbuterol and ambroxol",
  R03DA01: "diprophylline",
  R03DA02: "choline theophyllinate",
  R03DA03: "proxyphylline",
  R03DA04: "theophylline",
  R03DA05: "aminophylline",
  R03DA06: "etamiphylline",
  R03DA07: "theobromine",
  R03DA08: "bamifylline",
  R03DA09: "acefylline piperazine",
  R03DA10: "bufylline",
  R03DA11: "doxofylline",
  R03DA12: "mepyramine theophyllinacetate",
  R03DA20: "combinations of xanthines",
  R03DA51: "diprophylline, combinations",
  R03DA54: "theophylline, combinations excl. psycholeptics",
  R03DA55: "aminophylline, combinations",
  R03DA57: "theobromine, combinations",
  R03DA74: "theophylline, combinations with psycholeptics",
  R03DB01: "diprophylline and adrenergics",
  R03DB02: "choline theophyllinate and adrenergics",
  R03DB03: "proxyphylline and adrenergics",
  R03DB04: "theophylline and adrenergics",
  R03DB05: "aminophylline and adrenergics",
  R03DB06: "etamiphylline and adrenergics",
  R03DC01: "zafirlukast",
  R03DC02: "pranlukast",
  R03DC03: "montelukast",
  R03DC04: "ibudilast",
  R03DC53: "montelukast, combinations",
  R03DX01: "amlexanox",
  R03DX02: "eprozinol",
  R03DX03: "fenspiride",
  R03DX05: "omalizumab",
  R03DX06: "seratrodast",
  R03DX07: "roflumilast",
  R03DX08: "reslizumab",
  R03DX09: "mepolizumab",
  R03DX10: "benralizumab",
  R03DX11: "tezepelumab",
  R03DX12: "depemokimab",
  R05CA01: "tyloxapol",
  R05CA02: "potassium iodide",
  R05CA03: "guaifenesin",
  R05CA04: "ipecacuanha",
  R05CA05: "altheae radix",
  R05CA06: "senega",
  R05CA07: "antimony pentasulfide",
  R05CA08: "creosote",
  R05CA09: "guaiacolsulfonate",
  R05CA10: "combinations",
  R05CA11: "levoverbenone",
  R05CA12: "Hederae helicis folium",
  R05CA13: "cineole",
  R05CB01: "acetylcysteine",
  R05CB02: "bromhexine",
  R05CB03: "carbocisteine",
  R05CB04: "eprazinone",
  R05CB05: "mesna",
  R05CB06: "ambroxol",
  R05CB07: "sobrerol",
  R05CB08: "domiodol",
  R05CB09: "letosteine",
  R05CB10: "combinations",
  R05CB11: "stepronin",
  R05CB13: "dornase alfa (desoxyribonuclease)",
  R05CB14: "neltenexine",
  R05CB15: "erdosteine",
  R05CB16: "mannitol",
  R05DA01: "ethylmorphine",
  R05DA03: "hydrocodone",
  R05DA04: "codeine",
  R05DA05: "opium alkaloids with morphine",
  R05DA06: "normethadone",
  R05DA07: "noscapine",
  R05DA08: "pholcodine",
  R05DA09: "dextromethorphan",
  R05DA10: "thebacon",
  R05DA11: "dimemorfan",
  R05DA12: "acetyldihydrocodeine",
  R05DA20: "combinations",
  R05DB01: "benzonatate",
  R05DB02: "benproperine",
  R05DB03: "clobutinol",
  R05DB04: "isoaminile",
  R05DB05: "pentoxyverine",
  R05DB07: "oxolamine",
  R05DB09: "oxeladin",
  R05DB10: "clofedanol",
  R05DB11: "pipazetate",
  R05DB12: "bibenzonium bromide",
  R05DB13: "butamirate",
  R05DB14: "fedrilate",
  R05DB15: "zipeprol",
  R05DB16: "dibunate",
  R05DB17: "droxypropine",
  R05DB18: "prenoxdiazine",
  R05DB19: "dropropizine",
  R05DB20: "combinations",
  R05DB21: "cloperastine",
  R05DB22: "meprotixol",
  R05DB23: "piperidione",
  R05DB24: "tipepidine",
  R05DB25: "morclofone",
  R05DB26: "nepinalone",
  R05DB27: "levodropropizine",
  R05DB28: "dimethoxanate",
  R05DB29: "gefapixant",
  R05FA01: "opium derivatives and mucolytics",
  R05FA02: "opium derivatives and expectorants",
  R05FB01: "cough suppressants and mucolytics",
  R05FB02: "cough suppressants and expectorants",
  R06AA01: "bromazine",
  R06AA02: "diphenhydramine",
  R06AA04: "clemastine",
  R06AA06: "chlorphenoxamine",
  R06AA07: "diphenylpyraline",
  R06AA08: "carbinoxamine",
  R06AA09: "doxylamine",
  R06AA10: "trimethobenzamide",
  R06AA11: "dimenhydrinate",
  R06AA52: "diphenhydramine, combinations",
  R06AA54: "clemastine, combinations",
  R06AA56: "chlorphenoxamine, combinations",
  R06AA57: "diphenylpyraline, combinations",
  R06AA59: "doxylamine, combinations",
  R06AA61: "dimenhydrinate, combinations",
  R06AB01: "brompheniramine",
  R06AB02: "dexchlorpheniramine",
  R06AB03: "dimetindene",
  R06AB04: "chlorphenamine",
  R06AB05: "pheniramine",
  R06AB06: "dexbrompheniramine",
  R06AB07: "talastine",
  R06AB51: "brompheniramine, combinations",
  R06AB52: "dexchlorpheniramine, combinations",
  R06AB54: "chlorphenamine, combinations",
  R06AB56: "dexbrompheniramine, combinations",
  R06AC01: "mepyramine",
  R06AC02: "histapyrrodine",
  R06AC03: "chloropyramine",
  R06AC04: "tripelennamine",
  R06AC05: "methapyrilene",
  R06AC06: "thonzylamine",
  R06AC52: "histapyrrodine, combinations",
  R06AC53: "chloropyramine, combinations",
  R06AD01: "alimemazine",
  R06AD02: "promethazine",
  R06AD03: "thiethylperazine",
  R06AD04: "methdilazine",
  R06AD05: "hydroxyethylpromethazine",
  R06AD06: "thiazinam",
  R06AD07: "mequitazine",
  R06AD08: "oxomemazine",
  R06AD09: "isothipendyl",
  R06AD52: "promethazine, combinations",
  R06AD55: "hydroxyethylpromethazine, combinations",
  R06AE01: "buclizine",
  R06AE03: "cyclizine",
  R06AE04: "chlorcyclizine",
  R06AE05: "meclozine",
  R06AE06: "oxatomide",
  R06AE07: "cetirizine",
  R06AE09: "levocetirizine",
  R06AE51: "buclizine, combinations",
  R06AE53: "cyclizine, combinations",
  R06AE55: "meclozine, combinations",
  R06AX01: "bamipine",
  R06AX02: "cyproheptadine",
  R06AX03: "thenalidine",
  R06AX04: "phenindamine",
  R06AX05: "antazoline",
  R06AX07: "triprolidine",
  R06AX08: "pyrrobutamine",
  R06AX09: "azatadine",
  R06AX11: "astemizole",
  R06AX12: "terfenadine",
  R06AX13: "loratadine",
  R06AX15: "mebhydrolin",
  R06AX16: "deptropine",
  R06AX17: "ketotifen",
  R06AX18: "acrivastine",
  R06AX19: "azelastine",
  R06AX21: "tritoqualine",
  R06AX22: "ebastine",
  R06AX23: "pimethixene",
  R06AX24: "epinastine",
  R06AX25: "mizolastine",
  R06AX26: "fexofenadine",
  R06AX27: "desloratadine",
  R06AX28: "rupatadine",
  R06AX29: "bilastine",
  R06AX31: "quifenadine",
  R06AX32: "sequifenadine",
  R06AX53: "thenalidine, combinations",
  R06AX58: "pyrrobutamine, combinations",
  R07AA01: "colfosceril palmitate",
  R07AA02: "natural phospholipids",
  R07AA30: "combinations",
  R07AB01: "doxapram",
  R07AB02: "nikethamide",
  R07AB03: "pentetrazol",
  R07AB04: "etamivan",
  R07AB05: "bemegride",
  R07AB06: "prethcamide",
  R07AB07: "almitrine",
  R07AB08: "dimefline",
  R07AB09: "mepixanox",
  R07AB52: "nikethamide, combinations",
  R07AB53: "pentetrazol, combinations",
  R07AX01: "nitric oxide",
  R07AX02: "ivacaftor",
  R07AX30: "ivacaftor and lumacaftor",
  R07AX31: "ivacaftor and tezacaftor",
  R07AX32: "ivacaftor, tezacaftor and elexacaftor",
  R07AX33: "deutivacaftor, tezacaftor and vanzacaftor",
  // S — 감각기관
  S01AA01: "chloramphenicol",
  S01AA02: "chlortetracycline",
  S01AA03: "neomycin",
  S01AA04: "oxytetracycline",
  S01AA05: "tyrothricin",
  S01AA07: "framycetin",
  S01AA09: "tetracycline",
  S01AA10: "natamycin",
  S01AA11: "gentamicin",
  S01AA12: "tobramycin",
  S01AA13: "fusidic acid",
  S01AA14: "benzylpenicillin",
  S01AA15: "dihydrostreptomycin",
  S01AA16: "rifamycin",
  S01AA17: "erythromycin",
  S01AA18: "polymyxin B",
  S01AA19: "ampicillin",
  S01AA20: "antibiotics in combination with other drugs",
  S01AA21: "amikacin",
  S01AA22: "micronomicin",
  S01AA23: "netilmicin",
  S01AA24: "kanamycin",
  S01AA25: "azidamfenicol",
  S01AA26: "azithromycin",
  S01AA27: "cefuroxime",
  S01AA28: "vancomycin",
  S01AA29: "dibekacin",
  S01AA30: "combinations of different antibiotics",
  S01AA31: "cefmenoxime",
  S01AA32: "bacitracin",
  S01AB01: "sulfamethizole",
  S01AB02: "sulfafurazole",
  S01AB03: "sulfadicramide",
  S01AB04: "sulfacetamide",
  S01AB05: "sulfafenazol",
  S01AD01: "idoxuridine",
  S01AD02: "trifluridine",
  S01AD03: "aciclovir",
  S01AD05: "interferon",
  S01AD06: "vidarabine",
  S01AD07: "famciclovir",
  S01AD08: "fomivirsen",
  S01AD09: "ganciclovir",
  S01AE01: "ofloxacin",
  S01AE02: "norfloxacin",
  S01AE03: "ciprofloxacin",
  S01AE04: "lomefloxacin",
  S01AE05: "levofloxacin",
  S01AE06: "gatifloxacin",
  S01AE07: "moxifloxacin",
  S01AE08: "besifloxacin",
  S01AE09: "tosufloxacin",
  S01AX01: "mercury compounds",
  S01AX02: "silver compounds",
  S01AX03: "zinc compounds",
  S01AX04: "nitrofural",
  S01AX05: "bibrocathol",
  S01AX06: "resorcinol",
  S01AX07: "sodium borate",
  S01AX08: "hexamidine",
  S01AX09: "chlorhexidine",
  S01AX10: "sodium propionate",
  S01AX14: "dibrompropamidine",
  S01AX15: "propamidine",
  S01AX16: "picloxydine",
  S01AX18: "povidone-iodine",
  S01AX24: "polihexanide",
  S01AX25: "lotilaner",
  S01AX26: "cethexonium",
  S01BA01: "dexamethasone",
  S01BA02: "hydrocortisone",
  S01BA03: "cortisone",
  S01BA04: "prednisolone",
  S01BA05: "triamcinolone",
  S01BA06: "betamethasone",
  S01BA07: "fluorometholone",
  S01BA08: "medrysone",
  S01BA09: "clobetasone",
  S01BA10: "alclometasone",
  S01BA11: "desonide",
  S01BA12: "formocortal",
  S01BA13: "rimexolone",
  S01BA14: "loteprednol",
  S01BA15: "fluocinolone acetonide",
  S01BA16: "difluprednate",
  S01BA17: "clobetasol",
  S01BB01: "hydrocortisone and mydriatics",
  S01BB02: "prednisolone and mydriatics",
  S01BB03: "fluorometholone and mydriatics",
  S01BB04: "betamethasone and mydriatics",
  S01BC01: "indometacin",
  S01BC02: "oxyphenbutazone",
  S01BC03: "diclofenac",
  S01BC04: "flurbiprofen",
  S01BC05: "ketorolac",
  S01BC06: "piroxicam",
  S01BC07: "bendazac",
  S01BC08: "salicylic acid",
  S01BC09: "pranoprofen",
  S01BC10: "nepafenac",
  S01BC11: "bromfenac",
  S01CA01: "dexamethasone and antiinfectives",
  S01CA02: "prednisolone and antiinfectives",
  S01CA03: "hydrocortisone and antiinfectives",
  S01CA04: "fluocortolone and antiinfectives",
  S01CA05: "betamethasone and antiinfectives",
  S01CA06: "fludrocortisone and antiinfectives",
  S01CA07: "fluorometholone and antiinfectives",
  S01CA08: "methylprednisolone and antiinfectives",
  S01CA09: "chloroprednisone and antiinfectives",
  S01CA10: "fluocinolone acetonide and antiinfectives",
  S01CA11: "clobetasone and antiinfectives",
  S01CA12: "loteprednol and antiinfectives",
  S01CB01: "dexamethasone",
  S01CB02: "prednisolone",
  S01CB03: "hydrocortisone",
  S01CB04: "betamethasone",
  S01CB05: "fluorometholone",
  S01CC01: "diclofenac and antiinfectives",
  S01CC02: "indometacin and antiinfectives",
  S01EA01: "epinephrine",
  S01EA02: "dipivefrine",
  S01EA03: "apraclonidine",
  S01EA04: "clonidine",
  S01EA05: "brimonidine",
  S01EA51: "epinephrine, combinations",
  S01EA55: "brimonidine and ripasudil",
  S01EB01: "pilocarpine",
  S01EB02: "carbachol",
  S01EB03: "ecothiopate",
  S01EB04: "demecarium",
  S01EB05: "physostigmine",
  S01EB06: "neostigmine",
  S01EB07: "fluostigmine",
  S01EB08: "aceclidine",
  S01EB09: "acetylcholine",
  S01EB10: "paraoxon",
  S01EB51: "pilocarpine, combinations",
  S01EB58: "aceclidine, combinations",
  S01EC01: "acetazolamide",
  S01EC02: "diclofenamide",
  S01EC03: "dorzolamide",
  S01EC04: "brinzolamide",
  S01EC05: "methazolamide",
  S01EC54: "brinzolamide, combinations",
  S01ED01: "timolol",
  S01ED02: "betaxolol",
  S01ED03: "levobunolol",
  S01ED04: "metipranolol",
  S01ED05: "carteolol",
  S01ED06: "befunolol",
  S01ED51: "timolol, combinations",
  S01ED52: "betaxolol, combinations",
  S01ED54: "metipranolol, combinations",
  S01ED55: "carteolol, combinations",
  S01EE01: "latanoprost",
  S01EE02: "unoprostone",
  S01EE03: "bimatoprost",
  S01EE04: "travoprost",
  S01EE05: "tafluprost",
  S01EE06: "latanoprostene bunod",
  S01EE51: "latanoprost and netarsudil",
  S01EE52: "latanoprost and dorzolamide",
  S01EX01: "guanethidine",
  S01EX02: "dapiprazole",
  S01EX05: "netarsudil",
  S01EX06: "omidenepag",
  S01EX07: "ripasudil",
  S01FA01: "atropine",
  S01FA02: "scopolamine",
  S01FA03: "methylscopolamine",
  S01FA04: "cyclopentolate",
  S01FA05: "homatropine",
  S01FA06: "tropicamide",
  S01FA54: "cyclopentolate, combinations",
  S01FA56: "tropicamide, combinations",
  S01FB01: "phenylephrine",
  S01FB02: "ephedrine",
  S01FB03: "ibopamine",
  S01FB51: "phenylephrine and ketorolac",
  S01GA01: "naphazoline",
  S01GA02: "tetryzoline",
  S01GA03: "xylometazoline",
  S01GA04: "oxymetazoline",
  S01GA05: "phenylephrine",
  S01GA06: "oxedrine",
  S01GA07: "brimonidine",
  S01GA51: "naphazoline, combinations",
  S01GA52: "tetryzoline, combinations",
  S01GA53: "xylometazoline, combinations",
  S01GA55: "phenylephrine, combinations",
  S01GA56: "oxedrine, combinations",
  S01GX01: "cromoglicic acid",
  S01GX02: "levocabastine",
  S01GX03: "spaglumic acid",
  S01GX04: "nedocromil",
  S01GX05: "lodoxamide",
  S01GX06: "emedastine",
  S01GX07: "azelastine",
  S01GX08: "ketotifen",
  S01GX09: "olopatadine",
  S01GX10: "epinastine",
  S01GX11: "alcaftadine",
  S01GX12: "cetirizine",
  S01GX13: "bilastine",
  S01GX51: "cromoglicic acid, combinations",
  S01HA01: "cocaine",
  S01HA02: "oxybuprocaine",
  S01HA03: "tetracaine",
  S01HA04: "proxymetacaine",
  S01HA05: "procaine",
  S01HA06: "cinchocaine",
  S01HA07: "lidocaine",
  S01HA08: "chloroprocaine",
  S01HA30: "combinations",
  S01JA01: "fluorescein",
  S01JA02: "rose bengal sodium",
  S01JA51: "fluorescein, combinations",
  S01KA01: "hyaluronic acid",
  S01KA02: "hypromellose",
  S01KA51: "hyaluronic acid, combinations",
  S01KX01: "chymotrypsin",
  S01KX02: "trypan blue",
  S01LA01: "verteporfin",
  S01LA02: "anecortave",
  S01LA03: "pegaptanib",
  S01LA04: "ranibizumab",
  S01LA05: "aflibercept",
  S01LA06: "brolucizumab",
  S01LA07: "abicipar pegol",
  S01LA08: "bevacizumab",
  S01LA09: "faricimab",
  S01XA01: "guaiazulen",
  S01XA02: "retinol",
  S01XA03: "sodium chloride, hypertonic",
  S01XA04: "potassium iodide",
  S01XA05: "sodium edetate",
  S01XA06: "ethylmorphine",
  S01XA07: "alum",
  S01XA08: "acetylcysteine",
  S01XA09: "iodoheparinate",
  S01XA10: "inosine",
  S01XA11: "nandrolone",
  S01XA12: "dexpanthenol",
  S01XA13: "alteplase",
  S01XA14: "heparin",
  S01XA15: "ascorbic acid",
  S01XA18: "ciclosporin",
  S01XA19: "limbal stem cells, autologous",
  S01XA20: "artificial tears and other indifferent preparations",
  S01XA21: "mercaptamine",
  S01XA22: "ocriplasmin",
  S01XA23: "sirolimus",
  S01XA24: "cenegermin",
  S01XA25: "lifitegrast",
  S01XA26: "riboflavin",
  S01XA27: "voretigene neparvovec",
  S01XA28: "varenicline",
  S01XA29: "sepofarsen",
  S01XA31: "pegcetacoplan",
  S01XA32: "avacincaptad pegol",
  S01XA33: "diquafosol",
  S02AA01: "chloramphenicol",
  S02AA02: "nitrofural",
  S02AA03: "boric acid",
  S02AA04: "aluminium acetotartrate",
  S02AA05: "clioquinol",
  S02AA06: "hydrogen peroxide",
  S02AA07: "neomycin",
  S02AA08: "tetracycline",
  S02AA09: "chlorhexidine",
  S02AA10: "acetic acid",
  S02AA11: "polymyxin B",
  S02AA12: "rifamycin",
  S02AA13: "miconazole",
  S02AA14: "gentamicin",
  S02AA15: "ciprofloxacin",
  S02AA16: "ofloxacin",
  S02AA17: "fosfomycin",
  S02AA18: "cefmenoxime",
  S02AA30: "antiinfectives, combinations",
  S02BA01: "hydrocortisone",
  S02BA03: "prednisolone",
  S02BA06: "dexamethasone",
  S02BA07: "betamethasone",
  S02BA08: "fluocinolone acetonide",
  S02CA01: "prednisolone and antiinfectives",
  S02CA02: "flumetasone and antiinfectives",
  S02CA03: "hydrocortisone and antiinfectives",
  S02CA04: "triamcinolone and antiinfectives",
  S02CA05: "fluocinolone acetonide and antiinfectives",
  S02CA06: "dexamethasone and antiinfectives",
  S02CA07: "fludrocortisone and antiinfectives",
  S02DA01: "lidocaine",
  S02DA02: "cocaine",
  S02DA03: "phenazone",
  S02DA04: "cinchocaine",
  S02DA30: "combinations",
  S03AA01: "neomycin",
  S03AA02: "tetracycline",
  S03AA03: "polymyxin B",
  S03AA04: "chlorhexidine",
  S03AA05: "hexamidine",
  S03AA06: "gentamicin",
  S03AA07: "ciprofloxacin",
  S03AA08: "chloramphenicol",
  S03AA30: "antiinfectives, combinations",
  S03BA01: "dexamethasone",
  S03BA02: "prednisolone",
  S03BA03: "betamethasone",
  S03CA01: "dexamethasone and antiinfectives",
  S03CA02: "prednisolone and antiinfectives",
  S03CA04: "hydrocortisone and antiinfectives",
  S03CA05: "fludrocortisone and antiinfectives",
  S03CA06: "betamethasone and antiinfectives",
  S03CA07: "methylprednisolone and antiinfectives",
  // V — 기타
  V01AA01: "feather",
  V01AA02: "grass pollen",
  V01AA03: "house dust mites",
  V01AA04: "mould fungus and yeast fungus",
  V01AA05: "tree pollen",
  V01AA07: "insects",
  V01AA08: "food",
  V01AA09: "textiles",
  V01AA10: "flowers",
  V01AA11: "animals",
  V01AA20: "various",
  V03AB01: "ipecacuanha",
  V03AB02: "nalorphine",
  V03AB03: "edetates",
  V03AB04: "pralidoxime",
  V03AB05: "prednisolone and promethazine",
  V03AB06: "thiosulfate",
  V03AB08: "sodium nitrite",
  V03AB09: "dimercaprol",
  V03AB13: "obidoxime",
  V03AB14: "protamine",
  V03AB15: "naloxone",
  V03AB16: "ethanol",
  V03AB17: "methylthioninium chloride",
  V03AB18: "potassium permanganate",
  V03AB19: "physostigmine",
  V03AB20: "copper sulfate",
  V03AB21: "potassium iodide",
  V03AB22: "amyl nitrite",
  V03AB23: "acetylcysteine",
  V03AB24: "digitalis antitoxin",
  V03AB25: "flumazenil",
  V03AB26: "methionine",
  V03AB27: "4-dimethylaminophenol",
  V03AB29: "cholinesterase",
  V03AB31: "prussian blue",
  V03AB32: "glutathione",
  V03AB33: "hydroxocobalamin",
  V03AB34: "fomepizole",
  V03AB35: "sugammadex",
  V03AB36: "phentolamine",
  V03AB37: "idarucizumab",
  V03AB38: "andexanet alfa",
  V03AB54: "pralidoxime and atropine",
  V03AC01: "deferoxamine",
  V03AC02: "deferiprone",
  V03AC03: "deferasirox",
  V03AE01: "polystyrene sulfonate",
  V03AE02: "sevelamer",
  V03AE03: "lanthanum carbonate",
  V03AE04: "calcium acetate and magnesium carbonate",
  V03AE05: "sucroferric oxyhydroxide",
  V03AE06: "colestilan",
  V03AE07: "calcium acetate",
  V03AE08: "ferric citrate",
  V03AE09: "patiromer calcium",
  V03AE10: "sodium zirconium cyclosilicate",
  V03AF01: "mesna",
  V03AF02: "dexrazoxane",
  V03AF03: "calcium folinate",
  V03AF04: "calcium levofolinate",
  V03AF05: "amifostine",
  V03AF06: "sodium folinate",
  V03AF07: "rasburicase",
  V03AF08: "palifermin",
  V03AF09: "glucarpidase",
  V03AF10: "sodium levofolinate",
  V03AF11: "arginine and lysine",
  V03AF12: "trilaciclib",
  V03AG01: "sodium cellulose phosphate",
  V03AG05: "sodium phosphate",
  V03AH01: "diazoxide",
  V03AN01: "oxygen",
  V03AN02: "carbon dioxide",
  V03AN03: "helium",
  V03AN04: "nitrogen",
  V03AN05: "medical air",
  V03AX02: "nalfurafine",
  V03AX03: "cobicistat",
  V03AX04: "difelikefalin",
  V03AZ01: "ethanol",
  V04CA01: "tolbutamide",
  V04CA02: "glucose",
  V04CB01: "vitamin A concentrates",
  V04CC01: "sorbitol",
  V04CC02: "magnesium sulfate",
  V04CC03: "sincalide",
  V04CC04: "ceruletide",
  V04CD01: "metyrapone",
  V04CD03: "sermorelin",
  V04CD04: "corticorelin",
  V04CD05: "somatorelin",
  V04CD06: "macimorelin",
  V04CE01: "galactose",
  V04CE02: "sulfobromophthalein",
  V04CE03: "methacetin (13C)",
  V04CF01: "tuberculin",
  V04CF02: "mycobacterium tuberculosis, recombinant antigens",
  V04CG01: "cation exchange resins",
  V04CG02: "betazole",
  V04CG03: "histamine phosphate",
  V04CG04: "pentagastrin",
  V04CG05: "methylthioninium chloride",
  V04CG30: "caffeine and sodium benzoate",
  V04CH01: "inulin and other polyfructosans",
  V04CH02: "indigo carmine",
  V04CH03: "phenolsulfonphthalein",
  V04CH04: "alsactide",
  V04CH05: "relmapirazin",
  V04CH30: "aminohippuric acid",
  V04CJ01: "thyrotropin",
  V04CJ02: "protirelin",
  V04CK01: "secretin",
  V04CK02: "pancreozymin (cholecystokinin)",
  V04CK03: "bentiromide",
  V04CM01: "gonadorelin",
  V04CX01: "indocyanine green",
  V04CX02: "folic acid",
  V04CX03: "methacholine",
  V04CX04: "mannitol",
  V04CX05: "13C-urea",
  V04CX06: "hexaminolevulinate",
  V04CX07: "edrophonium",
  V04CX08: "carbon monoxide",
  V04CX09: "patent blue",
  V04CX10: "pafolacianine",
  V04CX11: "lithium chloride",
  V04CX12: "xenon",
  V04CX13: "pegulicianine",
  V06DC01: "glucose",
  V06DC02: "fructose",
  V08AA01: "diatrizoic acid",
  V08AA02: "metrizoic acid",
  V08AA03: "iodamide",
  V08AA04: "iotalamic acid",
  V08AA05: "ioxitalamic acid",
  V08AA06: "ioglicic acid",
  V08AA07: "acetrizoic acid",
  V08AA08: "iocarmic acid",
  V08AA09: "methiodal",
  V08AA10: "diodone",
  V08AB01: "metrizamide",
  V08AB02: "iohexol",
  V08AB03: "ioxaglic acid",
  V08AB04: "iopamidol",
  V08AB05: "iopromide",
  V08AB06: "iotrolan",
  V08AB07: "ioversol",
  V08AB08: "iopentol",
  V08AB09: "iodixanol",
  V08AB10: "iomeprol",
  V08AB11: "iobitridol",
  V08AB12: "ioxilan",
  V08AC01: "iodoxamic acid",
  V08AC02: "iotroxic acid",
  V08AC03: "ioglycamic acid",
  V08AC04: "adipiodone",
  V08AC05: "iobenzamic acid",
  V08AC06: "iopanoic acid",
  V08AC07: "iocetamic acid",
  V08AC08: "sodium iopodate",
  V08AC09: "tyropanoic acid",
  V08AC10: "calcium iopodate",
  V08AD01: "ethyl esters of iodised fatty acids",
  V08AD02: "iopydol",
  V08AD03: "propyliodone",
  V08AD04: "iofendylate",
  V08BA01: "barium sulfate with suspending agents",
  V08BA02: "barium sulfate without suspending agents",
  V08CA01: "gadopentetic acid",
  V08CA02: "gadoteric acid",
  V08CA03: "gadodiamide",
  V08CA04: "gadoteridol",
  V08CA05: "mangafodipir",
  V08CA06: "gadoversetamide",
  V08CA07: "ferric ammonium citrate",
  V08CA08: "gadobenic acid",
  V08CA09: "gadobutrol",
  V08CA10: "gadoxetic acid",
  V08CA11: "gadofosveset",
  V08CA12: "gadopiclenol",
  V08CA13: "gadoquatrane",
  V08CB01: "ferumoxsil",
  V08CB02: "ferristene",
  V08CB03: "iron oxide, nanoparticles",
  V08CX01: "perflubron",
  V08DA01: "perflutren, human albumin microspheres",
  V08DA02: "microparticles of galactose",
  V08DA03: "perflenapent",
  V08DA04: "perflutren, phospholipid microspheres",
  V08DA05: "sulfur hexafluoride, phospholipid microspheres",
  V08DA06: "perflubutane, phospholipid microspheres",
  V09AX03: "iodine (124I) 2beta-carbomethoxy-3beta-(4 iodophenyl)-tropane",
  V09FX04: "sodium iodide (124I)",
  V09GX04: "rubidium (82Rb) chloride",
  V09GX05: "ammonia (13N)",
  V09IX13: "methionine (11C)",
  V10XX04: "lutetium (177Lu) oxodotreotide",
  V10XX05: "lutetium (177Lu) vipivotide tetraxetan",
};

/**
 * ATC 5단계(7글자, 개별 성분) → 영문 성분명. 매칭 없으면 빈 문자열.
 */
export const atcLevel5Name = makeAtcNameLookup(ATC_LEVEL5_CATEGORIES, 7);
